import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';

import { useTranslation } from 'react-i18next';

const API_BASE_URL = '/api';
const logoWithNameURL = '/banner.png?v=2';

const Icons = {
    Edit: () => <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>,
    Delete: () => <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>,
    Print: () => <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>,
    Close: () => <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>,
    Add: () => <svg className="w-5 h-5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
};

const ConfirmModal = ({ isOpen, message, onConfirm, onCancel, t }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex justify-center items-center p-4 z-[999999] animate-fade-in">
            <div className="bg-[#1a262c] border border-[#4f5f68]/50 text-[#f3efe6] rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-scale-in">
                <div className="flex items-center gap-2.5 mb-4">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d97757] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#d97757]"></span>
                    </span>
                    <span className="font-mono text-xs tracking-widest uppercase text-[#d97757]">{t('pleaseConfirm', 'Bestätigung')}</span>
                </div>
                <h3 className="text-xl font-bold font-sans text-[#f3efe6] mb-3">{t('pleaseConfirm', 'Bitte bestätigen')}</h3>
                <p className="text-[#a4b3bc] mb-6 whitespace-pre-wrap text-sm leading-relaxed">{message}</p>
                <div className="flex justify-end gap-3">
                    <button onClick={onCancel} className="px-5 py-2.5 bg-[#121c21] hover:bg-[#21323a] text-[#f3efe6] border border-[#4f5f68]/40 rounded-xl text-sm font-semibold transition-all duration-200">{t('cancel', 'Abbrechen')}</button>
                    <button onClick={() => { onConfirm(); onCancel(); }} className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg shadow-red-600/25">{t('confirm', 'Bestätigen')}</button>
                </div>
            </div>
        </div>
    );
};

const StandblattPrintView = ({ data, onClose }) => {
    const { event, participant, selectedStiche, stiche, receipts } = data;
    const [pdfStatus, setPdfStatus] = useState('saving');
    const [pdfErrorMsg, setPdfErrorMsg] = useState('');

    useEffect(() => {
        const savePdf = async () => {
            const element = document.getElementById('standblatt-print-area');
            if (!element) return;

            const generateAndUpload = () => {
                const opt = {
                    margin:       10,
                    filename:     `Standblatt_${participant.firstname}_${participant.lastname}.pdf`,
                    image:        { type: 'jpeg', quality: 0.98 },
                    html2canvas:  { scale: 2 },
                    jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
                };
                if(window.html2pdf) {
                    window.html2pdf().set(opt).from(element).output('datauristring').then(async (pdfBase64) => {
                        const token = JSON.parse(localStorage.getItem('kks-user'))?.token;
                        if (!token) { setPdfStatus('error'); setPdfErrorMsg('Nicht eingeloggt.'); return; }
                        try {
                            const res = await fetch(`${API_BASE_URL}/events/${event.id}/standblatt/save`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ filename: opt.filename, pdfBase64 })
                            });
                            if (res.ok) { setPdfStatus('success'); } 
                            else {
                                const errData = await res.json().catch(() => ({}));
                                setPdfStatus('error'); setPdfErrorMsg(errData.message || `Server-Fehler Code: ${res.status}`);
                            }
                        } catch(error) { setPdfStatus('error'); setPdfErrorMsg(error.message); }
                    });
                }
            };

            if (window.html2pdf) { generateAndUpload(); } 
            else {
                const script = document.createElement('script');
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
                document.body.appendChild(script);
                script.onload = generateAndUpload;
            }
        };
        savePdf();
    }, [participant, event]);

    const calculatedTotalPrice = selectedStiche.reduce((sum, id) => {
        const stich = stiche.find(s => s.id === id);
        const basePrice = stich ? Number(stich.price) : 0;
        const isCompanion = participant.companionSticheIds?.includes(id);
        return sum + basePrice + (isCompanion ? basePrice : 0);
    }, 0);

    const finalPrintPrice = participant.totalPrice !== undefined ? participant.totalPrice : (participant.total_price !== undefined && participant.total_price !== null ? Number(participant.total_price) : calculatedTotalPrice);

    return (
        <div className="bg-[#10171a] text-black font-sans pb-10 print:bg-white print:pb-0 min-h-screen relative z-[99999]">
            <style>{`
                @media print { nav, footer, .no-print { display: none !important; } @page { size: A4 portrait; margin: 10mm; } body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } * { overflow: visible !important; } }
            `}</style>
            <div className="no-print bg-[#162127]/90 backdrop-blur-md border-b border-[#4f5f68]/30 p-4 text-[#f3efe6] flex justify-between items-center shadow-lg sticky top-0 z-50">
                <span className="font-bold flex items-center gap-3 text-sm font-mono">
                    <span className="text-[#3a7d8a] font-bold tracking-wider">DRUCKANSICHT</span>
                    {pdfStatus === 'success' && <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs px-2.5 py-1 rounded-md">✅ Server PDF-Backup gespeichert</span>}
                    {pdfStatus === 'saving' && <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs px-2.5 py-1 rounded-md">⏳ PDF wird gesichert...</span>}
                    {pdfStatus === 'error' && <span className="bg-red-500/20 text-red-300 border border-red-500/40 text-xs px-2.5 py-1 rounded-md whitespace-pre-wrap">❌ Fehler: {pdfErrorMsg}</span>}
                </span>
                <div className="flex gap-3">
                    <button onClick={() => window.print()} className="flow-btn-coral px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer shadow-lg shadow-[#d97757]/20"><Icons.Print/> Jetzt Drucken</button>
                    <button onClick={onClose} className="flow-btn-ghost px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer"><Icons.Close/> Ansicht schliessen</button>
                </div>
            </div>

            <div id="standblatt-print-area" className="bg-white max-w-4xl mx-auto p-10 mt-8 mb-8 shadow-2xl print:shadow-none print:m-0 print:p-0 print:block aspect-[1/1.414]">
                <img src={logoWithNameURL} alt="Logo" className="h-16 mb-4 block object-contain" />
                <h1 className="text-4xl font-bold italic underline mb-8">{event.name}</h1>
                <div className="flex justify-between mb-8 text-sm">
                    <div className="flex-1 flex items-end mr-4"><span className="mr-2 font-semibold">Name:</span><span className="border-b border-black flex-grow font-bold text-xl pb-1">{participant.lastname}</span></div>
                    <div className="flex-1 flex items-end mr-4"><span className="mr-2 font-semibold">Vorname:</span><span className="border-b border-black flex-grow font-bold text-xl pb-1">{participant.firstname}</span></div>
                    <div className="flex-1 flex items-end mr-4"><span className="mr-2 font-semibold">Jahrgang:</span><span className="border-b border-black flex-grow font-bold text-xl pb-1 text-center">{participant.yearOfBirth || participant.year_of_birth}</span></div>
                    <div className="flex-1 flex items-end"><span className="mr-2 font-semibold">Ort:</span><span className="border-b border-black flex-grow font-bold text-xl pb-1">{participant.city}</span></div>
                </div>
                
                <table className="w-full text-left border-collapse mb-8">
                    <thead>
                        <tr className="border-b-2 border-black bg-gray-100 text-xl"><th className="py-2 px-2 w-1/4">Stiche:</th><th className="py-2 px-2 w-1/6">Gelöst:</th><th className="py-2 px-2 w-1/2">Details/Schüsse:</th><th className="py-2 px-2 w-1/12 text-right">Total:</th></tr>
                    </thead>
                    <tbody>
                        {stiche.filter(s => selectedStiche.includes(s.id)).map(s => {
                            const receipt = receipts?.find(r => r.event_stich_id === s.id);
                            const parsedShots = receipt ? (typeof receipt.individual_scores === 'string' ? JSON.parse(receipt.individual_scores) : receipt.individual_scores) : [];
                            const isKombination = s.type === 'kombination';
                            const isAnmeldung = s.type === 'anmeldung';
                            const hasCompanion = participant.companionSticheIds?.includes(s.id);
                            const boxCount = Math.max(s.default_shots || (isKombination || isAnmeldung ? 0 : 10), parsedShots.length);

                            return (
                                <tr key={s.id} className="border-b border-gray-300 break-inside-avoid">
                                    <td className="py-4 px-2 font-bold text-lg">{s.template_name}</td>
                                    <td className="py-4 px-2 text-lg">{hasCompanion ? `${Number(s.price * 2).toFixed(2)} (${Number(s.price).toFixed(2)}x2)` : Number(s.price).toFixed(2)}</td>
                                    <td className="py-4 px-2">
                                        {isKombination ? (
                                            <div className="text-sm font-semibold text-gray-700 mt-1">
                                                {parsedShots.length > 0 ? parsedShots.map(p => `${p.name}: ${Number(p.weighted).toFixed(1).replace('.0', '')}`).join(' + ') : 'Wird automatisch berechnet'}
                                            </div>
                                        ) : isAnmeldung ? (
                                            <div className="text-sm font-semibold text-gray-700 mt-1">
                                                {hasCompanion ? 'Mit Begleitung (+1 Pers.)' : 'Alleine (1 Pers.)'}<br/>
                                                Kontakt: {participant.contact || '-'}
                                            </div>
                                        ) : (
                                            <div className="flex flex-wrap gap-1">
                                                {Array.from({length: boxCount}).map((_, i) => (
                                                    <div key={i} className="w-8 h-8 border border-black inline-flex items-center justify-center font-bold text-sm">{parsedShots[i] ? parsedShots[i].score : ''}</div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-4 px-2 text-right">
                                        <div className="w-16 h-10 border-2 border-black inline-flex items-center justify-center font-bold text-lg">{receipt ? Number(receipt.total_score).toFixed(1).replace('.0', '') : ''}</div>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <div className="mt-8 flex justify-between items-center text-2xl pt-4 break-inside-avoid">
                    <div className="font-bold">Zahlung: CHF {finalPrintPrice.toFixed(2)}</div>
                    <div className="italic font-bold text-lg">Der Vorstand dankt Dir für Deine Teilnahme</div>
                </div>
            </div>
        </div>
    );
};

const LandingPage = ({ onNavigate }) => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-center justify-center flex-grow p-4 md:p-8 relative z-10 max-w-6xl mx-auto w-full animate-fade-in">
            {/* Top Micro-Label / Kicker */}
            <div className="flex items-center gap-3 mb-6 font-mono text-[11px] md:text-xs tracking-[0.16em] uppercase text-[#3a7d8a] bg-[#3a7d8a]/10 px-4 py-1.5 rounded-full border border-[#3a7d8a]/30">
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3a7d8a] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3a7d8a]"></span>
                </span>
                <span>№ 01 / SCHIESSVEREIN SCOREBOARD & ERFASSUNG</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-extrabold text-[#f3efe6] tracking-tight leading-[1.08] mb-6 text-center max-w-4xl">
                Präzision im Treffer<span className="text-[#d97757]">.</span><br />
                Klarheit im Resultat<span className="text-[#3a7d8a]">.</span>
            </h1>

            <p className="text-base md:text-xl text-[#f3efe6]/75 max-w-2xl text-center mb-10 leading-relaxed font-sans">
                {t('landingSubtitle1', 'Die moderne, volldigitale Lösung für Schiessvereine in der Schweiz. Belege per KI scannen, Stiche auswerten und Ranglisten live präsentieren.')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
                <button 
                    onClick={() => onNavigate('login')} 
                    className="flow-btn-coral text-base md:text-lg px-8 py-3.5 flex items-center gap-3 font-bold cursor-pointer"
                >
                    <span>{t('landingLoginButton', 'Anmelden & Starten')}</span>
                    <span aria-hidden="true" className="text-lg">→</span>
                </button>
                <button 
                    onClick={() => onNavigate('public-scoreboard')} 
                    className="flow-btn-ghost text-base md:text-lg px-8 py-3.5 flex items-center gap-2.5 font-semibold cursor-pointer border-[#4f5f68]/50 hover:border-[#3a7d8a]"
                >
                    <span>🏆</span>
                    <span>{t('landingScoreboardButton', 'Öffentliche Ranglisten')}</span>
                </button>
            </div>

            {/* 3 Floweon-Style Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full text-left">
                <div className="flow-card p-6 md:p-8 flex flex-col justify-between group">
                    <div>
                        <div className="font-mono text-xs tracking-mono uppercase text-[#3a7d8a] group-hover:text-[#d97757] transition-colors mb-3">№ 01 / ERFASSUNG</div>
                        <h3 className="text-xl font-bold text-[#f3efe6] mb-3 group-hover:text-[#d97757] transition-colors">KI-Vision & Belegscan</h3>
                        <p className="text-sm text-[#f3efe6]/70 leading-relaxed">
                            Fotografieren Sie Schiesszettel direkt am Stand. Das lokale neuronale Modell erkennt alle 10er- und 20er-Serien sekundenschnell.
                        </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-[#4f5f68]/30 flex justify-between items-center font-mono text-xs text-[#f3efe6]/50">
                        <span>100% LOKAL</span>
                        <span className="text-[#3a7d8a]">BEREIT</span>
                    </div>
                </div>

                <div className="flow-card p-6 md:p-8 flex flex-col justify-between group">
                    <div>
                        <div className="font-mono text-xs tracking-mono uppercase text-[#3a7d8a] group-hover:text-[#d97757] transition-colors mb-3">№ 02 / AUSWERTUNG</div>
                        <h3 className="text-xl font-bold text-[#f3efe6] mb-3 group-hover:text-[#d97757] transition-colors">Automatische Ranglisten</h3>
                        <p className="text-sm text-[#f3efe6]/70 leading-relaxed">
                            Ob Einzel-, Gruppenstich, Differenzler oder Zick-Zack – alle Resultate werden nach Verbandsregeln blitzschnell sortiert und live aktualisiert.
                        </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-[#4f5f68]/30 flex justify-between items-center font-mono text-xs text-[#f3efe6]/50">
                        <span>ECHTZEIT</span>
                        <span className="text-[#d97757]">LIVE</span>
                    </div>
                </div>

                <div className="flow-card p-6 md:p-8 flex flex-col justify-between group">
                    <div>
                        <div className="font-mono text-xs tracking-mono uppercase text-[#3a7d8a] group-hover:text-[#d97757] transition-colors mb-3">№ 03 / EVENTS</div>
                        <h3 className="text-xl font-bold text-[#f3efe6] mb-3 group-hover:text-[#d97757] transition-colors">Kasse & Standblätter</h3>
                        <p className="text-sm text-[#f3efe6]/70 leading-relaxed">
                            Verwalten Sie Schützenfeste und Endstiche mit integrierter Kassenabrechnung, Auszeichnungen und druckfertigen PDF-Standblättern.
                        </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-[#4f5f68]/30 flex justify-between items-center font-mono text-xs text-[#f3efe6]/50">
                        <span>PDF & DRUCK</span>
                        <span className="text-[#3a7d8a]">INTEGRIERT</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Login = ({ onLoginSuccess, showMessage, onNavigate }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot' | 'registered' | 'mfa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Unverified account helper
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingVerify, setResendingVerify] = useState(false);

  // Forgot password confirmation state
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // MFA login challenge state
  const [mfaState, setMfaState] = useState(null); // { mfaToken, methods, email }
  const [activeMfaMethod, setActiveMfaMethod] = useState('totp');
  const [mfaCode, setMfaCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  const handleViewPublicScoreboard = () => {
    if (onNavigate) onNavigate('public-scoreboard');
    else {
      window.history.pushState({}, '', '/public-scoreboard');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail('');
    showMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (response.ok) {
        if (data.mfaRequired) {
          setMfaState({ mfaToken: data.mfaToken, methods: data.methods || [], email: data.email });
          setActiveMfaMethod(data.preferredMethod || 'totp');
          setEmailCodeSent(data.preferredMethod === 'email');
          setMfaCode('');
          setMode('mfa');
          return;
        }
        if (data.token && data.userId && data.email) {
          localStorage.setItem('kks-user', JSON.stringify({ id: data.userId, email: data.email, token: data.token }));
          onLoginSuccess({ id: data.userId, email: data.email, token: data.token });
        } else {
          showMessage(t('loginFailed', 'Login fehlgeschlagen'), 'error');
        }
      } else {
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          setUnverifiedEmail(data.email || email);
        }
        showMessage(data.message || t('unknownError', 'Fehler'), 'error');
      }
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail('');
    showMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (response.ok) {
        setUnverifiedEmail(data.email || email);
        setPassword('');
        setMode('registered');
        showMessage(data.message || 'Registrierung erfolgreich!', 'success');
      } else {
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          setUnverifiedEmail(data.email || email);
        }
        showMessage(data.message || t('registrationFailed', 'Registrierung fehlgeschlagen'), 'error');
      }
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (targetEmail) => {
    const mailToUse = targetEmail || unverifiedEmail || email;
    if (!mailToUse) return;
    setResendingVerify(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: mailToUse })
      });
      const data = await res.json();
      if (res.ok) showMessage(data.message || 'Bestätigungs-E-Mail wurde gesendet.', 'success');
      else showMessage(data.message || 'Fehler beim Senden.', 'error');
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setResendingVerify(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    showMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        setResetEmailSent(true);
        showMessage(data.message, 'success');
      } else {
        showMessage(data.message || 'Fehler beim Senden.', 'error');
      }
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMfaEmailCode = async () => {
    if (!mfaState?.mfaToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/login/mfa/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken: mfaState.mfaToken })
      });
      const data = await res.json();
      if (res.ok) {
        setEmailCodeSent(true);
        setActiveMfaMethod('email');
        showMessage(data.message, 'success');
      } else {
        showMessage(data.message || 'Fehler beim Senden des E-Mail-Codes.', 'error');
      }
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (!mfaState?.mfaToken) return;
    setLoading(true);
    showMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/login/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mfaToken: mfaState.mfaToken,
          code: mfaCode,
          method: activeMfaMethod
        })
      });
      const data = await res.json();
      if (res.ok && data.token && data.userId) {
        localStorage.setItem('kks-user', JSON.stringify({ id: data.userId, email: data.email, token: data.token }));
        onLoginSuccess({ id: data.userId, email: data.email, token: data.token });
      } else {
        showMessage(data.message || 'Ungültiger Sicherheitscode.', 'error');
      }
    } catch (error) {
      showMessage(`${t('networkError', 'Netzwerkfehler')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center flex-grow p-4 relative z-10 animate-fade-in">
      <div className="flow-card p-8 md:p-10 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-2 mb-4 font-mono text-xs tracking-mono uppercase text-[#3a7d8a]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#d97757]"></span>
          <span>ZUGANG / AUTHENTIFIZIERUNG</span>
        </div>

        {mode === 'mfa' && mfaState ? (
          <div>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-[#f3efe6] mb-2">
              🛡️ {t('mfaChallengeTitle', 'Zwei-Faktor-Authentifizierung')}
            </h2>
            <p className="text-sm text-[#f3efe6]/70 mb-6">
              Konto: <strong className="text-[#f3efe6]">{mfaState.email}</strong>
            </p>

            <form onSubmit={handleVerifyMfa} className="space-y-5">
              <div>
                <label htmlFor="mfa-otp-input" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1">
                  {activeMfaMethod === 'totp' ? 'Authenticator-Code (6-stellig)' : 'E-Mail-Sicherheitscode (6-stellig)'}
                </label>
                <p id="mfa-otp-hint" className="text-xs text-[#a4b3bc] mb-2.5 leading-relaxed">
                  {activeMfaMethod === 'totp'
                    ? t('mfaTotpPrompt', 'Geben Sie den 6-stelligen Code aus Ihrer Smartphone Authenticator-App ein.')
                    : t('mfaEmailPrompt', 'Geben Sie den 6-stelligen Sicherheitscode ein, den wir an Ihre E-Mail-Adresse gesendet haben.')}
                </p>
                <input
                  type="text"
                  id="mfa-otp-input"
                  name="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  aria-describedby="mfa-otp-hint"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  enterKeyHint="done"
                  required
                  className="flow-input w-full text-center font-mono text-2xl tracking-[0.35em] py-3"
                />
              </div>

              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="w-full flow-btn-coral py-3 font-bold cursor-pointer disabled:opacity-50"
              >
                {loading ? 'Wird geprüft...' : t('mfaVerifyButton', 'Code bestätigen & Anmelden')}
              </button>

              <div className="space-y-2 pt-2 border-t border-[#4f5f68]/30">
                {activeMfaMethod === 'email' && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleSendMfaEmailCode}
                    className="w-full flow-btn-ghost py-2 text-xs font-mono cursor-pointer"
                  >
                    ✉️ {t('mfaResendEmailCode', 'E-Mail-Code erneut senden')}
                  </button>
                )}

                {mfaState.methods.includes('email') && activeMfaMethod !== 'email' && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      if (!emailCodeSent) handleSendMfaEmailCode();
                      else setActiveMfaMethod('email');
                      setMfaCode('');
                    }}
                    className="w-full flow-btn-ghost py-2 text-xs font-mono cursor-pointer"
                  >
                    ✉️ {t('mfaSwitchToEmail', 'Stattdessen Sicherheitscode per E-Mail senden')}
                  </button>
                )}

                {mfaState.methods.includes('totp') && activeMfaMethod !== 'totp' && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => { setActiveMfaMethod('totp'); setMfaCode(''); }}
                    className="w-full flow-btn-ghost py-2 text-xs font-mono cursor-pointer"
                  >
                    📱 {t('mfaSwitchToTotp', 'Smartphone Authenticator-App verwenden')}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => { setMode('signin'); setMfaState(null); setMfaCode(''); }}
                  className="w-full text-xs text-[#a4b3bc] hover:text-[#f3efe6] py-2 cursor-pointer"
                >
                  ← {t('backToLogin', 'Zurück zum Login')}
                </button>
              </div>
            </form>
          </div>
        ) : mode === 'registered' ? (
          <div className="space-y-5">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-2xl">
              ✉️
            </div>
            <h2 className="text-2xl font-display font-bold text-[#f3efe6]">E-Mail-Adresse bestätigen</h2>
            <p className="text-sm text-[#a4b3bc] leading-relaxed">
              Wir haben eine Verifizierungs-E-Mail von <span className="font-mono text-[#f3efe6]">noreply@kks-scoreboard.ch</span> an{' '}
              <strong className="text-[#f3efe6]">{unverifiedEmail}</strong> gesendet. Bitte klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.
            </p>
            <div className="space-y-2.5 pt-2">
              <button
                type="button"
                disabled={resendingVerify}
                onClick={() => handleResendVerification(unverifiedEmail)}
                className="w-full flow-btn-tide py-2.5 text-sm font-semibold cursor-pointer"
              >
                {resendingVerify ? 'Wird gesendet...' : t('resendVerificationButton', 'Bestätigungs-E-Mail erneut senden')}
              </button>
              <button
                type="button"
                onClick={() => setMode('signin')}
                className="w-full flow-btn-ghost py-2.5 text-sm font-medium cursor-pointer"
              >
                ← {t('backToLogin', 'Zurück zum Login')}
              </button>
            </div>
          </div>
        ) : mode === 'forgot' ? (
          <div>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-[#f3efe6] mb-2">
              {t('forgotPasswordTitle', 'Passwort zurücksetzen')}
            </h2>
            <p className="text-sm text-[#f3efe6]/65 mb-6">
              {t('forgotPasswordSubtitle', 'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen sicheren Link zum Festlegen eines neuen Passworts.')}
            </p>

            {resetEmailSent && (
              <div className="mb-5 p-4 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-xs text-emerald-200 leading-relaxed">
                ✅ Falls ein Konto für <strong>{email}</strong> existiert, wurde eine E-Mail mit dem Reset-Link von <code>noreply@kks-scoreboard.ch</code> versendet. Bitte prüfen Sie auch Ihren Spam-Ordner.
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label htmlFor="forgot-email" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">
                  {t('email', 'E-Mail')}
                </label>
                <input
                  type="email"
                  id="forgot-email"
                  name="email"
                  autoComplete="username"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  enterKeyHint="done"
                  placeholder="name@verein.ch"
                  className="flow-input w-full"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flow-btn-coral py-3 font-bold cursor-pointer"
              >
                {loading ? 'Wird gesendet...' : t('sendResetLinkButton', 'Reset-Link per E-Mail senden')}
              </button>
              <button
                type="button"
                onClick={() => { setMode('signin'); setResetEmailSent(false); }}
                className="w-full flow-btn-ghost py-2.5 text-sm font-medium cursor-pointer"
              >
                ← {t('backToLogin', 'Zurück zum Login')}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[#131d22] border border-[#4f5f68]/30 mb-6">
              <button
                type="button"
                onClick={() => { setMode('signin'); setUnverifiedEmail(''); }}
                className={`py-2 text-xs md:text-sm font-bold rounded-lg transition cursor-pointer ${mode === 'signin' ? 'bg-[#d97757] text-[#f3efe6] shadow-md' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
              >
                {t('login', 'Anmelden')}
              </button>
              <button
                type="button"
                onClick={() => { setMode('signup'); setUnverifiedEmail(''); }}
                className={`py-2 text-xs md:text-sm font-bold rounded-lg transition cursor-pointer ${mode === 'signup' ? 'bg-[#3a7d8a] text-[#f3efe6] shadow-md' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
              >
                {t('register', 'Registrieren')}
              </button>
            </div>

            <h2 className="text-2xl md:text-3xl font-display font-bold text-[#f3efe6] mb-1.5">
              {mode === 'signin' ? t('login', 'Anmelden') : t('register', 'Konto erstellen')}
            </h2>
            <p className="text-sm text-[#f3efe6]/60 mb-6">
              {mode === 'signin'
                ? 'Melden Sie sich an, um Schussdaten und Gruppen zu verwalten.'
                : 'Nach der Registrierung erhalten Sie eine Bestätigungs-E-Mail zur Aktivierung Ihres Kontos.'}
            </p>

            {unverifiedEmail && (
              <div className="mb-5 p-4 rounded-xl bg-amber-950/60 border border-amber-500/40 text-xs text-amber-200 space-y-2.5">
                <p>⚠️ Die E-Mail-Adresse <strong>{unverifiedEmail}</strong> wurde noch nicht bestätigt.</p>
                <button
                  type="button"
                  disabled={resendingVerify}
                  onClick={() => handleResendVerification(unverifiedEmail)}
                  className="w-full py-2 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100 font-semibold transition cursor-pointer"
                >
                  {resendingVerify ? 'Wird gesendet...' : t('resendVerificationButton', 'Bestätigungs-E-Mail erneut senden')}
                </button>
              </div>
            )}

            {mode === 'signin' ? (
              <form onSubmit={handleSignIn} className="space-y-5">
                <div>
                  <label htmlFor="signin-email" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">
                    {t('email', 'E-Mail')}
                  </label>
                  <input
                    type="email"
                    id="signin-email"
                    name="email"
                    autoComplete="username"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    enterKeyHint="next"
                    placeholder="name@verein.ch"
                    className="flow-input w-full"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label htmlFor="signin-password" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc]">
                      {t('password', 'Passwort')}
                    </label>
                    <button
                      type="button"
                      onClick={() => { setMode('forgot'); setResetEmailSent(false); }}
                      className="text-xs font-mono text-[#8ec8d4] hover:text-[#d97757] transition cursor-pointer"
                    >
                      {t('forgotPasswordLink', 'Passwort vergessen?')}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="signin-password"
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      enterKeyHint="done"
                      placeholder="••••••••"
                      className="flow-input w-full pr-20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? t('hidePassword', 'Passwort verbergen') : t('showPassword', 'Passwort anzeigen')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-mono text-[#a4b3bc] hover:text-[#f3efe6] px-2 py-1 rounded bg-[#1a262c]/80 cursor-pointer"
                    >
                      {showPassword ? 'Verbergen' : 'Anzeigen'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col space-y-3 pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flow-btn-coral py-3 font-bold cursor-pointer"
                  >
                    {loading ? 'Wird geladen...' : t('loginButton', 'Anmelden')}
                  </button>
                  <button
                    type="button"
                    onClick={handleViewPublicScoreboard}
                    className="w-full flow-btn-ghost py-2.5 text-sm font-medium cursor-pointer"
                  >
                    🏆 {t('viewPublicScoreboard', 'Öffentliche Rangliste anzeigen')}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSignUp} className="space-y-5">
                <div>
                  <label htmlFor="signup-email" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">
                    {t('email', 'E-Mail')}
                  </label>
                  <input
                    type="email"
                    id="signup-email"
                    name="email"
                    autoComplete="username"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    enterKeyHint="next"
                    placeholder="name@verein.ch"
                    className="flow-input w-full"
                  />
                </div>
                <div>
                  <label htmlFor="signup-password" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1">
                    {t('password', 'Passwort')} <span className="text-[#f3efe6]/50">(mind. 8 Zeichen)</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="signup-password"
                      name="new-password"
                      autoComplete="new-password"
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      enterKeyHint="done"
                      placeholder="Mindestens 8 Zeichen"
                      className="flow-input w-full pr-20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? t('hidePassword', 'Passwort verbergen') : t('showPassword', 'Passwort anzeigen')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-mono text-[#a4b3bc] hover:text-[#f3efe6] px-2 py-1 rounded bg-[#1a262c]/80 cursor-pointer"
                    >
                      {showPassword ? 'Verbergen' : 'Anzeigen'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col space-y-3 pt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flow-btn-tide py-3 font-bold cursor-pointer"
                  >
                    {loading ? 'Wird erstellt...' : t('registerButton', 'Registrieren & Bestätigungs-Mail senden')}
                  </button>
                  <button
                    type="button"
                    onClick={handleViewPublicScoreboard}
                    className="w-full flow-btn-ghost py-2.5 text-sm font-medium cursor-pointer"
                  >
                    🏆 {t('viewPublicScoreboard', 'Öffentliche Rangliste anzeigen')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const VerifyEmailPage = ({ showMessage, onNavigate }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [statusMsg, setStatusMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token') || '';
    const emailParam = params.get('email') || '';
    if (emailParam) setResendEmail(emailParam);

    if (!token) {
      setStatus('error');
      setStatusMsg('Kein gültiger Bestätigungstoken in der URL gefunden.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email: emailParam })
        });
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setStatusMsg(data.message || 'Ihre E-Mail-Adresse wurde erfolgreich bestätigt!');
        } else {
          setStatus('error');
          if (data.email) setResendEmail(data.email);
          setStatusMsg(data.message || 'Bestätigung fehlgeschlagen.');
        }
      } catch (err) {
        setStatus('error');
        setStatusMsg('Netzwerkfehler bei der Bestätigung Ihrer E-Mail-Adresse.');
      }
    })();
  }, []);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail })
      });
      const data = await res.json();
      if (res.ok) showMessage(data.message, 'success');
      else showMessage(data.message || 'Fehler beim Senden.', 'error');
    } catch {
      showMessage('Netzwerkfehler.', 'error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center flex-grow p-4 relative z-10 animate-fade-in">
      <div className="flow-card p-8 md:p-10 w-full max-w-md shadow-2xl text-center">
        <div className="font-mono text-xs uppercase tracking-widest text-[#3a7d8a] mb-3">
          {t('verifyEmailTitle', 'E-Mail-Bestätigung')}
        </div>
        {status === 'verifying' && (
          <div className="py-6 space-y-3">
            <div className="text-3xl animate-pulse">⏳</div>
            <p className="text-[#f3efe6] font-semibold">Ihre E-Mail-Adresse wird bestätigt...</p>
          </div>
        )}
        {status === 'success' && (
          <div className="space-y-5">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-3xl">
              ✅
            </div>
            <h2 className="text-2xl font-bold text-[#f3efe6]">E-Mail bestätigt!</h2>
            <p className="text-sm text-[#a4b3bc] leading-relaxed">{statusMsg}</p>
            <button
              type="button"
              onClick={() => onNavigate('login')}
              className="w-full flow-btn-coral py-3 font-bold cursor-pointer"
            >
              Jetzt zum Login →
            </button>
          </div>
        )}
        {status === 'error' && (
          <div className="space-y-5 text-left">
            <div className="p-4 rounded-xl bg-red-950/60 border border-red-500/40 text-sm text-red-200">
              ❌ {statusMsg}
            </div>
            <form onSubmit={handleResend} className="space-y-3">
              <label htmlFor="resend-verify-email" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc]">
                Neuen Bestätigungslink anfordern
              </label>
              <input
                type="email"
                id="resend-verify-email"
                name="email"
                autoComplete="username"
                spellCheck={false}
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
                placeholder="name@verein.ch"
                className="flow-input w-full"
              />
              <button type="submit" disabled={resending} className="w-full flow-btn-tide py-2.5 text-sm font-bold cursor-pointer">
                {resending ? 'Wird gesendet...' : t('resendVerificationButton', 'Bestätigungs-E-Mail erneut senden')}
              </button>
            </form>
            <button
              type="button"
              onClick={() => onNavigate('login')}
              className="w-full flow-btn-ghost py-2.5 text-sm cursor-pointer"
            >
              ← {t('backToLogin', 'Zurück zum Login')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ResetPasswordPage = ({ showMessage, onNavigate }) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const token = typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('token') || '') : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      showMessage('Ungültiger Link: Kein Reset-Token gefunden.', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        showMessage(data.message, 'success');
      } else {
        showMessage(data.message || 'Fehler beim Zurücksetzen des Passworts.', 'error');
      }
    } catch {
      showMessage('Netzwerkfehler.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center flex-grow p-4 relative z-10 animate-fade-in">
      <div className="flow-card p-8 md:p-10 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-2 mb-4 font-mono text-xs uppercase tracking-wider text-[#3a7d8a]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#d97757]"></span>
          <span>KONTOWIEDERHERSTELLUNG</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-[#f3efe6] mb-2">
          {t('resetPasswordTitle', 'Neues Passwort festlegen')}
        </h2>

        {done ? (
          <div className="space-y-5 mt-4">
            <div className="p-4 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-sm text-emerald-200">
              ✅ Ihr Passwort wurde erfolgreich gespeichert. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
            </div>
            <button
              type="button"
              onClick={() => onNavigate('login')}
              className="w-full flow-btn-coral py-3 font-bold cursor-pointer"
            >
              Jetzt anmelden →
            </button>
          </div>
        ) : !token ? (
          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-xl bg-red-950/60 border border-red-500/40 text-sm text-red-200">
              Ungültiger oder unvollständiger Link zum Zurücksetzen des Passworts.
            </div>
            <button type="button" onClick={() => onNavigate('login')} className="w-full flow-btn-ghost py-2.5 text-sm cursor-pointer">
              ← {t('backToLogin', 'Zurück zum Login')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <p className="text-sm text-[#f3efe6]/65">
              {t('resetPasswordSubtitle', 'Wählen Sie ein neues, sicheres Passwort (mindestens 8 Zeichen).')}
            </p>
            <div>
              <label htmlFor="reset-new-password" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">
                {t('newPassword', 'Neues Passwort')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="reset-new-password"
                  name="new-password"
                  autoComplete="new-password"
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  enterKeyHint="done"
                  placeholder="Mindestens 8 Zeichen"
                  className="flow-input w-full pr-20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-mono text-[#a4b3bc] hover:text-[#f3efe6] px-2 py-1 rounded bg-[#1a262c]/80 cursor-pointer"
                >
                  {showPassword ? 'Verbergen' : 'Anzeigen'}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flow-btn-coral py-3 font-bold cursor-pointer"
            >
              {loading ? 'Wird gespeichert...' : t('saveNewPasswordButton', 'Neues Passwort speichern')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate('login')}
              className="w-full flow-btn-ghost py-2.5 text-sm cursor-pointer"
            >
              ← {t('backToLogin', 'Zurück zum Login')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const Settings = ({ user, showMessage, handleLogout, requestConfirm }) => {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [deleteEmail, setDeleteEmail] = useState('');

    // MFA state
    const [mfaEmailEnabled, setMfaEmailEnabled] = useState(false);
    const [mfaTotpEnabled, setMfaTotpEnabled] = useState(false);
    const [mfaLoading, setMfaLoading] = useState(false);

    // Email MFA setup/disable state
    const [emailSetupStep, setEmailSetupStep] = useState(false);
    const [emailSetupCode, setEmailSetupCode] = useState('');
    const [disableEmailOpen, setDisableEmailOpen] = useState(false);
    const [disableEmailPassword, setDisableEmailPassword] = useState('');

    // TOTP MFA setup/disable state
    const [totpSetupData, setTotpSetupData] = useState(null); // { secret, otpauthUrl, qrDataUrl }
    const [totpSetupCode, setTotpSetupCode] = useState('');
    const [disableTotpOpen, setDisableTotpOpen] = useState(false);
    const [disableTotpPassword, setDisableTotpPassword] = useState('');

    const loadSecurityStatus = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/user/security`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMfaEmailEnabled(!!data.mfaEmailEnabled);
                setMfaTotpEnabled(!!data.mfaTotpEnabled);
            }
        } catch (err) {
            console.error(err);
        }
    }, [user?.token]);

    useEffect(() => { loadSecurityStatus(); }, [loadSecurityStatus]);

    const handleStartEmailMfaSetup = async () => {
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/email/send-setup`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${user.token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setEmailSetupStep(true);
                setEmailSetupCode('');
                showMessage(data.message, 'success');
            } else {
                showMessage(data.message || 'Fehler beim Senden des Codes.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleEnableEmailMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/email/enable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ code: emailSetupCode })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaEmailEnabled(true);
                setEmailSetupStep(false);
                setEmailSetupCode('');
                showMessage(data.message, 'success');
            } else {
                showMessage(data.message || 'Ungültiger Code.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDisableEmailMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/email/disable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ currentPassword: disableEmailPassword })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaEmailEnabled(false);
                setDisableEmailOpen(false);
                setDisableEmailPassword('');
                showMessage(data.message, 'success');
            } else {
                showMessage(data.message || 'Fehler beim Deaktivieren.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleStartTotpSetup = async () => {
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/totp/setup`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${user.token}` }
            });
            const data = await res.json();
            if (res.ok && data.otpauthUrl) {
                const qrDataUrl = await QRCode.toDataURL(data.otpauthUrl, { width: 220, margin: 2 });
                setTotpSetupData({ secret: data.secret, otpauthUrl: data.otpauthUrl, qrDataUrl });
                setTotpSetupCode('');
            } else {
                showMessage(data.message || 'Fehler beim Erstellen des QR-Codes.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleEnableTotpMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/totp/enable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ code: totpSetupCode })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaTotpEnabled(true);
                setTotpSetupData(null);
                setTotpSetupCode('');
                showMessage(data.message, 'success');
            } else {
                showMessage(data.message || 'Ungültiger Authenticator-Code.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDisableTotpMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/user/mfa/totp/disable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ currentPassword: disableTotpPassword })
            });
            const data = await res.json();
            if (res.ok) {
                setMfaTotpEnabled(false);
                setDisableTotpOpen(false);
                setDisableTotpPassword('');
                showMessage(data.message, 'success');
            } else {
                showMessage(data.message || 'Fehler beim Deaktivieren.', 'error');
            }
        } catch {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) { showMessage(t('errorPasswordsDoNotMatch', 'Passwörter stimmen nicht überein.'), 'error'); return; }
        try {
            const response = await fetch(`${API_BASE_URL}/user/change-password`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                body: JSON.stringify({ currentPassword, newPassword }),
            });
            const data = await response.json();
            if (response.ok) { showMessage(t('passwordChangeSuccess', 'Passwort geändert.'), 'success'); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); } 
            else { showMessage(data.message || t('passwordChangeFailed', 'Fehler'), 'error'); }
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler.'), 'error'); }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        requestConfirm(t('deleteAccountWarning', 'Diese Aktion kann nicht rückgängig gemacht werden.'), async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/user/delete-account`, {
                    method: 'DELETE', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                    body: JSON.stringify({ email: deleteEmail })
                });
                if (response.ok) { showMessage('Konto gelöscht.', 'success'); handleLogout(); } 
                else { const data = await response.json().catch(()=>({})); showMessage(data.message || 'Fehler', 'error'); }
            } catch (error) { showMessage('Netzwerkfehler.', 'error'); }
        });
    };

    return (
      <div className="p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-widest text-[#d97757] block mb-1">Konto & Sicherheit</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#f3efe6] tracking-tight">{t('settings', 'Einstellungen')}</h2>
          </div>

          {/* MFA / 2FA CARD */}
          <div className="flow-card p-6 md:p-8 mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] mb-2 flex items-center gap-2">
              🛡️ {t('mfaSettingsTitle', 'Zwei-Faktor-Authentifizierung (MFA)')}
            </h3>
            <p className="text-sm text-[#a4b3bc] mb-6">
              {t('mfaSettingsSubtitle', 'Schützen Sie Ihr Konto zusätzlich mit einem Einmalcode per E-Mail oder einer Authenticator-App auf Ihrem Smartphone.')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Option 1: E-Mail MFA */}
              <div className="p-5 rounded-2xl bg-[#131d22] border border-[#4f5f68]/35 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="font-bold text-base text-[#f3efe6] flex items-center gap-2">
                      <span>✉️</span>
                      <span>{t('mfaEmailCardTitle', 'E-Mail-Sicherheitscode')}</span>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono uppercase font-bold ${mfaEmailEnabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-[#4f5f68]/25 text-[#a4b3bc] border border-[#4f5f68]/40'}`}>
                      {mfaEmailEnabled ? t('mfaActive', 'Aktiv') : t('mfaInactive', 'Inaktiv')}
                    </span>
                  </div>
                  <p className="text-xs text-[#a4b3bc] leading-relaxed mb-4">
                    {t('mfaEmailCardDesc', 'Sendet bei der Anmeldung einen 6-stelligen Einmalcode an Ihre bestätigte E-Mail-Adresse.')}
                  </p>
                </div>

                {!mfaEmailEnabled ? (
                  !emailSetupStep ? (
                    <button
                      type="button"
                      disabled={mfaLoading}
                      onClick={handleStartEmailMfaSetup}
                      className="flow-btn-tide w-full py-2.5 text-xs md:text-sm font-bold cursor-pointer"
                    >
                      Per E-Mail aktivieren
                    </button>
                  ) : (
                    <form onSubmit={handleEnableEmailMfa} className="space-y-3 pt-3 border-t border-[#4f5f68]/30">
                      <label htmlFor="setup-email-otp" className="block text-xs font-mono uppercase text-[#a4b3bc]">
                        6-stelliger Code aus E-Mail
                      </label>
                      <p id="setup-email-otp-hint" className="text-[11px] text-[#a4b3bc]">
                        Code gesendet an {user?.email}
                      </p>
                      <input
                        type="text"
                        id="setup-email-otp"
                        name="otp"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{6}"
                        maxLength={6}
                        aria-describedby="setup-email-otp-hint"
                        value={emailSetupCode}
                        onChange={(e) => setEmailSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        required
                        className="flow-input w-full text-center font-mono text-lg tracking-[0.3em]"
                      />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setEmailSetupStep(false)} className="flow-btn-ghost flex-1 py-2 text-xs cursor-pointer">
                          Abbrechen
                        </button>
                        <button type="submit" disabled={mfaLoading || emailSetupCode.length !== 6} className="flow-btn-coral flex-1 py-2 text-xs font-bold cursor-pointer">
                          Bestätigen
                        </button>
                      </div>
                    </form>
                  )
                ) : !disableEmailOpen ? (
                  <button
                    type="button"
                    onClick={() => setDisableEmailOpen(true)}
                    className="w-full py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 text-xs font-semibold transition cursor-pointer"
                  >
                    Deaktivieren
                  </button>
                ) : (
                  <form onSubmit={handleDisableEmailMfa} className="space-y-3 pt-3 border-t border-[#4f5f68]/30">
                    <label htmlFor="disable-email-mfa-pw" className="block text-xs font-mono uppercase text-[#a4b3bc]">
                      Aktuelles Passwort zur Bestätigung
                    </label>
                    <input
                      type="password"
                      id="disable-email-mfa-pw"
                      name="password"
                      autoComplete="current-password"
                      value={disableEmailPassword}
                      onChange={(e) => setDisableEmailPassword(e.target.value)}
                      required
                      className="flow-input w-full text-sm"
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setDisableEmailOpen(false); setDisableEmailPassword(''); }} className="flow-btn-ghost flex-1 py-2 text-xs cursor-pointer">
                        Abbrechen
                      </button>
                      <button type="submit" disabled={mfaLoading} className="bg-red-600 hover:bg-red-500 text-white rounded-xl flex-1 py-2 text-xs font-bold cursor-pointer">
                        Deaktivieren
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Option 2: Smartphone OTP / Authenticator App */}
              <div className="p-5 rounded-2xl bg-[#131d22] border border-[#4f5f68]/35 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="font-bold text-base text-[#f3efe6] flex items-center gap-2">
                      <span>📱</span>
                      <span>{t('mfaTotpCardTitle', 'Smartphone Authenticator-App (OTP)')}</span>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono uppercase font-bold ${mfaTotpEnabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-[#4f5f68]/25 text-[#a4b3bc] border border-[#4f5f68]/40'}`}>
                      {mfaTotpEnabled ? t('mfaActive', 'Aktiv') : t('mfaInactive', 'Inaktiv')}
                    </span>
                  </div>
                  <p className="text-xs text-[#a4b3bc] leading-relaxed mb-4">
                    {t('mfaTotpCardDesc', 'Verwenden Sie Google Authenticator, Microsoft Authenticator, Apple Passwörter oder Authy für zeitbasierte 6-stellige Codes.')}
                  </p>
                </div>

                {!mfaTotpEnabled ? (
                  !totpSetupData ? (
                    <button
                      type="button"
                      disabled={mfaLoading}
                      onClick={handleStartTotpSetup}
                      className="flow-btn-coral w-full py-2.5 text-xs md:text-sm font-bold cursor-pointer"
                    >
                      Authenticator-App einrichten
                    </button>
                  ) : (
                    <form onSubmit={handleEnableTotpMfa} className="space-y-4 pt-3 border-t border-[#4f5f68]/30">
                      <div className="flex flex-col items-center bg-white p-3 rounded-xl w-fit mx-auto">
                        <img src={totpSetupData.qrDataUrl} alt="TOTP QR-Code" className="w-44 h-44 object-contain" />
                      </div>
                      <div className="text-center">
                        <span className="block text-[10px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1">
                          Manueller Schlüssel (falls kein Kamera-Scan möglich):
                        </span>
                        <code className="inline-block bg-[#1a262c] border border-[#4f5f68]/40 px-2.5 py-1 rounded text-xs font-mono text-[#8ec8d4] select-all break-all">
                          {totpSetupData.secret.match(/.{1,4}/g)?.join(' ') || totpSetupData.secret}
                        </code>
                      </div>
                      <div>
                        <label htmlFor="setup-totp-code" className="block text-xs font-mono uppercase text-[#a4b3bc] mb-1">
                          6-stelliger App-Code zur Bestätigung
                        </label>
                        <p id="setup-totp-hint" className="text-[11px] text-[#a4b3bc] mb-2">
                          Scannen Sie den QR-Code in Ihrer Smartphone-App und geben Sie den angezeigten Code ein.
                        </p>
                        <input
                          type="text"
                          id="setup-totp-code"
                          name="otp"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          pattern="\d{6}"
                          maxLength={6}
                          aria-describedby="setup-totp-hint"
                          value={totpSetupCode}
                          onChange={(e) => setTotpSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          required
                          className="flow-input w-full text-center font-mono text-lg tracking-[0.3em]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setTotpSetupData(null)} className="flow-btn-ghost flex-1 py-2 text-xs cursor-pointer">
                          Abbrechen
                        </button>
                        <button type="submit" disabled={mfaLoading || totpSetupCode.length !== 6} className="flow-btn-coral flex-1 py-2 text-xs font-bold cursor-pointer">
                          Aktivieren
                        </button>
                      </div>
                    </form>
                  )
                ) : !disableTotpOpen ? (
                  <button
                    type="button"
                    onClick={() => setDisableTotpOpen(true)}
                    className="w-full py-2.5 rounded-xl bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 text-xs font-semibold transition cursor-pointer"
                  >
                    Deaktivieren
                  </button>
                ) : (
                  <form onSubmit={handleDisableTotpMfa} className="space-y-3 pt-3 border-t border-[#4f5f68]/30">
                    <label htmlFor="disable-totp-mfa-pw" className="block text-xs font-mono uppercase text-[#a4b3bc]">
                      Aktuelles Passwort zur Bestätigung
                    </label>
                    <input
                      type="password"
                      id="disable-totp-mfa-pw"
                      name="password"
                      autoComplete="current-password"
                      value={disableTotpPassword}
                      onChange={(e) => setDisableTotpPassword(e.target.value)}
                      required
                      className="flow-input w-full text-sm"
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setDisableTotpOpen(false); setDisableTotpPassword(''); }} className="flow-btn-ghost flex-1 py-2 text-xs cursor-pointer">
                        Abbrechen
                      </button>
                      <button type="submit" disabled={mfaLoading} className="bg-red-600 hover:bg-red-500 text-white rounded-xl flex-1 py-2 text-xs font-bold cursor-pointer">
                        Deaktivieren
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="flow-card p-6 md:p-8 mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] mb-6 flex items-center gap-2">🔑 {t('changePassword', 'Passwort ändern')}</h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label htmlFor="settings-current-pw" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('currentPassword', 'Aktuelles Passwort')}</label>
                <input type="password" id="settings-current-pw" name="current-password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="flow-input w-full"/>
              </div>
              <div>
                <label htmlFor="settings-new-pw" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('newPassword', 'Neues Passwort')} (mind. 8 Zeichen)</label>
                <input type="password" id="settings-new-pw" name="new-password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="flow-input w-full"/>
              </div>
              <div>
                <label htmlFor="settings-confirm-pw" className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('confirmNewPassword', 'Neues Passwort bestätigen')}</label>
                <input type="password" id="settings-confirm-pw" name="confirm-password" autoComplete="new-password" minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="flow-input w-full"/>
              </div>
              <button type="submit" className="flow-btn-coral w-full py-3 text-sm md:text-base shadow-lg shadow-[#d97757]/20 cursor-pointer">{t('savePassword', 'Speichern')}</button>
            </form>
          </div>

          <div className="bg-[#241315]/80 p-6 md:p-8 rounded-2xl border border-red-500/30 shadow-xl backdrop-blur-md">
            <h3 className="text-xl md:text-2xl font-bold text-red-400 mb-4 flex items-center gap-2">⚠️ {t('deleteAccount', 'Konto löschen')}</h3>
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <label htmlFor="settings-delete-email" className="block text-xs font-mono uppercase tracking-wider text-red-300/80 mb-1.5">Zur Bestätigung E-Mail eingeben: {user?.email}</label>
                <input type="email" id="settings-delete-email" name="email" spellCheck={false} value={deleteEmail} onChange={(e) => setDeleteEmail(e.target.value)} placeholder={user?.email} required className="flow-input w-full border-red-500/40 text-red-100 focus:border-red-400"/>
              </div>
              <button type="submit" className="w-full bg-red-600/80 hover:bg-red-600 text-[#f3efe6] font-semibold py-3 rounded-xl transition shadow-lg shadow-red-600/20 flex items-center justify-center gap-2 cursor-pointer"><Icons.Delete/> Ich möchte mein Konto löschen</button>
            </form>
          </div>
        </div>
      </div>
    );
};

const LegalPage = ({ topic, onNavigate }) => { 
    const { t } = useTranslation();
    const titleKey = topic === 'privacy' ? 'privacyPolicy' : topic === 'cookies' ? 'cookiePolicy' : topic === 'disclaimer' ? 'disclaimer' : 'imprint';
    const textKey = topic === 'privacy' ? 'privacyText' : topic === 'cookies' ? 'cookieText' : topic === 'disclaimer' ? 'disclaimerText' : 'imprintText';
    const tabs = [
        { id: 'privacy', label: t('privacyPolicy', 'Datenschutzerklärung') },
        { id: 'cookies', label: t('cookiePolicy', 'Cookie-Richtlinie') },
        { id: 'disclaimer', label: t('disclaimer', 'Haftungsausschluss') },
        { id: 'imprint', label: t('imprint', 'Impressum') }
    ];
    return (
        <div className="p-4 md:p-8 animate-fade-in">
            <div className="max-w-4xl mx-auto flow-card p-6 md:p-10">
                {onNavigate && (
                    <div className="flex flex-wrap gap-2 pb-6 mb-6 border-b border-[#4f5f68]/30">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => onNavigate(tab.id)}
                                className={`px-3.5 py-1.5 rounded-xl text-xs md:text-sm font-semibold transition cursor-pointer ${topic === tab.id ? 'bg-[#d97757] text-[#f3efe6] shadow-md' : 'bg-[#131d22] text-[#a4b3bc] hover:text-[#f3efe6] border border-[#4f5f68]/30'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}
                <h2 className="text-3xl font-extrabold text-[#f3efe6] mb-6">{t(titleKey, 'Rechtliches & Datenschutz')}</h2>
                <div className="text-[#a4b3bc] text-sm md:text-base leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: t(textKey, '') }} />
            </div>
        </div>
    ); 
};

const TemplateManager = ({ user, showMessage, requestConfirm }) => {
    const { t } = useTranslation();
    const [view, setView] = useState('list');
    const [templates, setTemplates] = useState([]);
    
    const [editId, setEditId] = useState(null);
    const [newTemplate, setNewTemplate] = useState({ name: '', shots: 10, target: 100, type: 'standard', best_of_count: 0, team_size: 0, kombination_data: [] });

    const fetchData = useCallback(async () => {
        try {
            const tempRes = await fetch(`${API_BASE_URL}/templates`, { headers: { 'Authorization': `Bearer ${user.token}` } });
            if (tempRes.ok) setTemplates(await tempRes.json());
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    }, [user.token, showMessage, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        const url = editId ? `${API_BASE_URL}/templates/${editId}` : `${API_BASE_URL}/templates`;
        try {
            const res = await fetch(url, { method: editId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` }, body: JSON.stringify(newTemplate) });
            if (res.ok) { showMessage(editId ? 'Vorlage aktualisiert' : t('templateSaved', 'Vorlage gespeichert'), 'success'); fetchData(); setView('list'); setEditId(null); }
            else { showMessage(t('errorCreatingTemplate', 'Fehler beim Speichern der Vorlage'), 'error'); }
        } catch (error) { showMessage(error.message, 'error'); }
    };

    const handleEdit = (t) => {
        setEditId(t.id);
        setNewTemplate({
            name: t.name,
            shots: t.default_shots || 10,
            target: t.target_value || 100,
            type: t.type || 'standard',
            best_of_count: t.best_of_count || 0,
            team_size: t.team_size || 0,
            kombination_data: t.kombination_data || []
        });
        setView('create_std');
    };

    const handleDelete = async (id) => {
        requestConfirm(t('confirmDelete', 'Löschen bestätigen?'), async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/templates/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (res.ok) { showMessage(t('deleteSuccess', 'Erfolgreich gelöscht'), 'success'); fetchData(); }
            } catch (error) { showMessage(error.message, 'error'); }
        });
    };

    return (
        <div className="p-4 max-w-4xl mx-auto">
            <div className="mb-8 text-center">
                <span className="font-mono text-xs uppercase tracking-widest text-[#d97757] block mb-1">Stich- & Wertungskonfiguration</span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#f3efe6] tracking-tight">{t('manageTemplates', 'Vorlagen verwalten')}</h2>
            </div>
            
            {view === 'list' && (
                <div className="space-y-6">
                    <div className="flex gap-4 justify-center">
                        <button onClick={() => { setEditId(null); setNewTemplate({ name: '', shots: 10, target: 100, type: 'standard', best_of_count: 0, team_size: 0, kombination_data: [] }); setView('create_std'); }} className="flow-btn-coral py-2.5 px-6 shadow-lg shadow-[#d97757]/20 flex items-center gap-2 cursor-pointer"><Icons.Add/> {t('createStandardTemplate', 'Vorlage erstellen')}</button>
                    </div>
                    
                    <div className="flow-card p-6 md:p-8">
                        <h3 className="font-mono text-xs uppercase tracking-widest text-[#a4b3bc] mb-4 border-b border-[#4f5f68]/30 pb-2.5">{t('templates', 'Vorlagen')}</h3>
                        <ul className="divide-y divide-[#4f5f68]/20">{templates.map(t => (
                            <li key={t.id} className="py-3.5 flex justify-between items-center text-[#f3efe6]">
                                <span className="font-medium">{t.name} <span className="text-xs font-mono text-[#a4b3bc]">({t.type === 'kombination' ? 'Berechnung' : t.type === 'anmeldung' ? 'Anmeldung' : t.type === 'differenzler' ? 'Differenzler' : `${t.default_shots} Schuss`})</span></span>
                                {t.created_by === user.id && <div className="flex gap-2">
                                    <button onClick={() => handleEdit(t)} className="text-[#3a7d8a] hover:bg-[#3a7d8a]/20 p-2 rounded-lg transition cursor-pointer" title="Bearbeiten"><Icons.Edit/></button>
                                    <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:bg-red-500/20 p-2 rounded-lg transition cursor-pointer" title="Löschen"><Icons.Delete/></button>
                                </div>}
                            </li>
                        ))}</ul>
                    </div>
                </div>
            )}

            {view === 'create_std' && (
                <form onSubmit={handleSaveTemplate} className="flow-card p-6 md:p-8 space-y-4 max-w-2xl mx-auto">
                    <h3 className="text-2xl font-bold mb-4 text-center text-[#f3efe6]">{editId ? 'Vorlage bearbeiten' : t('createStandardTemplate', 'Vorlage erstellen')}</h3>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('templateName', 'Name')}</label>
                    <input type="text" value={newTemplate.name} onChange={e => setNewTemplate({...newTemplate, name: e.target.value})} className="flow-input w-full" required /></div>
                    
                    {newTemplate.type !== 'kombination' && newTemplate.type !== 'anmeldung' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('shotCount', 'Anzahl Schüsse')}</label>
                            <input type="number" value={newTemplate.shots} onChange={e => setNewTemplate({...newTemplate, shots: parseInt(e.target.value)})} className="flow-input w-full" required /></div>
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('targetType', 'Wertung')}</label>
                            <input type="number" value={newTemplate.target} onChange={e => setNewTemplate({...newTemplate, target: parseInt(e.target.value)})} className="flow-input w-full" required /></div>
                        </div>
                    )}
                    
                    <div className="flex flex-wrap gap-4 mt-4 bg-[#131d22]/80 p-4 rounded-xl border border-[#4f5f68]/30 text-[#f3efe6] text-sm">
                        <label className="flex items-center gap-2 cursor-pointer font-medium">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'standard'} onChange={() => setNewTemplate({...newTemplate, type: 'standard'})} /> Standard
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'best_of'} onChange={() => setNewTemplate({...newTemplate, type: 'best_of'})} /> Best-Of
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'zick_zack'} onChange={() => setNewTemplate({...newTemplate, type: 'zick_zack'})} /> Zick-Zack
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium text-[#d97757]">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'differenzler'} onChange={() => setNewTemplate({...newTemplate, type: 'differenzler'})} /> Differenzler
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'gruppen_stich'} onChange={() => setNewTemplate({...newTemplate, type: 'gruppen_stich'})} /> Gruppenschuss
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium">
                            <input type="radio" className="w-4 h-4 text-[#d97757] focus:ring-[#d97757]" checked={newTemplate.type === 'kombination'} onChange={() => setNewTemplate({...newTemplate, type: 'kombination'})} /> Kombination
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer font-medium text-emerald-400 border-l border-[#4f5f68]/30 pl-4 ml-2">
                            <input type="radio" className="w-4 h-4 text-emerald-500 focus:ring-emerald-500" checked={newTemplate.type === 'anmeldung'} onChange={() => setNewTemplate({...newTemplate, type: 'anmeldung'})} /> Anmeldung (Admin)
                        </label>
                    </div>

                    {newTemplate.type === 'best_of' && (
                        <div className="bg-[#131d22]/80 p-4 rounded-xl border border-[#4f5f68]/30">
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('countBestShots', 'Anzahl beste Schüsse')}</label>
                            <input type="number" value={newTemplate.best_of_count} onChange={e => setNewTemplate({...newTemplate, best_of_count: parseInt(e.target.value)})} className="flow-input w-full" />
                        </div>
                    )}

                    {newTemplate.type === 'gruppen_stich' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#131d22]/80 p-4 rounded-xl border border-[#4f5f68]/30">
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Teamgrösse (0 = beliebig/individuell)</label><input type="number" min="0" value={newTemplate.team_size} onChange={e => setNewTemplate({...newTemplate, team_size: parseInt(e.target.value) || 0})} className="flow-input w-full" /></div>
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Davon zählen die besten X (0 = alle)</label><input type="number" min="0" value={newTemplate.best_of_count} onChange={e => setNewTemplate({...newTemplate, best_of_count: parseInt(e.target.value) || 0})} className="flow-input w-full" /></div>
                        </div>
                    )}

                    {newTemplate.type === 'kombination' && (
                        <div className="bg-[#131d22]/80 p-4 rounded-xl border border-[#4f5f68]/30">
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#d97757] mb-2">{t('components', 'Komponenten (Stiche & Prozente)')}</label>
                            {(newTemplate.kombination_data || []).map((comp, idx) => (
                                <div key={idx} className="flex gap-2 items-center mb-2">
                                    <select value={comp.template_id} onChange={e => { const newData = [...(newTemplate.kombination_data || [])]; newData[idx].template_id = parseInt(e.target.value); setNewTemplate({...newTemplate, kombination_data: newData}); }} className="flow-input flex-1 py-1 text-sm">
                                        <option value="">{t('pleaseSelect', 'Bitte wählen')}</option>
                                        {templates.filter(t => t.id !== editId && t.type !== 'kombination' && t.type !== 'anmeldung').map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                                    </select>
                                    <input type="number" placeholder="%" value={comp.percentage} onChange={e => { const newData = [...(newTemplate.kombination_data || [])]; newData[idx].percentage = parseFloat(e.target.value); setNewTemplate({...newTemplate, kombination_data: newData}); }} className="flow-input w-24 py-1 text-sm"/> <span className="font-mono font-bold text-[#d97757]">%</span>
                                    <button type="button" onClick={() => { const newData = (newTemplate.kombination_data || []).filter((_, i) => i !== idx); setNewTemplate({...newTemplate, kombination_data: newData}); }} className="text-red-400 hover:text-red-300 p-1.5 rounded-lg hover:bg-red-500/20 transition cursor-pointer"><Icons.Delete/></button>
                                </div>
                            ))}
                            <button type="button" onClick={() => setNewTemplate({...newTemplate, kombination_data: [...(newTemplate.kombination_data || []), {template_id: '', percentage: 100}]})} className="text-xs bg-[#d97757]/20 hover:bg-[#d97757]/30 text-[#d97757] border border-[#d97757]/40 px-3 py-1.5 rounded-lg font-mono font-bold mt-2 shadow-xs cursor-pointer flex items-center gap-1.5"><Icons.Add/> {t('addComponent', 'Komponente')}</button>
                        </div>
                    )}
                    
                    <div className="flex gap-4 mt-6">
                        <button type="button" onClick={() => setView('list')} className="flow-btn-ghost flex-1 py-2.5 text-sm cursor-pointer">{t('cancel', 'Abbrechen')}</button>
                        <button type="submit" className="flow-btn-coral flex-1 py-2.5 text-sm shadow-lg shadow-[#d97757]/20 cursor-pointer flex items-center justify-center gap-2"><Icons.Add/> {t('save', 'Speichern')}</button>
                    </div>
                </form>
            )}
        </div>
    );
};

const EventKasseModal = ({ event, user, showMessage, requestConfirm, onBack, setPrintData, t }) => {
    const [stiche, setStiche] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [allReceipts, setAllReceipts] = useState([]);
    
    const [selectedStiche, setSelectedStiche] = useState([]);
    const [companionStiche, setCompanionStiche] = useState([]);
    const [participant, setParticipant] = useState({ firstname: '', lastname: '', yearOfBirth: '', city: '', contact: '', notes: '' });
    const [activeTab, setActiveTab] = useState('create');
    const [editingParticipantId, setEditingParticipantId] = useState(null);
    
    const [isCustomPrice, setIsCustomPrice] = useState(false);
    const [customPrice, setCustomPrice] = useState(0);
    
    const fetchData = useCallback(async () => {
        try {
            const [stRes, pRes, rRes] = await Promise.all([
                fetch(`${API_BASE_URL}/events/${event.id}/stiche`, { headers: { 'Authorization': `Bearer ${user.token}` } }),
                fetch(`${API_BASE_URL}/events/${event.id}/participants`, { headers: { 'Authorization': `Bearer ${user.token}` } }),
                fetch(`${API_BASE_URL}/receipts?groupId=${event.group_id}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
            ]);
            if(stRes.ok) setStiche(await stRes.json());
            if(pRes.ok) setParticipants(await pRes.json());
            if(rRes.ok) setAllReceipts(await rRes.json());
        } catch(e) { console.error(e); }
    }, [event.id, event.group_id, user.token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleToggleStich = (id) => {
        if (selectedStiche.includes(id)) {
            setSelectedStiche(selectedStiche.filter(s => s !== id));
            setCompanionStiche(companionStiche.filter(s => s !== id));
        } else {
            setSelectedStiche([...selectedStiche, id]);
        }
    };

    const handleToggleCompanion = (id) => {
        if (companionStiche.includes(id)) setCompanionStiche(companionStiche.filter(s => s !== id));
        else setCompanionStiche([...companionStiche, id]);
    };

    const calculatedTotalPrice = selectedStiche.reduce((sum, id) => {
        const stich = stiche.find(s => s.id === id);
        const basePrice = stich ? Number(stich.price) : 0;
        const isCompanion = companionStiche.includes(id);
        return sum + basePrice + (isCompanion ? basePrice : 0);
    }, 0);

    const effectiveTotalPrice = isCustomPrice ? Number(customPrice) : calculatedTotalPrice;

    const handleSave = async () => {
        if(!participant.lastname || !participant.firstname) { showMessage("Bitte Name und Vorname ausfüllen.", "error"); return; }
        try {
            const url = editingParticipantId ? `${API_BASE_URL}/events/${event.id}/participants/${editingParticipantId}` : `${API_BASE_URL}/events/${event.id}/participants`;
            const method = editingParticipantId ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` }, body: JSON.stringify({ ...participant, totalPrice: effectiveTotalPrice, sticheIds: selectedStiche, companionSticheIds: companionStiche }) });
            if(res.ok) {
                showMessage(editingParticipantId ? 'Schütze aktualisiert!' : t('standblattSaved', 'Standblatt erfolgreich gespeichert!'), 'success');
                fetchData(); 
                if (!editingParticipantId) handleShowStandblatt({ ...participant, sticheIds: selectedStiche, companionSticheIds: companionStiche, totalPrice: effectiveTotalPrice });
                else setActiveTab('list');
                
                setParticipant({ firstname: '', lastname: '', yearOfBirth: '', city: '', contact: '', notes: '' });
                setSelectedStiche([]); setCompanionStiche([]); setEditingParticipantId(null);
                setIsCustomPrice(false); setCustomPrice(0);
            } else { const data = await res.json().catch(()=>({})); showMessage(data.message || 'Fehler beim Speichern.', 'error'); }
        } catch(error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    };

    const handleDeleteParticipant = async (participantId) => {
        requestConfirm("Diesen Schützen wirklich unwiderruflich löschen?", async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/events/${event.id}/participants/${participantId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if(res.ok) { showMessage('Schütze gelöscht', 'success'); fetchData(); } 
            } catch(error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
        });
    };

    const handleShowStandblatt = async (p) => {
        const fn1 = `${p.lastname} ${p.firstname}`.trim().toLowerCase(); 
        const fn2 = `${p.firstname} ${p.lastname}`.trim().toLowerCase();
        const pReceipts = allReceipts.filter(r => {
            const rName = (r.participant_name || '').toLowerCase();
            return rName === fn1 || rName === fn2;
        });
        setPrintData({ event, participant: p, selectedStiche: p.sticheIds || [], stiche, receipts: pReceipts });
    };

    const [printKasseActive, setPrintKasseActive] = useState(false);
    const [kassePrintOrientation, setKassePrintOrientation] = useState('landscape');
    const [kasseSearch, setKasseSearch] = useState('');

    const handleKasseExcelExport = () => {
        let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
        
        const headers = [
            "Nr.",
            "Name",
            "Vorname",
            "Jahrgang",
            "PLZ / Ort",
            "Kontakt (Tel / E-Mail)",
            "Betrag (CHF)",
            "Status",
            "Gelöste Stiche",
            "Geschossene Resultate",
            "Notizen"
        ];
        csvContent += headers.map(h => `"${h}"`).join(";") + "\n";

        let totalRevenue = 0;
        let unpaidAlertCount = 0;

        participants.forEach((p, index) => {
            const rawName1 = `${p.lastname} ${p.firstname}`.toLowerCase().trim();
            const rawName2 = `${p.firstname} ${p.lastname}`.toLowerCase().trim();
            const price = Number(p.total_price || 0);
            totalRevenue += price;

            const relevantStiche = stiche.filter(s => {
                const hasPaid = (p.sticheIds || []).includes(s.id);
                const hasShot = allReceipts.some(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                return hasPaid || hasShot;
            });

            const geloesteSticheArr = [];
            const resultateArr = [];
            let hasUnpaid = false;

            relevantStiche.forEach(s => {
                const hasPaid = (p.sticheIds || []).includes(s.id);
                const receipt = allReceipts.find(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                const hasShot = !!receipt;

                if (hasPaid) {
                    const isCompanion = (p.companionSticheIds || []).includes(s.id);
                    geloesteSticheArr.push(`${s.template_name} (CHF ${Number(s.price * (isCompanion ? 2 : 1)).toFixed(2)}${isCompanion ? ' inkl. Begleitung' : ''})`);
                }
                if (hasShot) {
                    const scoreStr = receipt.template_type === 'differenzler' 
                        ? `Diff ${Number(receipt.total_score).toFixed(1).replace(/\.0$/, '')}` 
                        : `${Number(receipt.total_score).toFixed(1).replace(/\.0$/, '')} Pkt`;
                    resultateArr.push(`${s.template_name}: ${scoreStr}${!hasPaid ? ' [NICHT BEZAHLT!]' : ''}`);
                }
                if (!hasPaid && hasShot) {
                    hasUnpaid = true;
                }
            });

            if (hasUnpaid) unpaidAlertCount++;
            const statusStr = hasUnpaid ? "⚠️ Unbezahlte Stiche vorhanden!" : "Bezahlt";

            const row = [
                index + 1,
                p.lastname || '',
                p.firstname || '',
                p.year_of_birth || '',
                p.city || '',
                p.contact || '',
                price.toFixed(2),
                statusStr,
                geloesteSticheArr.join(", "),
                resultateArr.join(", "),
                (p.notes || '').replace(/\r?\n/g, ' ')
            ];

            csvContent += row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";") + "\n";
        });

        csvContent += `\n"TOTAL EINNAHMEN";"";"";"";"";"";"${totalRevenue.toFixed(2)}";"Schützen: ${participants.length}${unpaidAlertCount > 0 ? ` (${unpaidAlertCount} mit unbezahlten Stichen)` : ''}";"";"";""\n`;

        try {
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            const safeEventName = (event?.name || 'Event').replace(/[^a-zA-Z0-9_-]/g, '_');
            link.setAttribute("download", `Kasse_Schuetzen_${safeEventName}_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            showMessage("Excel-Export erfolgreich heruntergeladen!", "success");
        } catch(err) {
            console.error("Export Error", err);
            showMessage("Fehler beim Excel-Export", "error");
        }
    };

    if (printKasseActive) {
        const totalRevenue = participants.reduce((sum, p) => sum + Number(p.total_price || 0), 0);
        return createPortal(
            <div className="fixed inset-0 bg-[#10171a] print:bg-white text-black font-sans min-h-screen z-[9999999] overflow-y-auto print-kasse-container">
                <style>{`
                    @media print { 
                        nav, footer, .no-print, #root { display: none !important; } 
                        @page { size: A4 ${kassePrintOrientation}; margin: 8mm; } 
                        body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
                        .print-kasse-container { position: static !important; background: white !important; overflow: visible !important; width: 100% !important; height: auto !important; }
                        table { page-break-inside: auto; }
                        tr { page-break-inside: avoid; page-break-after: auto; }
                        * { overflow: visible !important; } 
                    }
                `}</style>
                <div className="no-print bg-[#162127] border-b border-[#4f5f68]/40 p-4 text-[#f3efe6] flex flex-wrap justify-between items-center shadow-2xl sticky top-0 z-[10000000] gap-4">
                    <span className="font-bold flex items-center gap-3 text-sm font-mono">
                        <span className="text-[#3a7d8a] font-bold tracking-wider">DRUCKVORSCHAU / KASSE & STANDBLATT</span>
                        <span className="text-[#a4b3bc] text-xs">({event.name} — {participants.length} Schützen)</span>
                    </span>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex bg-[#131d22] border border-[#4f5f68]/40 rounded-xl p-1 text-xs font-mono">
                            <button 
                                type="button" 
                                onClick={() => setKassePrintOrientation('landscape')} 
                                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${kassePrintOrientation === 'landscape' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                            >
                                Querformat
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setKassePrintOrientation('portrait')} 
                                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${kassePrintOrientation === 'portrait' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                            >
                                Hochformat
                            </button>
                        </div>
                        <button onClick={() => window.print()} className="flow-btn-coral px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer shadow-lg shadow-[#d97757]/30 hover:brightness-110 active:scale-95 transition-all"><Icons.Print/> Jetzt Drucken / Als PDF speichern</button>
                        <button onClick={() => setPrintKasseActive(false)} className="flow-btn-ghost px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer hover:bg-[#4f5f68]/20 transition-all"><Icons.Close/> Ansicht schliessen</button>
                    </div>
                </div>

                <div className="p-8 max-w-7xl mx-auto bg-white my-6 shadow-2xl print:shadow-none print:m-0 print:p-0 print:max-w-none">
                    <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
                        <div>
                            <img src={logoWithNameURL} alt="Logo" className="h-14 mb-2 block object-contain" />
                            <h1 className="text-3xl font-bold">Kasse & Standblatt — Schützenliste</h1>
                            <h2 className="text-xl font-semibold text-gray-700">{event.name}</h2>
                        </div>
                        <div className="text-right font-mono text-xs text-gray-600">
                            <div>Druckdatum: {new Date().toLocaleDateString('de-CH')}</div>
                            <div>Zeit: {new Date().toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}</div>
                            <div className="mt-2 text-sm font-bold text-black border border-black px-3 py-1 bg-gray-100 rounded inline-block">
                                Total Kasse: CHF {totalRevenue.toFixed(2)}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-6 font-mono text-xs print:grid-cols-3">
                        <div className="border border-black p-3 rounded bg-gray-50">
                            <div className="text-gray-600 uppercase text-[10px] font-bold">Erfasste Schützen</div>
                            <div className="text-2xl font-black text-black">{participants.length}</div>
                        </div>
                        <div className="border border-black p-3 rounded bg-gray-50">
                            <div className="text-gray-600 uppercase text-[10px] font-bold">Total Einnahmen (Kasse)</div>
                            <div className="text-2xl font-black text-black">CHF {totalRevenue.toFixed(2)}</div>
                        </div>
                        <div className="border border-black p-3 rounded bg-gray-50">
                            <div className="text-gray-600 uppercase text-[10px] font-bold">Durchschnitt pro Schütze</div>
                            <div className="text-2xl font-black text-black">CHF {participants.length > 0 ? (totalRevenue / participants.length).toFixed(2) : '0.00'}</div>
                        </div>
                    </div>

                    <table className="w-full text-left border-collapse border border-black text-xs">
                        <thead>
                            <tr className="bg-gray-200 border-b border-black font-bold">
                                <th className="p-2 border border-black w-10 text-center">Nr.</th>
                                <th className="p-2 border border-black w-44">Schütze</th>
                                <th className="p-2 border border-black w-32">Wohnort / Jg.</th>
                                <th className="p-2 border border-black w-36">Kontakt</th>
                                <th className="p-2 border border-black">Gelöste Stiche & Resultate</th>
                                <th className="p-2 border border-black w-44">Notizen</th>
                                <th className="p-2 border border-black w-24 text-right">Betrag</th>
                            </tr>
                        </thead>
                        <tbody>
                            {participants.map((p, idx) => {
                                const rawName1 = `${p.lastname} ${p.firstname}`.toLowerCase().trim();
                                const rawName2 = `${p.firstname} ${p.lastname}`.toLowerCase().trim();
                                const relevantStiche = stiche.filter(s => {
                                    const hasPaid = (p.sticheIds || []).includes(s.id);
                                    const hasShot = allReceipts.some(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                                    return hasPaid || hasShot;
                                });

                                return (
                                    <tr key={p.id} className="border-b border-gray-300 align-top">
                                        <td className="p-2 border border-black text-center font-mono font-bold">{idx + 1}</td>
                                        <td className="p-2 border border-black font-bold text-black">{p.lastname} {p.firstname}</td>
                                        <td className="p-2 border border-black">
                                            <div>{p.city || '-'}</div>
                                            {p.year_of_birth && <div className="text-[11px] text-gray-600 font-mono">Jg. {p.year_of_birth}</div>}
                                        </td>
                                        <td className="p-2 border border-black font-mono text-[11px]">{p.contact || '-'}</td>
                                        <td className="p-2 border border-black">
                                            <div className="flex flex-wrap gap-1">
                                                {relevantStiche.map(s => {
                                                    const hasPaid = (p.sticheIds || []).includes(s.id);
                                                    const receipt = allReceipts.find(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                                                    const hasShot = !!receipt;
                                                    const scoreText = hasShot ? (receipt.template_type === 'differenzler' ? `: Diff ${Number(receipt.total_score).toFixed(1).replace(/\.0$/, '')}` : `: ${Number(receipt.total_score).toFixed(1).replace('.0', '')}`) : "";

                                                    let badgeClass = "bg-gray-100 text-gray-700 border-gray-300";
                                                    if (hasPaid && hasShot) badgeClass = "bg-green-100 text-green-900 border-green-500 font-semibold";
                                                    else if (hasPaid && !hasShot) badgeClass = "bg-blue-50 text-blue-900 border-blue-400";
                                                    else if (!hasPaid && hasShot) badgeClass = "bg-red-100 text-red-900 border-red-500 font-bold";

                                                    return (
                                                        <span key={s.id} className={`inline-block border px-1.5 py-0.5 rounded text-[10px] font-mono ${badgeClass}`}>
                                                            {s.template_name}{scoreText}{!hasPaid && hasShot ? " [NICHT BEZAHLT!]" : ""}
                                                        </span>
                                                    );
                                                })}
                                                {relevantStiche.length === 0 && <span className="text-gray-400 italic text-[10px]">-</span>}
                                            </div>
                                        </td>
                                        <td className="p-2 border border-black font-mono text-[11px] text-gray-700 whitespace-pre-wrap">{p.notes || '-'}</td>
                                        <td className="p-2 border border-black font-mono font-bold text-right text-black">CHF {Number(p.total_price || 0).toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-200 border-t-2 border-black font-bold">
                                <td colSpan="6" className="p-2 border border-black text-right uppercase font-mono">Total Einnahmen (Event-Kasse):</td>
                                <td className="p-2 border border-black font-mono text-right text-sm">CHF {totalRevenue.toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>,
            document.body
        );
    }

    return createPortal(
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl animate-scale-in custom-scrollbar">
                <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-4">
                    <button onClick={onBack} className="text-[#a4b3bc] hover:text-[#f3efe6] font-mono text-sm flex items-center gap-1.5 transition cursor-pointer">&larr; {t('back', 'Zurück')}</button>
                    <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">💰 {t('kasseAndStandblatt', 'Kasse & Standblatt')} — {event.name}</h3>
                </div>
                <div className="flex gap-2 mb-6 border-b border-[#4f5f68]/30 pb-3">
                    <button onClick={() => setActiveTab('create')} className={`px-4 py-2 font-mono text-sm font-semibold rounded-xl transition cursor-pointer ${activeTab === 'create' ? 'bg-[#d97757] text-[#162127] font-bold shadow-md shadow-[#d97757]/20' : 'text-[#a4b3bc] hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{editingParticipantId ? "Schütze bearbeiten" : "Schütze erfassen"}</button>
                    <button onClick={() => {
                        setActiveTab('list'); setEditingParticipantId(null); 
                        setParticipant({ firstname: '', lastname: '', yearOfBirth: '', city: '', contact: '', notes: '' }); 
                        setSelectedStiche([]); setCompanionStiche([]);
                        setIsCustomPrice(false); setCustomPrice(0);
                    }} className={`px-4 py-2 font-mono text-sm font-semibold rounded-xl transition cursor-pointer ${activeTab === 'list' ? 'bg-[#d97757] text-[#162127] font-bold shadow-md shadow-[#d97757]/20' : 'text-[#a4b3bc] hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>Erfasste Schützen ({participants.length})</button>
                </div>
                {activeTab === 'create' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="font-mono text-xs uppercase tracking-wider text-[#d97757] mb-3">{t('participantDetails', 'Teilnehmerdaten (optional)')}</h4>
                            <div className="space-y-3">
                                <input type="text" placeholder={t('firstname', 'Vorname')} value={participant.firstname} onChange={e => setParticipant({...participant, firstname: e.target.value})} className="flow-input w-full" required />
                                <input type="text" placeholder={t('lastname', 'Name')} value={participant.lastname} onChange={e => setParticipant({...participant, lastname: e.target.value})} className="flow-input w-full" required />
                                <div className="flex gap-3">
                                    <input type="text" placeholder={t('yearOfBirth', 'Jahrgang')} value={participant.yearOfBirth} onChange={e => setParticipant({...participant, yearOfBirth: e.target.value})} className="flow-input w-1/3" />
                                    <input type="text" placeholder={t('city', 'Ort')} value={participant.city} onChange={e => setParticipant({...participant, city: e.target.value})} className="flow-input w-2/3" />
                                </div>
                                <input type="text" placeholder="Kontakt (Tel. / E-Mail) - optional" value={participant.contact} onChange={e => setParticipant({...participant, contact: e.target.value})} className="flow-input w-full" />
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">📝 Notizen (individuell / intern für dich):</label>
                                    <textarea
                                        placeholder="Individuelle Notizen zum Schützen (z.B. Stand, Waffe, Infos etc.)..."
                                        value={participant.notes || ''}
                                        onChange={e => setParticipant({...participant, notes: e.target.value})}
                                        className="flow-input w-full text-sm"
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#131d22]/80 p-5 rounded-xl border border-[#4f5f68]/30">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="font-mono text-xs uppercase tracking-wider text-[#3a7d8a]">Gelöste Stiche (Preise in CHF)</h4>
                                <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#3a7d8a] bg-[#3a7d8a]/15 border border-[#3a7d8a]/30 px-2.5 py-1 rounded-lg hover:bg-[#3a7d8a]/25 transition">
                                    <input type="checkbox" className="w-4 h-4 rounded cursor-pointer accent-[#3a7d8a]" checked={selectedStiche.length === stiche.length && stiche.length > 0} onChange={(e) => { if (e.target.checked) setSelectedStiche(stiche.map(s => s.id)); else { setSelectedStiche([]); setCompanionStiche([]); } }} /> Alle Stiche
                                </label>
                            </div>
                            <ul className="space-y-2 mb-4 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                {stiche.map(s => {
                                    const isSelected = selectedStiche.includes(s.id);
                                    const isAnmeldung = s.type === 'anmeldung';
                                    const hasCompanion = companionStiche.includes(s.id);

                                    return (
                                        <li key={s.id} className={`border transition-all p-3 rounded-xl ${isSelected ? 'bg-[#3a7d8a]/15 border-[#3a7d8a]/40 text-[#f3efe6]' : 'border-[#4f5f68]/20 bg-[#162127]/60 hover:bg-[#4f5f68]/15 text-[#a4b3bc]'}`}>
                                            <div className="flex justify-between items-center cursor-pointer" onClick={() => handleToggleStich(s.id)}>
                                                <div className="flex items-center gap-3">
                                                    <input type="checkbox" checked={isSelected} readOnly className="w-4 h-4 accent-[#d97757] rounded cursor-pointer" />
                                                    <span className="font-bold text-sm text-[#f3efe6]">{s.template_name}</span>
                                                </div>
                                                <span className="font-mono text-sm text-[#d97757] font-semibold">CHF {Number(s.price).toFixed(2)}</span>
                                            </div>
                                            {isSelected && isAnmeldung && (
                                                <div className="ml-7 mt-2 p-2.5 bg-[#10171a] rounded-lg border border-[#3a7d8a]/30 shadow-sm flex justify-between items-center">
                                                    <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#8ec8d4]">
                                                        <input type="checkbox" checked={hasCompanion} onChange={() => handleToggleCompanion(s.id)} className="w-4 h-4 accent-[#3a7d8a] rounded" />
                                                        Mit Begleitung (+1 Person)
                                                    </label>
                                                    {hasCompanion && <span className="text-xs font-mono font-bold text-[#d97757]">+ CHF {Number(s.price).toFixed(2)}</span>}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            
                            <div className="flex flex-col border-t border-[#4f5f68]/30 pt-4 mb-6">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-mono uppercase tracking-wider text-[#a4b3bc]">Total zu bezahlen:</span>
                                    {isCustomPrice ? (
                                        <span className="text-[#a4b3bc]/60 line-through font-mono text-base">CHF {calculatedTotalPrice.toFixed(2)}</span>
                                    ) : (
                                        <span className="text-2xl font-mono font-black text-[#d97757]">CHF {calculatedTotalPrice.toFixed(2)}</span>
                                    )}
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#a4b3bc]">
                                        <input type="checkbox" checked={isCustomPrice} onChange={(e) => { 
                                             setIsCustomPrice(e.target.checked); 
                                             if(e.target.checked) setCustomPrice(calculatedTotalPrice); 
                                         }} className="w-4 h-4 accent-[#d97757] rounded cursor-pointer" />
                                         Sonderpreis / Manuelles Total
                                     </label>
                                     {isCustomPrice && (
                                         <div className="flex items-center gap-2">
                                             <span className="font-mono font-bold text-xs text-[#a4b3bc]">CHF</span>
                                             <input type="number" step="0.05" value={customPrice} onChange={e => setCustomPrice(e.target.value)} className="flow-input w-28 py-1 text-right font-mono font-bold text-[#d97757] text-sm" />
                                         </div>
                                     )}
                                </div>
                            </div>
                            
                            <button onClick={handleSave} className="flow-btn-coral w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-[#d97757]/20 cursor-pointer">{editingParticipantId ? <><Icons.Edit/> Aktualisieren</> : <><Icons.Print/> Speichern & Vorschau</>}</button>
                            {editingParticipantId && <button onClick={() => { 
                                setEditingParticipantId(null); 
                                setParticipant({ firstname: '', lastname: '', yearOfBirth: '', city: '', contact: '', notes: '' }); 
                                setSelectedStiche([]); setCompanionStiche([]); 
                                setIsCustomPrice(false); setCustomPrice(0);
                                setActiveTab('list'); 
                            }} className="flow-btn-ghost w-full mt-2 py-2.5 rounded-xl text-sm font-bold cursor-pointer">Abbrechen</button>}
                        </div>
                    </div>
                )}
                {activeTab === 'list' && (
                    <div className="space-y-4">
                        <div className="bg-[#131d22]/90 border border-emerald-500/30 p-4 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm">
                            <div>
                                <span className="text-emerald-300 font-mono text-xs uppercase tracking-wider block mb-0.5">Total Einnahmen (Event-Kasse):</span>
                                <span className="text-emerald-400 font-mono font-extrabold text-2xl">CHF {participants.reduce((sum, p) => sum + Number(p.total_price || 0), 0).toFixed(2)}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button 
                                    type="button" 
                                    onClick={handleKasseExcelExport} 
                                    className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 font-semibold py-2 px-3.5 rounded-xl text-xs md:text-sm shadow-sm flex items-center gap-2 transition cursor-pointer"
                                    title="Alle Schützen und Kasse als Excel/CSV exportieren"
                                >
                                    📊 Excel Export
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setPrintKasseActive(true)} 
                                    className="bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 font-semibold py-2 px-3.5 rounded-xl text-xs md:text-sm shadow-sm flex items-center gap-2 transition cursor-pointer"
                                    title="Kassenbericht und Schützenliste drucken oder als PDF speichern"
                                >
                                    🖨️ Kasse drucken / PDF
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                placeholder="Schütze suchen (Name, Vorname, Ort, Kontakt)..." 
                                value={kasseSearch} 
                                onChange={e => setKasseSearch(e.target.value)} 
                                className="flow-input w-full text-sm"
                            />
                            {kasseSearch && (
                                <button 
                                    type="button" 
                                    onClick={() => setKasseSearch('')} 
                                    className="flow-btn-ghost px-3 text-xs font-mono whitespace-nowrap cursor-pointer"
                                >
                                    Zurücksetzen
                                </button>
                            )}
                        </div>
                        
                        <div className="space-y-3 mt-4">
                            {(() => {
                                const q = kasseSearch.trim().toLowerCase();
                                const displayed = q ? participants.filter(p => {
                                    const full1 = `${p.lastname} ${p.firstname}`.toLowerCase();
                                    const full2 = `${p.firstname} ${p.lastname}`.toLowerCase();
                                    const city = (p.city || '').toLowerCase();
                                    const contact = (p.contact || '').toLowerCase();
                                    const notes = (p.notes || '').toLowerCase();
                                    return full1.includes(q) || full2.includes(q) || city.includes(q) || contact.includes(q) || notes.includes(q);
                                }) : participants;

                                if (displayed.length === 0) {
                                    return <p className="text-center py-8 text-[#a4b3bc] font-mono italic text-xs">{q ? 'Keine Schützen gefunden für diese Suche.' : 'Noch keine Schützen erfasst.'}</p>;
                                }

                                return displayed.map(p => {
                                    const rawName1 = `${p.lastname} ${p.firstname}`.toLowerCase().trim();
                                    const rawName2 = `${p.firstname} ${p.lastname}`.toLowerCase().trim();
                                    
                                    const relevantStiche = stiche.filter(s => {
                                        const hasPaid = (p.sticheIds || []).includes(s.id);
                                        const hasShot = allReceipts.some(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                                        return hasPaid || hasShot;
                                    });

                                    return (
                                        <div key={p.id} className="border border-[#4f5f68]/30 p-4 rounded-xl bg-[#131d22]/80 hover:border-[#4f5f68]/60 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all">
                                            <div className="flex-1">
                                                <div className="flex items-center flex-wrap gap-2 mb-2">
                                                    <span className="font-bold text-lg text-[#f3efe6]">{p.lastname} {p.firstname}</span>
                                                    {p.year_of_birth && <span className="text-xs bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/30 font-mono px-2 py-0.5 rounded">Jg. {p.year_of_birth}</span>}
                                                    {p.city && <span className="text-xs text-[#a4b3bc] bg-[#4f5f68]/20 px-2 py-0.5 rounded border border-[#4f5f68]/30 font-mono">{p.city}</span>}
                                                    {p.contact && <span className="text-xs text-[#a4b3bc] font-mono">📞 {p.contact}</span>}
                                                    <span className="font-mono font-bold text-[#d97757] bg-[#d97757]/10 px-3 py-1 rounded-full border border-[#d97757]/30 text-xs ml-auto md:ml-0">CHF {Number(p.total_price).toFixed(2)}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {relevantStiche.map(s => {
                                                        const hasPaid = (p.sticheIds || []).includes(s.id);
                                                        const receipt = allReceipts.find(r => r.event_stich_id === s.id && ((r.participant_name||'').toLowerCase() === rawName1 || (r.participant_name||'').toLowerCase() === rawName2));
                                                        const hasShot = !!receipt;
                                                        
                                                        let badgeStyle = "bg-[#4f5f68]/20 text-[#a4b3bc] border-[#4f5f68]/30";
                                                        let scoreText = hasShot ? (receipt.template_type === 'differenzler' ? `: Diff ${Number(receipt.total_score).toFixed(1).replace(/\.0$/, '')}` : `: ${Number(receipt.total_score).toFixed(1).replace('.0', '')}`) : "";
                                                        
                                                        if(hasPaid && hasShot) { 
                                                          badgeStyle = "bg-emerald-500/20 text-emerald-300 border-emerald-500/40 font-bold"; 
                                                        } else if(hasPaid && !hasShot) { 
                                                          badgeStyle = "bg-[#3a7d8a]/20 text-[#8ec8d4] border-[#3a7d8a]/40"; 
                                                          scoreText = ""; 
                                                        } else if(!hasPaid && hasShot) { 
                                                          badgeStyle = "bg-red-500/20 text-red-300 border-red-500/50 font-bold animate-pulse"; 
                                                          scoreText += " ❌ Nicht bezahlt!"; 
                                                        }
                                                        
                                                        return (
                                                            <span key={s.id} className={`text-xs px-2.5 py-1 rounded-lg border font-mono ${badgeStyle} flex items-center shadow-xs`}>
                                                                {s.template_name}{scoreText}
                                                            </span>
                                                        );
                                                    })}
                                                    {relevantStiche.length === 0 && <span className="text-[#a4b3bc]/60 italic text-xs font-mono">Keine Stiche gelöst/geschossen</span>}
                                                </div>
                                                {p.notes && (
                                                    <div className="mt-2.5 text-xs bg-[#1a262c] text-amber-200/90 border border-amber-500/30 rounded-lg p-2.5 flex items-start gap-1.5 shadow-xs max-w-2xl font-mono">
                                                        <span className="font-bold text-amber-300 whitespace-nowrap">📝 Notiz:</span>
                                                        <span className="whitespace-pre-wrap">{p.notes}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2 w-full md:w-auto">
                                                <button onClick={() => { 
                                                    setParticipant({ firstname: p.firstname, lastname: p.lastname, yearOfBirth: p.year_of_birth || '', city: p.city || '', contact: p.contact || '', notes: p.notes || '' }); 
                                                    setSelectedStiche(p.sticheIds || []); 
                                                    setCompanionStiche(p.companionSticheIds || []); 
                                                    const calcTotal = (p.sticheIds || []).reduce((sum, id) => {
                                                        const stich = stiche.find(s => s.id === id);
                                                        return sum + (stich ? Number(stich.price) : 0) + ((p.companionSticheIds || []).includes(id) ? (stich ? Number(stich.price) : 0) : 0);
                                                    }, 0);
                                                    setIsCustomPrice(Number(p.total_price) !== calcTotal);
                                                    setCustomPrice(Number(p.total_price) !== calcTotal ? Number(p.total_price) : 0);
                                                    setEditingParticipantId(p.id); setActiveTab('create'); 
                                                }} className="flex-1 md:flex-none flow-btn-ghost px-3 py-2 rounded-xl text-xs font-mono font-bold flex justify-center items-center cursor-pointer"><Icons.Edit/></button>
                                                <button onClick={() => handleShowStandblatt(p)} className="flex-1 md:flex-none flow-btn-tide px-4 py-2 rounded-xl text-xs font-mono font-bold flex justify-center items-center gap-2 cursor-pointer"><Icons.Print/> <span className="hidden sm:inline">Standblatt</span></button>
                                                <button onClick={() => handleDeleteParticipant(p.id)} className="flex-none text-red-400 hover:bg-red-500/20 border border-red-500/30 p-2 rounded-xl cursor-pointer transition flex justify-center items-center"><Icons.Delete/></button>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

const StichManagerModal = ({ event, user, showMessage, requestConfirm, onBack, t }) => {
    const [stiche, setStiche] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [newStich, setNewStich] = useState({ template_id: '', price: 0, sort_order: 0 });

    const fetchData = useCallback(async () => {
        try {
            const [stRes, tRes] = await Promise.all([
                fetch(`${API_BASE_URL}/events/${event.id}/stiche`, { headers: { 'Authorization': `Bearer ${user.token}` } }),
                fetch(`${API_BASE_URL}/templates`, { headers: { 'Authorization': `Bearer ${user.token}` } })
            ]);
            if (stRes.ok) setStiche(await stRes.json());
            if (tRes.ok) setTemplates(await tRes.json());
        } catch(error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    }, [event.id, user.token, showMessage, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE_URL}/events/${event.id}/stiche`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: JSON.stringify(newStich)
            });
            if (res.ok) { showMessage(t('stichCreated', 'Stich erstellt'), 'success'); fetchData(); setNewStich({ template_id: '', price: 0, sort_order: 0}); }
        } catch(error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    };

    const handleDelete = async (id) => {
        requestConfirm(t('confirmDelete', 'Löschen bestätigen?'), async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/stiche/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (res.ok) { showMessage(t('deleteSuccess', 'Erfolgreich gelöscht'), 'success'); fetchData(); }
            } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
        });
    };

    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in custom-scrollbar">
                <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-4">
                    <button onClick={onBack} className="text-[#a4b3bc] hover:text-[#f3efe6] font-mono text-sm flex items-center gap-1.5 transition cursor-pointer">&larr; {t('back', 'Zurück')}</button>
                    <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">🎯 Stiche für: {event.name}</h3>
                </div>
                
                <form onSubmit={handleCreate} className="mb-6 bg-[#131d22]/80 p-5 rounded-xl space-y-4 border border-[#4f5f68]/30">
                    <h4 className="font-mono text-xs uppercase tracking-wider text-[#d97757] border-b border-[#4f5f68]/30 pb-2">{t('addStich', 'Neuen Stich erfassen')}</h4>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Vorlage</label>
                        <select required value={newStich.template_id} onChange={e => setNewStich({...newStich, template_id: e.target.value})} className="flow-input w-full text-sm">
                            <option value="">{t('pleaseSelect', 'Bitte wählen')}</option>
                            {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.type === 'kombination' ? 'Berechnung' : t.type === 'anmeldung' ? 'Anmeldung' : `${t.default_shots} Schuss auf ${t.target_value}`})</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('price', 'Preis (CHF)')}</label>
                        <input type="number" step="0.05" min="0" value={newStich.price} onChange={e => setNewStich({...newStich, price: parseFloat(e.target.value)})} className="flow-input w-full font-mono text-sm" />
                    </div>
                    <button type="submit" className="flow-btn-coral w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-[#d97757]/20 cursor-pointer"><Icons.Add/> {t('create', 'Erstellen')}</button>
                </form>

                <h4 className="font-mono text-xs uppercase tracking-wider text-[#3a7d8a] mb-3">{t('existingStiche', 'Erfasste Stiche')}</h4>
                <ul className="space-y-3">
                    {stiche.map(s => (
                        <li key={s.id} className="p-4 bg-[#131d22]/80 border border-[#4f5f68]/30 rounded-xl flex justify-between items-center hover:border-[#4f5f68]/50 transition">
                            <div>
                                <h5 className="font-bold text-base text-[#f3efe6]">{s.template_name}</h5>
                                <p className="text-xs font-mono text-[#a4b3bc] mt-0.5">Preis: <span className="text-[#d97757] font-semibold">CHF {Number(s.price).toFixed(2)}</span></p>
                            </div>
                            <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:bg-red-500/20 border border-red-500/30 p-2 rounded-xl transition cursor-pointer"><Icons.Delete/></button>
                        </li>
                    ))}
                    {stiche.length === 0 && <p className="text-[#a4b3bc] font-mono text-center py-6 italic text-xs">{t('noStiche', 'Noch keine Stiche erfasst.')}</p>}
                </ul>
            </div>
        </div>
    );
};

const EventManagerModal = ({ groupId, user, showMessage, requestConfirm, onClose, t }) => {
    const [events, setEvents] = useState([]);
    const [view, setView] = useState('list');
    const [newEvent, setNewEvent] = useState({ name: '', description: '', start_date: '', end_date: '' });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [qrModalData, setQrModalData] = useState(null);

    const fetchEvents = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${groupId}/events`, { headers: { 'Authorization': `Bearer ${user.token}` } });
            if (res.ok) setEvents(await res.json());
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    }, [groupId, user.token, showMessage, t]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${groupId}/events`, { 
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`},
                body: JSON.stringify(newEvent)
            });
            if (res.ok) { showMessage(t('eventCreated', 'Event erstellt'), 'success'); fetchEvents(); setView('list'); setNewEvent({name:'', description:'', start_date:'', end_date:''}); }
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    };

    const handleTogglePublish = async (ev) => {
        const nextState = !ev.is_public;
        try {
            const res = await fetch(`${API_BASE_URL}/events/${ev.id}/publish`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                },
                body: JSON.stringify({ is_public: nextState })
            });
            const data = await res.json();
            if (res.ok) {
                showMessage(data.message || (nextState ? t('eventPublished', 'Event veröffentlicht') : t('eventUnpublished', 'Event auf privat gesetzt')), 'success');
                fetchEvents();
            } else {
                showMessage(data.message || 'Fehler beim Aktualisieren', 'error');
            }
        } catch (error) {
            showMessage(t('networkError', 'Netzwerkfehler'), 'error');
        }
    };

    const handleDelete = async (id) => {
        requestConfirm(t('confirmDelete', 'Löschen bestätigen?'), async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/events/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (res.ok) { showMessage(t('deleteSuccess', 'Erfolgreich gelöscht'), 'success'); fetchEvents(); }
            } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
        });
    };

    if (view === 'stiche' && selectedEvent) { return <StichManagerModal event={selectedEvent} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onBack={() => setView('list')} t={t} />; }

    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
            <QRCodeModal isOpen={!!qrModalData} onClose={() => setQrModalData(null)} targetData={qrModalData} t={t} />
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in custom-scrollbar">
                <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-3">
                    <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">📅 {t('manageEvents') || 'Events Verwalten'}</h3>
                    <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition"><Icons.Close/></button>
                </div>
                
                {view === 'list' && (
                    <>
                        <button onClick={() => setView('create')} className="mb-6 flow-btn-coral py-2.5 px-4 rounded-xl w-full flex items-center justify-center gap-2 text-sm shadow-lg shadow-[#d97757]/20 cursor-pointer"><Icons.Add/> {t('createEvent') || 'Neues Event erstellen'}</button>
                        <div className="space-y-4">
                            {events.map(ev => (
                                <div key={ev.id} className="p-4 bg-[#131d22]/80 border border-[#4f5f68]/30 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-[#4f5f68]/60 transition">
                                    <div>
                                        <div className="flex items-center gap-2.5 flex-wrap">
                                            <h4 className="font-bold text-lg text-[#f3efe6]">{ev.name}</h4>
                                            {ev.is_public ? (
                                                <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded-full text-xs font-mono font-bold flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> {t('published', 'Öffentlich')}
                                                </span>
                                            ) : (
                                                <span className="bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/30 px-2 py-0.5 rounded-full text-xs font-mono flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span> Privat
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-[#a4b3bc] mb-2">{ev.description}</p>
                                        <div className="inline-block bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/30 text-xs font-mono px-2 py-0.5 rounded">{ev.start_date ? new Date(ev.start_date).toLocaleDateString() : 'Offen'} - {ev.end_date ? new Date(ev.end_date).toLocaleDateString() : 'Offen'}</div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                                        <button 
                                            type="button" 
                                            onClick={() => handleTogglePublish(ev)} 
                                            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border flex items-center gap-1.5 cursor-pointer ${ev.is_public ? 'bg-emerald-600/25 hover:bg-emerald-600/35 text-emerald-300 border-emerald-500/40' : 'bg-[#1a262c] hover:bg-[#21323a] text-[#a4b3bc] border-[#4f5f68]/40'}`}
                                            title={ev.is_public ? t('unpublishEventHint', 'Event und alle Stiche auf privat setzen') : t('publishEventHint', 'Event und alle Stiche für das Public Scoreboard veröffentlichen')}
                                        >
                                            <span className={`w-2 h-2 rounded-full ${ev.is_public ? 'bg-emerald-400 animate-pulse' : 'bg-gray-400'}`}></span>
                                            {ev.is_public ? t('published', 'Öffentlich') : t('publishEvent', 'Veröffentlichen')}
                                        </button>

                                        <button 
                                            type="button"
                                            onClick={() => setQrModalData({
                                                groupId: ev.group_id || groupId,
                                                groupName: ev.group_name || 'Gruppe',
                                                eventId: ev.id,
                                                eventName: ev.name,
                                                folderId: ev.folder_id,
                                                folderName: ev.name
                                            })} 
                                            className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 font-semibold px-3 py-1.5 rounded-xl text-xs transition cursor-pointer flex items-center gap-1"
                                            title={t('qrCodeFlyer', 'QR-Code & Aushang generieren')}
                                        >
                                            📱 {t('qrCode', 'QR-Code')}
                                        </button>

                                        <button onClick={() => { setSelectedEvent(ev); setView('stiche'); }} className="flex-1 md:flex-none bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 font-semibold px-3 py-1.5 rounded-xl text-xs transition cursor-pointer">{t('manageStiche') || 'Stiche konfigurieren'}</button>
                                        <button onClick={() => handleDelete(ev.id)} className="text-red-400 hover:bg-red-500/20 p-1.5 rounded-xl border border-red-500/30 transition cursor-pointer"><Icons.Delete/></button>
                                    </div>
                                </div>
                            ))}
                            {events.length === 0 && <p className="text-center text-[#a4b3bc] font-mono py-8 italic text-sm">{t('noEvents') || 'Keine Events gefunden.'}</p>}
                        </div>
                    </>
                )}

                {view === 'create' && (
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('eventName') || 'Name (z.B. Sauschiessen)'}</label><input type="text" required value={newEvent.name} onChange={e => setNewEvent({...newEvent, name: e.target.value})} className="flow-input w-full" /></div>
                        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('eventDescription') || 'Beschreibung'}</label><textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} className="flow-input w-full" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('startDate') || 'Startdatum'}</label><input type="date" value={newEvent.start_date} onChange={e => setNewEvent({...newEvent, start_date: e.target.value})} className="flow-input w-full text-sm" /></div>
                            <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('endDate') || 'Enddatum'}</label><input type="date" value={newEvent.end_date} onChange={e => setNewEvent({...newEvent, end_date: e.target.value})} className="flow-input w-full text-sm" /></div>
                        </div>
                        <div className="flex space-x-3 pt-4 border-t border-[#4f5f68]/30">
                            <button type="button" onClick={() => setView('list')} className="flow-btn-ghost flex-1 py-2.5 text-sm cursor-pointer">{t('cancel', 'Abbrechen')}</button>
                            <button type="submit" className="flow-btn-coral flex-1 py-2.5 text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer"><Icons.Add/> {t('create', 'Erstellen')}</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const EventsPage = ({ user, userGroups, showMessage, requestConfirm, t, setPrintData }) => {
    const [events, setEvents] = useState([]);
    const [view, setView] = useState('list');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [newEvent, setNewEvent] = useState({ group_id: '', name: '', description: '', start_date: '', end_date: '' });
    const [qrModalData, setQrModalData] = useState(null);

    const fetchEvents = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/events`, { headers: { 'Authorization': `Bearer ${user.token}` } });
            if (res.ok) setEvents(await res.json());
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    }, [user.token, showMessage, t]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${newEvent.group_id}/events`, { 
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`},
                body: JSON.stringify(newEvent)
            });
            if (res.ok) { showMessage(t('eventCreated', 'Event erstellt'), 'success'); fetchEvents(); setView('list'); setNewEvent({group_id: '', name:'', description:'', start_date:'', end_date:''}); }
        } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
    };

    const handleTogglePublish = async (ev) => {
        const nextState = !ev.is_public;
        try {
            const res = await fetch(`${API_BASE_URL}/events/${ev.id}/publish`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                },
                body: JSON.stringify({ is_public: nextState })
            });
            const data = await res.json();
            if (res.ok) {
                showMessage(data.message || (nextState ? t('eventPublished', 'Event veröffentlicht') : t('eventUnpublished', 'Event auf privat gesetzt')), 'success');
                fetchEvents();
            } else {
                showMessage(data.message || 'Fehler beim Aktualisieren', 'error');
            }
        } catch (error) {
            showMessage(t('networkError', 'Netzwerkfehler'), 'error');
        }
    };

    const handleDelete = async (id) => {
        requestConfirm(t('confirmDelete', 'Löschen bestätigen?'), async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/events/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (res.ok) { showMessage(t('deleteSuccess', 'Erfolgreich gelöscht'), 'success'); fetchEvents(); }
            } catch (error) { showMessage(t('networkError', 'Netzwerkfehler'), 'error'); }
        });
    };

    if (view === 'stiche' && selectedEvent) return <StichManagerModal event={selectedEvent} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onBack={() => setView('list')} t={t} />;
    if (view === 'kasse' && selectedEvent) return <EventKasseModal event={selectedEvent} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onBack={() => setView('list')} setPrintData={setPrintData} t={t} />;

    return (
        <div className="p-4 max-w-5xl mx-auto">
            <QRCodeModal isOpen={!!qrModalData} onClose={() => setQrModalData(null)} targetData={qrModalData} t={t} />
            <div className="mb-8 text-center">
                <span className="font-mono text-xs uppercase tracking-widest text-[#d97757] block mb-1">Anlässe & Schiessanlässe</span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-[#f3efe6] tracking-tight">{t('eventsTab', 'Events')}</h2>
            </div>
            
            <div className="mb-6 bg-[#131d22]/90 border border-[#3a7d8a]/40 text-[#8ec8d4] p-4 rounded-2xl shadow-sm flex items-start gap-3">
                <span className="text-xl">ℹ️</span>
                <p className="text-xs font-mono leading-relaxed">
                    <strong className="text-[#f3efe6]">Live-Rangliste & Freigabe:</strong><br/>
                    Aktiviere einfach den Schalter <strong>"{t('publishEvent', 'Veröffentlichen')}"</strong> beim gewünschten Event, um das Event und alle seine Stiche für das Public Scoreboard freizugeben. Mit dem <strong>"📱 QR-Code"</strong> Button kannst du direkt einen Aushang / Flyer für das Schützenhaus drucken oder den Link teilen!
                </p>
            </div>

            {view === 'list' && (
                <div className="flow-card p-6 md:p-8">
                    <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-4">
                        <h3 className="font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('manageEvents', 'Events verwalten')}</h3>
                        <button onClick={() => setView('create')} className="flow-btn-coral py-2 px-4 text-sm font-semibold rounded-xl flex items-center gap-2 cursor-pointer shadow-lg shadow-[#d97757]/20"><Icons.Add/> {t('createEvent', 'Neues Event erstellen')}</button>
                    </div>
                    <div className="space-y-4">
                        {events.map(ev => (
                            <div key={ev.id} className="p-4 bg-[#131d22]/80 border border-[#4f5f68]/30 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-[#4f5f68]/60 transition">
                                <div>
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        <h4 className="font-bold text-xl text-[#f3efe6]">{ev.name}</h4>
                                        {ev.is_public ? (
                                            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2.5 py-0.5 rounded-full text-xs font-mono font-bold flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-emerald-400"></span> {t('published', 'Öffentlich')}
                                            </span>
                                        ) : (
                                            <span className="bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/30 px-2.5 py-0.5 rounded-full text-xs font-mono flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-gray-400"></span> Privat
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 mb-1 mt-1 font-mono text-xs">
                                        <span className="bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40 px-2 py-0.5 rounded font-bold">{ev.group_name}</span>
                                        <span className="bg-[#4f5f68]/20 text-[#a4b3bc] px-2 py-0.5 rounded border border-[#4f5f68]/30">{ev.start_date ? new Date(ev.start_date).toLocaleDateString() : 'Offen'} - {ev.end_date ? new Date(ev.end_date).toLocaleDateString() : 'Offen'}</span>
                                    </div>
                                    {ev.description && <p className="text-xs text-[#a4b3bc] mt-1">{ev.description}</p>}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                                    <button 
                                        type="button" 
                                        onClick={() => handleTogglePublish(ev)} 
                                        className={`px-3 py-2 rounded-xl text-xs md:text-sm font-semibold transition border flex items-center gap-2 cursor-pointer ${ev.is_public ? 'bg-emerald-600/25 hover:bg-emerald-600/35 text-emerald-300 border-emerald-500/40 shadow-xs' : 'bg-[#1a262c] hover:bg-[#21323a] text-[#a4b3bc] border-[#4f5f68]/40'}`}
                                        title={ev.is_public ? t('unpublishEventHint', 'Event und alle Stiche auf privat setzen') : t('publishEventHint', 'Event und alle Stiche für das Public Scoreboard veröffentlichen')}
                                    >
                                        <span className={`w-2.5 h-2.5 rounded-full ${ev.is_public ? 'bg-emerald-400 animate-pulse' : 'bg-gray-400'}`}></span>
                                        {ev.is_public ? t('published', 'Öffentlich') : t('publishEvent', 'Veröffentlichen')}
                                    </button>

                                    <button 
                                        type="button"
                                        onClick={() => setQrModalData({
                                            groupId: ev.group_id,
                                            groupName: ev.group_name,
                                            eventId: ev.id,
                                            eventName: ev.name,
                                            folderId: ev.folder_id,
                                            folderName: ev.name
                                        })} 
                                        className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 font-semibold px-3 py-2 rounded-xl text-xs md:text-sm transition cursor-pointer flex items-center gap-1.5"
                                        title={t('qrCodeFlyer', 'QR-Code & Aushang generieren')}
                                    >
                                        📱 <span>{t('qrCode', 'QR-Code')}</span>
                                    </button>

                                    <button onClick={() => { setSelectedEvent(ev); setView('kasse'); }} className="flex-1 md:flex-none bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 font-semibold px-4 py-2 rounded-xl text-xs md:text-sm transition cursor-pointer">{t('kasseAndStandblatt', 'Kasse & Standblatt')}</button>
                                    <button onClick={() => { setSelectedEvent(ev); setView('stiche'); }} className="flex-1 md:flex-none bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 font-semibold px-4 py-2 rounded-xl text-xs md:text-sm transition cursor-pointer">{t('manageStiche', 'Stiche verwalten')}</button>
                                    <button onClick={() => handleDelete(ev.id)} className="text-red-400 hover:bg-red-500/20 p-2 rounded-xl border border-red-500/30 transition cursor-pointer"><Icons.Delete/></button>
                                </div>
                            </div>
                        ))}
                        {events.length === 0 && <p className="text-center text-[#a4b3bc] font-mono py-8 italic text-sm">{t('noEvents', 'Keine Events gefunden.')}</p>}
                    </div>
                </div>
            )}
            {view === 'create' && (
                <form onSubmit={handleCreate} className="flow-card p-6 md:p-8 space-y-4 max-w-xl mx-auto">
                    <h3 className="text-2xl font-bold mb-4 text-[#f3efe6]">{t('createEvent', 'Event erstellen')}</h3>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Gruppe auswählen *</label>
                        <select required value={newEvent.group_id} onChange={e => setNewEvent({...newEvent, group_id: e.target.value})} className="flow-input w-full">
                            <option value="">Bitte wählen...</option>
                            {userGroups.filter(g => g.can_manage_events || g.created_by === user.id).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                    </div>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('eventName', 'Eventname')}</label><input type="text" required value={newEvent.name} onChange={e => setNewEvent({...newEvent, name: e.target.value})} className="flow-input w-full" /></div>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('eventDescription', 'Beschreibung')}</label><textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} className="flow-input w-full" /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('startDate', 'Startdatum')}</label><input type="date" value={newEvent.start_date} onChange={e => setNewEvent({...newEvent, start_date: e.target.value})} className="flow-input w-full" /></div>
                        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('endDate', 'Enddatum')}</label><input type="date" value={newEvent.end_date} onChange={e => setNewEvent({...newEvent, end_date: e.target.value})} className="flow-input w-full" /></div>
                    </div>
                    <div className="flex space-x-4 pt-4 border-t border-[#4f5f68]/30">
                        <button type="button" onClick={() => setView('list')} className="flow-btn-ghost flex-1 py-2.5 text-sm cursor-pointer">{t('cancel', 'Abbrechen')}</button>
                        <button type="submit" className="flow-btn-coral flex-1 py-2.5 text-sm shadow-lg shadow-[#d97757]/20 cursor-pointer flex items-center justify-center gap-2"><Icons.Add/> {t('create', 'Erstellen')}</button>
                    </div>
                </form>
            )}
        </div>
    );
};

const Dashboard = ({ user, userGroups, folders, showMessage, requestConfirm, onDataChange, editingReceipt, onCancelEdit }) => {
    const { t } = useTranslation();
    const [captureMode, setCaptureMode] = useState('template');
    const [dbTemplates, setDbTemplates] = useState([]);

    const [participantName, setParticipantName] = useState('');
    const [teamName, setTeamName] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [selectedFolderId, setSelectedFolderId] = useState('');
    const [receiptId, setReceiptId] = useState('');
    const [yearOfBirth, setYearOfBirth] = useState('');
    const [manualRankModifier, setManualRankModifier] = useState(0);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    
    const [groupEvents, setGroupEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [eventStiche, setEventStiche] = useState([]);
    const [selectedStichId, setSelectedStichId] = useState('');
    const [eventParticipants, setEventParticipants] = useState([]);
    const [selectedParticipantId, setSelectedParticipantId] = useState('');

    const [totalScore, setTotalScore] = useState('');
    const [shotCount, setShotCount] = useState('10');
    const [targetType, setTargetType] = useState('100');
    const [individualScores, setIndividualScores] = useState([]);

    const [kombinationDetails, setKombinationDetails] = useState([]);
    const [isCompanion, setIsCompanion] = useState(false);
    const [contactInfo, setContactInfo] = useState('');
    const [estimatedScore, setEstimatedScore] = useState('');
    const [existingTeamNames, setExistingTeamNames] = useState([]);

    useEffect(() => {
        if (selectedGroupId) {
            fetch(`${API_BASE_URL}/receipts?groupId=${selectedGroupId}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
                .then(res => res.json())
                .then(data => {
                    const teams = new Set();
                    data.forEach(r => {
                        if (r.team_name) teams.add(r.team_name.trim());
                    });
                    setExistingTeamNames(Array.from(teams).sort());
                })
                .catch(console.error);
        } else {
            setExistingTeamNames([]);
        }
    }, [selectedGroupId, user.token]);

    const directionMap = useMemo(() => ({ ul: '↖', u: '↑', ur: '↗', l: '←', c: '●', r: '→', dl: '↙', d: '↓', dr: '↘' }), []);
    const directionOrder = useMemo(() => ['ul', 'u', 'ur', 'l', 'c', 'r', 'dl', 'd', 'dr'], []);

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const tempRes = await fetch(`${API_BASE_URL}/templates`, { headers: { 'Authorization': `Bearer ${user.token}` } });
                if (tempRes.ok) {
                    const data = await tempRes.json();
                    setDbTemplates(data);
                    if (data.length > 0 && !editingReceipt) setSelectedTemplateId(data[0].id);
                }
            } catch (error) { console.error(error); }
        };
        loadTemplates();
    }, [user.token, editingReceipt]);

    useEffect(() => {
        if (captureMode === 'event' && selectedGroupId) {
            fetch(`${API_BASE_URL}/groups/${selectedGroupId}/events`, { headers: { 'Authorization': `Bearer ${user.token}` } }).then(res => res.json()).then(data => { setGroupEvents(data); if(!editingReceipt) {setSelectedEventId(''); setEventStiche([]); setSelectedStichId('');} }).catch(console.error);
        }
    }, [captureMode, selectedGroupId, user.token, editingReceipt]);

    useEffect(() => {
        if (captureMode === 'event' && selectedEventId) {
            fetch(`${API_BASE_URL}/events/${selectedEventId}/stiche`, { headers: { 'Authorization': `Bearer ${user.token}` } }).then(res => res.json()).then(data => { setEventStiche(data); if(!editingReceipt) setSelectedStichId(''); }).catch(console.error);
            fetch(`${API_BASE_URL}/events/${selectedEventId}/participants`, { headers: { 'Authorization': `Bearer ${user.token}` } }).then(res => res.json()).then(data => { setEventParticipants(data); if(!editingReceipt) setSelectedParticipantId(''); }).catch(console.error);
        }
    }, [captureMode, selectedEventId, user.token, editingReceipt]);

    useEffect(() => {
        if (editingReceipt) {
            setCaptureMode(editingReceipt.event_stich_id ? 'event' : (editingReceipt.template_id ? 'template' : 'individual'));
            setParticipantName(editingReceipt.participant_name || '');
            setTeamName(editingReceipt.team_name || '');
            setReceiptId(editingReceipt.receipt_id || '');
            setTotalScore(editingReceipt.total_score ? Number(editingReceipt.total_score).toString() : '');
            setShotCount(editingReceipt.shot_count?.toString() || '10');
            setTargetType(editingReceipt.target_type?.toString() || '100');
            setYearOfBirth(editingReceipt.year_of_birth || '');
            setManualRankModifier(editingReceipt.manual_rank_modifier || 0);
            setEstimatedScore(editingReceipt.estimated_score !== undefined && editingReceipt.estimated_score !== null ? Number(editingReceipt.estimated_score).toString() : '');
            
            if (editingReceipt.template_type === 'anmeldung') {
                const data = (typeof editingReceipt.individual_scores === 'string' ? JSON.parse(editingReceipt.individual_scores) : editingReceipt.individual_scores)[0] || {};
                setIsCompanion(!!data.companion);
                setContactInfo(data.contact || '');
                setIndividualScores([]);
            } else {
                setIndividualScores(typeof editingReceipt.individual_scores === 'string' ? JSON.parse(editingReceipt.individual_scores) : (editingReceipt.individual_scores || []));
            }
            
            setSelectedGroupId(editingReceipt.group_id?.toString() || '');
            setTimeout(() => {
                setSelectedFolderId(editingReceipt.folder_id?.toString() || '');
                if(editingReceipt.event_id) setSelectedEventId(editingReceipt.event_id.toString());
                setTimeout(() => {
                    if(editingReceipt.event_stich_id) setSelectedStichId(editingReceipt.event_stich_id.toString());
                    if(editingReceipt.template_id) setSelectedTemplateId(editingReceipt.template_id.toString());
                }, 300);
            }, 300);
        }
    }, [editingReceipt]);

    const handleParticipantChange = (e) => {
        const pid = e.target.value; setSelectedParticipantId(pid);
        if (pid) { 
            const p = eventParticipants.find(x => x.id == pid); 
            if (p) {
                setParticipantName(`${p.lastname} ${p.firstname}`.trim()); 
                setYearOfBirth(p.year_of_birth || '');
            }
        }
    };

    const currentTemplate = useMemo(() => {
        if (captureMode === 'template') return dbTemplates.find(t => t.id == selectedTemplateId);
        if (captureMode === 'event') { const stich = eventStiche.find(s => s.id == selectedStichId); return stich ? dbTemplates.find(t => t.id == stich.template_id) : null; }
        return null;
    }, [captureMode, selectedTemplateId, selectedStichId, dbTemplates, eventStiche]);

    useEffect(() => {
        if (!editingReceipt) { setIsCompanion(false); setContactInfo(''); setEstimatedScore(''); }
    }, [currentTemplate, captureMode, editingReceipt]);

    // Live-Berechnung des Totals für alle Vorlagen
    useEffect(() => {
        if (currentTemplate && currentTemplate.type === 'best_of') {
            const validScores = individualScores.map(s => parseFloat(s.score)).filter(s => !isNaN(s)).sort((a, b) => b - a);
            const bestOfCount = currentTemplate.best_of_count || currentTemplate.default_shots || 10;
            setTotalScore(validScores.slice(0, bestOfCount).reduce((a, b) => a + b, 0).toFixed(1).replace(/\.0$/, ''));
        } else if (currentTemplate && currentTemplate.type === 'differenzler') {
            const validScores = individualScores.map(s => parseFloat(s.score)).filter(s => !isNaN(s));
            const actual = validScores.reduce((a, b) => a + b, 0);
            if (estimatedScore !== '' && !isNaN(parseFloat(estimatedScore))) {
                const diff = Math.abs(actual - parseFloat(estimatedScore));
                setTotalScore(diff.toFixed(1).replace(/\.0$/, ''));
            } else {
                setTotalScore('');
            }
        } else if (currentTemplate && currentTemplate.type !== 'kombination' && currentTemplate.type !== 'anmeldung') {
            const validScores = individualScores.map(s => parseFloat(s.score)).filter(s => !isNaN(s));
            setTotalScore(validScores.reduce((a, b) => a + b, 0).toFixed(1).replace(/\.0$/, ''));
        } else if (captureMode === 'individual') {
             const validScores = individualScores.map(s => parseFloat(s.score)).filter(s => !isNaN(s));
             setTotalScore(validScores.reduce((a, b) => a + b, 0).toFixed(1).replace(/\.0$/, ''));
        }
    }, [individualScores, currentTemplate, captureMode, estimatedScore]);

    const bestOfLiveInfo = useMemo(() => {
        if (!currentTemplate || currentTemplate.type !== 'best_of') return null;
        const count = currentTemplate.best_of_count || currentTemplate.default_shots || 10;
        const parsed = individualScores.map((s, idx) => ({
            origIdx: idx,
            score: parseFloat(s.score),
            hasVal: s.score !== '' && !isNaN(parseFloat(s.score))
        }));
        const valid = parsed.filter(s => s.hasVal).sort((a, b) => b.score - a.score);
        const topIndices = new Set(valid.slice(0, count).map(s => s.origIdx));
        const tieBreaker = valid.length > count ? valid[count] : null;
        return {
            count,
            topIndices,
            tieBreakerIdx: tieBreaker ? tieBreaker.origIdx : null,
            tieBreakerScore: tieBreaker ? tieBreaker.score : null,
            total: valid.slice(0, count).reduce((sum, s) => sum + s.score, 0)
        };
    }, [currentTemplate, individualScores]);

    const loadParticipantScores = useCallback(async () => {
        if (currentTemplate?.type === 'kombination' && participantName && selectedGroupId) {
             try {
                 const res = await fetch(`${API_BASE_URL}/receipts?groupId=${selectedGroupId}`, { headers: { 'Authorization': `Bearer ${user.token}` } });
                 if (res.ok) {
                     const receipts = await res.json();
                     const pName1 = participantName.trim().toLowerCase(); const parts = participantName.trim().split(' '); const pNameRev = parts.length === 2 ? `${parts[1]} ${parts[0]}`.toLowerCase() : pName1;
                     const pReceipts = receipts.filter(r => { const rName = (r.participant_name || '').toLowerCase(); return rName === pName1 || rName === pNameRev; });
                     let calcTotal = 0; const details = [];
                     (currentTemplate.kombination_data || []).forEach(comp => {
                         const tReceipts = pReceipts.filter(r => Number(r.template_id) === Number(comp.template_id));
                         let bestScore = 0; if (tReceipts.length > 0) bestScore = Math.max(...tReceipts.map(r => parseFloat(r.total_score) || 0));
                         const weighted = bestScore * (Number(comp.percentage) / 100); calcTotal += weighted;
                         const tName = dbTemplates.find(t => t.id == comp.template_id)?.name || 'Unbekannt'; details.push({ name: tName, bestScore, percentage: comp.percentage, weighted });
                     });
                     setTotalScore(Number(calcTotal).toFixed(2).replace(/\.00$/, '')); setKombinationDetails(details);
                 }
             } catch (error) { console.error(error); }
        }
    }, [currentTemplate, participantName, selectedGroupId, user.token, dbTemplates]);

    useEffect(() => { loadParticipantScores(); }, [loadParticipantScores]);

    const handleCalculateAll = async () => {
        requestConfirm(`Alle ${eventParticipants.length} Schützen berechnen?`, async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/receipts?groupId=${selectedGroupId}`, { headers: { 'Authorization': `Bearer ${user.token}` } });
                const allReceipts = await res.json();
                const existingCombinationReceipts = allReceipts.filter(r => r.event_stich_id == selectedStichId);
                let savedCount = 0;

                for (const p of eventParticipants) {
                    const rawName = `${p.lastname} ${p.firstname}`.trim(); const fn1 = rawName.toLowerCase(); const fn2 = `${p.firstname} ${p.lastname}`.trim().toLowerCase();
                    const pReceipts = allReceipts.filter(r => { const rName = (r.participant_name || '').toLowerCase(); return rName === fn1 || rName === fn2; });
                    let calcTotal = 0; const details = [];
                    (currentTemplate.kombination_data || []).forEach(comp => {
                        const tReceipts = pReceipts.filter(r => Number(r.template_id) === Number(comp.template_id));
                        let bestScore = 0; if (tReceipts.length > 0) bestScore = Math.max(...tReceipts.map(r => parseFloat(r.total_score) || 0));
                        const weighted = bestScore * (Number(comp.percentage) / 100); calcTotal += weighted;
                        details.push({ name: dbTemplates.find(t => t.id == comp.template_id)?.name || 'Unbekannt', bestScore, percentage: comp.percentage, weighted });
                    });
                    if (calcTotal > 0) {
                        const existing = existingCombinationReceipts.find(r => r.participant_name === rawName);
                        const receiptData = { participantName: rawName, teamName: null, totalScore: parseFloat(calcTotal.toFixed(2)), shotCount: 0, targetType: 100, individualScores: details, receiptId: '', groupId: selectedGroupId, folderId: null, templateId: currentTemplate.id, eventStichId: selectedStichId };
                        const url = existing ? `${API_BASE_URL}/receipts/${existing.id}` : `${API_BASE_URL}/receipts`;
                        const saveRes = await fetch(url, { method: existing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` }, body: JSON.stringify(receiptData) });
                        if(saveRes.ok) savedCount++;
                    }
                }
                if (savedCount === 0) showMessage('Achtung: 0 Resultate berechnet!', 'error'); else showMessage(`Erfolgreich! ${savedCount} Resultate gespeichert.`, 'success');
                onDataChange();
            } catch (error) { showMessage(`Fehler: ${error.message}`, 'error'); } finally { setLoading(false); }
        });
    };

    const resizeImageForScan = (file, maxDimension = 1600) => {
        return new Promise((resolve, reject) => {
            if (file.size < 2.5 * 1024 * 1024) {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
                reader.readAsDataURL(file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    let { width, height } = img;
                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = Math.round((height * maxDimension) / width);
                            width = maxDimension;
                        } else {
                            width = Math.round((width * maxDimension) / height);
                            height = maxDimension;
                        }
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                };
                img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
            reader.readAsDataURL(file);
        });
    };

    const handleScanImage = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setScanning(true);
        setLoading(true);
        showMessage("Optimiere und analysiere Bild mit lokaler KI...", "info");
        try {
            const base64 = await resizeImageForScan(file, 1600);
            const expected = parseInt(shotCount, 10) || (currentTemplate?.default_shots || 10);
            const response = await fetch(`${API_BASE_URL}/scan-receipt`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                },
                body: JSON.stringify({
                    image: base64,
                    expectedShots: expected
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.message || errorData.error || 'Fehler beim Erkennen des Belegs';
                showMessage(`Scan Fehler (${response.status}): ${errorMsg}`, "error");
                setLoading(false);
                return;
            }

            const data = await response.json();
            if (data.success && (Array.isArray(data.shots) || Array.isArray(data.scores))) {
                const list = (data.shots && data.shots.length > 0) ? data.shots : (data.scores || []);
                if (list.length > 0) {
                    let formatted = list.map(s => typeof s === 'object' ? { score: (s.score !== undefined ? s.score : '').toString(), direction: s.direction || '' } : { score: s.toString(), direction: '' });
                    if (expected > formatted.length && (captureMode === 'template' || captureMode === 'event')) {
                        while (formatted.length < expected) {
                            formatted.push({ score: '', direction: '' });
                        }
                        setIndividualScores(formatted);
                        showMessage(`Erfolgreich ${list.length} von ${expected} Schüssen gescannt!`, "success");
                    } else {
                        setIndividualScores(formatted);
                        setShotCount(formatted.length.toString());
                        showMessage(`Erfolgreich ${formatted.length} Schüsse gescannt!`, "success");
                    }
                } else {
                    showMessage("Keine Schüsse auf dem Beleg erkannt.", "error");
                }
            } else {
                showMessage("Keine Schüsse auf dem Beleg erkannt.", "error");
            }
        } catch (error) {
            showMessage(`Fehler beim Scan: ${error.message}`, "error");
        } finally {
            setLoading(false);
            setScanning(false);
            if (e.target) e.target.value = '';
        }
    };

    useEffect(() => {
        if (editingReceipt) return;
        if ((captureMode === 'template' || captureMode === 'event') && currentTemplate) {
            if (currentTemplate.type !== 'kombination' && currentTemplate.type !== 'anmeldung') {
                const initialCount = currentTemplate.default_shots || 10;
                setShotCount(initialCount.toString());
                setIndividualScores(Array.from({ length: initialCount }, () => ({ score: '', direction: '' })));
            }
        } else if (captureMode === 'individual') {
             const count = parseInt(shotCount, 10); setIndividualScores(Array.from({ length: isNaN(count) ? 0 : count }, () => ({ score: '', direction: '' })));
        }
    }, [captureMode, currentTemplate, editingReceipt]);
    
        const handleShotKeyDown = (e, index) => {
        if (e.key === "ArrowDown" || e.key === "Enter") {
            e.preventDefault();
            const next = document.getElementById("shot-input-" + (index + 1));
            if (next) { next.focus(); next.select(); }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prev = document.getElementById("shot-input-" + (index - 1));
            if (prev) { prev.focus(); prev.select(); }
        } else if (e.key === "ArrowRight") {
            const target = e.target;
            if (target.selectionEnd === target.value.length || target.selectionStart === target.selectionEnd) {
                const next = document.getElementById("shot-input-" + (index + 1));
                if (next) { e.preventDefault(); next.focus(); next.select(); }
            }
        } else if (e.key === "ArrowLeft") {
            const target = e.target;
            if (target.selectionStart === 0 && target.selectionEnd === 0) {
                const prev = document.getElementById("shot-input-" + (index - 1));
                if (prev) { e.preventDefault(); prev.focus(); prev.select(); }
            }
        } else if (e.key === "Tab") {
            if (!e.shiftKey) {
                const next = document.getElementById("shot-input-" + (index + 1));
                if (next) { e.preventDefault(); next.focus(); next.select(); }
            } else {
                const prev = document.getElementById("shot-input-" + (index - 1));
                if (prev) { e.preventDefault(); prev.focus(); prev.select(); }
            }
        }
    };
    const handleIndividualScoreChange = (index, value) => { const newScores = [...individualScores]; newScores[index].score = value; setIndividualScores(newScores); };
    const handleIndividualDirectionChange = (index, value) => { const newScores = [...individualScores]; newScores[index].direction = newScores[index].direction === value ? '' : value; setIndividualScores(newScores); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (captureMode === 'event' && !selectedStichId) { showMessage(t('pleaseSelectStich', 'Bitte wählen'), 'error'); return; }

        setLoading(true);
        const isTemplate = captureMode === 'template' || captureMode === 'event';
        let templateIdForSubmit = captureMode === 'template' ? selectedTemplateId : null;
        let eventStichIdForSubmit = null;

        if (captureMode === 'event') {
            const stich = eventStiche.find(s => s.id == selectedStichId);
            if (stich) { eventStichIdForSubmit = stich.id; templateIdForSubmit = stich.template_id; }
        }

        let finalShotCount = isTemplate && currentTemplate && currentTemplate.type !== 'kombination' && currentTemplate.type !== 'anmeldung' ? individualScores.length : parseInt(shotCount, 10);
        let finalTargetType = isTemplate && currentTemplate ? currentTemplate.target_value : parseInt(targetType, 10);
        
        let individualScoresToSave = individualScores;
        let finalTotalScore = parseFloat(totalScore) || 0;
        let finalEstimatedScore = null;
        let finalActualScore = null;

        if (currentTemplate?.type === 'kombination') {
            individualScoresToSave = kombinationDetails;
        } else if (currentTemplate?.type === 'anmeldung') {
            individualScoresToSave = [{ companion: isCompanion, contact: contactInfo }];
            finalTotalScore = isCompanion ? 2 : 1;
            finalShotCount = 0;
            finalTargetType = 0;
        } else if (currentTemplate?.type === 'differenzler') {
            finalActualScore = individualScores.reduce((sum, shot) => sum + (parseFloat(shot.score) || 0), 0);
            finalEstimatedScore = parseFloat(estimatedScore) || 0;
            finalTotalScore = Math.abs(finalActualScore - finalEstimatedScore);
        } else if (isTemplate && currentTemplate?.type === 'best_of') {
            const validScores = individualScores.map(s => parseFloat(s.score)).filter(s => !isNaN(s)).sort((a, b) => b - a);
            const bestOfCount = currentTemplate.best_of_count || currentTemplate.default_shots || 10;
            finalTotalScore = validScores.slice(0, bestOfCount).reduce((a, b) => a + b, 0);
        } else if (isTemplate) {
            finalTotalScore = individualScores.reduce((sum, shot) => sum + (parseFloat(shot.score) || 0), 0);
        }

        const receiptData = { 
            participantName, 
            teamName: currentTemplate?.type === 'gruppen_stich' ? (teamName?.trim() || null) : null, 
            totalScore: finalTotalScore, 
            shotCount: finalShotCount, 
            targetType: finalTargetType, 
            individualScores: individualScoresToSave, 
            receiptId, 
            groupId: selectedGroupId || null, 
            folderId: selectedFolderId || null, 
            templateId: templateIdForSubmit, 
            eventStichId: eventStichIdForSubmit, 
            yearOfBirth, 
            manualRankModifier: parseInt(manualRankModifier) || 0,
            estimatedScore: finalEstimatedScore,
            actualScore: finalActualScore
        };

        try {
            const url = editingReceipt ? `${API_BASE_URL}/receipts/${editingReceipt.id}` : `${API_BASE_URL}/receipts`;
            const method = editingReceipt ? 'PUT' : 'POST';
            const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` }, body: JSON.stringify(receiptData) });
            if (response.ok) {
                showMessage(editingReceipt ? t('receiptUpdatedSuccess', 'Erfolgreich aktualisiert') : t('saveReceiptSuccess', 'Erfolgreich gespeichert'), 'success');
                if(editingReceipt) onCancelEdit();
                else {
                    // WICHTIG: TeilnehmerName bleibt erhalten!
                    setReceiptId(''); setIsCompanion(false); setContactInfo(''); setEstimatedScore(''); setManualRankModifier(0);
                    if (currentTemplate && currentTemplate.type !== 'kombination' && currentTemplate.type !== 'anmeldung') {
                        setIndividualScores(Array.from({ length: currentTemplate.default_shots }, () => ({ score: '', direction: '' })));
                        setTotalScore('0');
                    }
                }
                onDataChange();
            } else { const data = await response.json().catch(()=>({})); showMessage(data.message || t('addReceiptError', 'Fehler beim Speichern'), 'error'); }
        } catch (error) { showMessage(error.message, 'error'); } finally { setLoading(false); }
    };

    const groupFolders = useMemo(() => {
        if (!folders) return [];
        const tree = []; const map = {};
        const filteredFolders = folders.filter(f => f.group_id === parseInt(selectedGroupId));
        filteredFolders.forEach(folder => { map[folder.id] = { ...folder, children: [] }; });
        filteredFolders.forEach(folder => { if (folder.parent_id && map[folder.parent_id]) { map[folder.parent_id].children.push(map[folder.id]); } else { tree.push(map[folder.id]); } });
        return tree;
    }, [folders, selectedGroupId]);
    
    const renderFolderOptions = (nodes, level = 0) => nodes.flatMap(node => [ <option key={node.id} value={node.id}> {'\u00A0\u00A0'.repeat(level)} {node.name} </option>, ...renderFolderOptions(node.children || [], level + 1) ]);

    return ( 
        <div className="p-2 sm:p-4 md:p-8 relative z-10 animate-fade-in max-w-6xl 2xl:max-w-[1600px] mx-auto w-full"> 
            <div className="flow-card p-6 md:p-10 w-full mb-8 relative shadow-2xl">
                {editingReceipt && (
                    <div className="absolute top-6 right-6">
                        <button type="button" onClick={onCancelEdit} className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40 px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-1.5 transition-all">
                            <Icons.Close/> {t('cancelEdit', 'Abbrechen')}
                        </button>
                    </div>
                )}
                
                <div className="flex flex-wrap justify-between items-center gap-4 mb-8 pb-4 border-b border-[#4f5f68]/30">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5 font-mono text-xs tracking-mono uppercase text-[#3a7d8a]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#d97757]"></span>
                            <span>{editingReceipt ? t('editReceiptTitle', 'BEARBEITUNG') : t('captureAndScan', 'ERFASSUNG & SCAN')}</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-display font-bold text-[#f3efe6]">{editingReceipt ? t('editReceipt', 'Beleg bearbeiten') : t('capture', 'Resultat erfassen')}</h2>
                    </div>

                    {!editingReceipt && (
                        <label className={`font-bold py-2.5 px-5 rounded-xl shadow-lg flex items-center gap-2.5 transition-all cursor-pointer ${
                            scanning 
                                ? 'bg-[#3a7d8a]/80 text-white cursor-wait opacity-90 shadow-inner' 
                                : 'flow-btn-coral'
                        }`}>
                            {scanning ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                    </svg>
                                    <span>{t('scanning', 'Wird gescannt...')}</span>
                                </>
                            ) : (
                                <>
                                    <span className="text-xl">📷</span>
                                    <span>{t('scanPhoto', 'Foto scannen')}</span>
                                </>
                            )}
                            <input 
                                type="file" 
                                accept="image/*" 
                                capture="environment" 
                                className="hidden" 
                                onChange={handleScanImage} 
                                disabled={scanning || loading} 
                            />
                        </label>
                    )}
                </div>
                
                {/* Mode Selector */}
                <div className="flex justify-center mb-8 bg-[#121c21] rounded-xl p-1.5 border border-[#4f5f68]/35 max-w-xl mx-auto">
                    <button 
                        type="button" 
                        onClick={() => setCaptureMode('template')} 
                        className={`w-1/3 py-2 px-3 rounded-lg text-sm md:text-base font-semibold transition-all ${
                            captureMode === 'template' 
                                ? 'bg-[#d97757] text-[#f3efe6] shadow-lg shadow-[#d97757]/20' 
                                : 'text-[#f3efe6]/70 hover:text-[#f3efe6] hover:bg-[#1a262c]'
                        }`}
                    >
                        {t('template', 'Vorlage')}
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setCaptureMode('event')} 
                        className={`w-1/3 py-2 px-3 rounded-lg text-sm md:text-base font-semibold transition-all ${
                            captureMode === 'event' 
                                ? 'bg-[#3a7d8a] text-[#f3efe6] shadow-lg shadow-[#3a7d8a]/20' 
                                : 'text-[#f3efe6]/70 hover:text-[#f3efe6] hover:bg-[#1a262c]'
                        }`}
                    >
                        {t('eventMode', 'Event')}
                    </button>
                    <button 
                        type="button" 
                        onClick={() => setCaptureMode('individual')} 
                        className={`w-1/3 py-2 px-3 rounded-lg text-sm md:text-base font-semibold transition-all ${
                            captureMode === 'individual' 
                                ? 'bg-[#4f5f68] text-[#f3efe6] shadow-md' 
                                : 'text-[#f3efe6]/70 hover:text-[#f3efe6] hover:bg-[#1a262c]'
                        }`}
                    >
                        {t('individual', 'Individuell')}
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('selectGroup', 'Gruppe auswählen')}</label>
                            <select value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)} className="flow-input w-full">
                                <option value="">{t('selectGroup', 'Gruppe auswählen')}</option>
                                {(userGroups || []).filter(g => g.can_capture || g.created_by === user.id).map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
                            </select>
                        </div>
                        {captureMode !== 'event' && selectedGroupId && (
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('selectFolder', 'Ordner auswählen')}</label>
                            <select value={selectedFolderId} onChange={(e) => setSelectedFolderId(e.target.value)} className="flow-input w-full">
                                <option value="">{t('selectFolder', 'Ordner auswählen')}</option>
                                {renderFolderOptions(groupFolders)}
                            </select>
                        </div>
                        )}
                    </div>

                    {currentTemplate?.type === 'gruppen_stich' && (
                        <div className="bg-[#121c21]/90 p-5 rounded-xl border border-[#3a7d8a]/40 shadow-sm">
                            <div className="flex justify-between items-center mb-1.5">
                                <label className="block text-xs font-mono uppercase tracking-wider text-[#3a7d8a]">
                                    {t('teamName', 'Gruppe / Teamname')} <span className="text-[#a4b3bc] font-normal font-sans">(optional)</span>
                                </label>
                                {teamName && (
                                    <button 
                                        type="button" 
                                        onClick={() => setTeamName('')} 
                                        className="text-xs text-[#a4b3bc] hover:text-[#f3efe6] font-mono underline cursor-pointer"
                                    >
                                        Keine Gruppe zuweisen
                                    </button>
                                )}
                            </div>
                            <input 
                                type="text" 
                                list="existing-team-names" 
                                value={teamName} 
                                onChange={e => setTeamName(e.target.value)} 
                                placeholder="Gruppe / Team (leer lassen für 'Keine Gruppe')" 
                                className="flow-input w-full" 
                            />
                            <p className="text-[11px] font-mono text-[#a4b3bc] mt-1.5">
                                ℹ️ Schützen ohne Gruppenangabe werden automatisch zuunterst am letzten Platz in der Gruppe „Keine Gruppe“ angezeigt.
                            </p>
                            <datalist id="existing-team-names">
                                {existingTeamNames.map((name, index) => <option key={index} value={name} />)}
                            </datalist>
                        </div>
                    )}

                    {captureMode === 'event' && selectedGroupId && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-5 bg-[#121c21]/90 rounded-xl border border-[#3a7d8a]/30">
                            <div className="flex flex-col w-full">
                                <label className="block text-xs font-mono uppercase tracking-wider text-[#3a7d8a] mb-1.5">Event</label>
                                <select value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)} className="flow-input w-full">
                                    <option value="">{t('pleaseSelect', 'Bitte wählen')}</option>
                                    {groupEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                                </select>
                            </div>
                            {selectedEventId && (
                                 <div className="flex flex-col w-full">
                                     <label className="block text-xs font-mono uppercase tracking-wider text-[#3a7d8a] mb-1.5">{t('stich', 'Stich')}</label>
                                     <select value={selectedStichId} onChange={e => setSelectedStichId(e.target.value)} className="flow-input w-full">
                                         <option value="">{t('pleaseSelect', 'Bitte wählen')}</option>
                                         {eventStiche.map(s => <option key={s.id} value={s.id}>{s.template_name} (CHF {Number(s.price).toFixed(2)})</option>)}
                                     </select>
                                 </div>
                             )}
                        </div>
                    )}
            {captureMode === 'event' && selectedEventId && (
                <div className="mb-6 p-5 border border-[#4f5f68]/35 rounded-xl bg-[#121c21]/90 shadow-sm">
                    <label className="block text-xs font-mono uppercase tracking-wider text-[#3a7d8a] mb-1.5">{t('selectParticipantLabel', 'Teilnehmer (Aus Kasse / Standblatt)')}</label>
                    <select className="flow-input w-full mb-2" value={selectedParticipantId} onChange={handleParticipantChange}>
                        <option value="">{t('selectParticipantDropdown', 'Manuelle Eingabe / Bitte wählen')}</option>
                        {eventParticipants.map(p => <option key={p.id} value={p.id}>{p.lastname} {p.firstname} ({p.year_of_birth}){p.notes ? ` 📝 (${p.notes.substring(0, 25)}...)` : ''}</option>)}
                    </select>
                    {(() => {
                        const selP = eventParticipants.find(x => x.id == selectedParticipantId);
                        if (selP?.notes) {
                            return (
                                <div className="mt-2 text-xs bg-[#d97757]/15 text-[#f3efe6] border border-[#d97757]/40 rounded-lg p-2.5 flex items-start gap-2 shadow-xs">
                                    <span className="font-bold text-[#d97757] whitespace-nowrap">📝 Notiz:</span>
                                    <span className="whitespace-pre-wrap">{selP.notes}</span>
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            )}

            {captureMode === 'template' && (
                 <div className="grid grid-cols-1 gap-6 items-center">
                    <div className="flex flex-col w-full">
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('template', 'Vorlage')}</label>
                        <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} className="flow-input w-full">
                            <optgroup label={t('templates', 'Vorlagen')}>{dbTemplates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}</optgroup>
                        </select>
                    </div>
                </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                    <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Name</label>
                    <input type="text" value={participantName} onChange={(e) => setParticipantName(e.target.value)} placeholder="Vorname Name" required className="flow-input w-full"/>
                </div>
                <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Jahrgang</label>
                    <input type="text" value={yearOfBirth} onChange={(e) => setYearOfBirth(e.target.value)} placeholder="YYYY" className="flow-input w-full font-mono"/>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Quittungs ID</label>
                    <input type="text" value={receiptId} onChange={(e) => setReceiptId(e.target.value)} placeholder="z.B. 00123" className="flow-input w-full font-mono"/>
                </div>
            </div>

            {currentTemplate?.type === 'anmeldung' && (
                <div className="bg-[#121c21]/90 p-6 rounded-xl border border-[#3a7d8a]/40 col-span-full shadow-sm">
                    <h4 className="font-bold text-[#3a7d8a] text-lg mb-4 font-sans">Administrativ: Anmeldung</h4>
                    <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer p-3.5 bg-[#1a262c] rounded-xl border border-[#4f5f68]/40 shadow-sm hover:bg-[#21323a] transition-all">
                            <input type="checkbox" checked={isCompanion} onChange={(e) => setIsCompanion(e.target.checked)} className="w-5 h-5 text-[#3a7d8a] rounded cursor-pointer accent-[#3a7d8a]" />
                            <span className="font-bold text-[#f3efe6]">Kommt mit Begleitung (+1 Person)</span>
                        </label>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Kontaktinformationen (Tel. / E-Mail)</label>
                            <input type="text" value={contactInfo} onChange={(e) => setContactInfo(e.target.value)} placeholder="079 123 45 67 oder max@muster.ch" className="flow-input w-full" />
                        </div>
                    </div>
                </div>
            )}

            {currentTemplate?.type === 'kombination' && (
                <div className="bg-[#121c21]/90 p-6 rounded-xl border border-[#3a7d8a]/40 col-span-full shadow-sm">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 border-b border-[#4f5f68]/30 pb-3 gap-4">
                        <h4 className="font-bold text-[#3a7d8a] text-lg font-sans">Automatische Berechnung</h4>
                        {captureMode === 'event' && eventParticipants.length > 0 && <button type="button" onClick={handleCalculateAll} disabled={loading} className="flow-btn-coral text-sm py-2 px-4 shadow-md font-bold transition">🏆 Alle {eventParticipants.length} Schützen berechnen</button>}
                    </div>
                    {participantName ? (
                        <div className="space-y-2 mt-2">
                            {kombinationDetails.map((det, idx) => (
                                <div key={idx} className="flex justify-between border-b border-[#4f5f68]/20 pb-2 text-sm">
                                    <span className="text-[#f3efe6]">{det.name} <span className="text-[#a4b3bc] text-xs">(Best: {det.bestScore})</span></span>
                                    <span className="font-mono text-[#a4b3bc]">x {det.percentage}% = <b className="text-[#d97757] font-mono">{Number(det.weighted).toFixed(1).replace(/\.0$/, '')}</b></span>
                                </div>
                            ))}
                            <div className="flex justify-between font-bold text-xl pt-3 text-[#f3efe6]"><span>Total berechnet:</span><span className="text-[#d97757] font-mono text-2xl">{totalScore}</span></div>
                            <button type="button" onClick={loadParticipantScores} className="w-full mt-3 flow-btn-ghost text-xs py-2">Neu berechnen</button>
                        </div>
                    ) : <p className="text-sm italic text-[#a4b3bc]">Bitte gib den Teilnehmernamen ein.</p>}
                </div>
            )}

            {currentTemplate?.type === 'differenzler' && (
                <div className="bg-[#121c21]/90 p-6 rounded-xl border border-[#d97757]/50 col-span-full shadow-lg mb-4">
                    <div className="flex items-center gap-3 mb-4 border-b border-[#4f5f68]/30 pb-3">
                        <span className="text-2xl">🎯</span>
                        <div>
                            <h4 className="font-bold text-[#f3efe6] text-lg font-sans">Differenzler: Schätzung vor dem Schiessen</h4>
                            <p className="text-xs text-[#a4b3bc]">Der Schütze sagt vor dem Schiessen seine Punktzahl an. Die Differenz zum Resultat wird automatisch berechnet.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#d97757] mb-1.5">
                                Angesagte Punktzahl (Schätzung) *
                            </label>
                            <input 
                                type="number" 
                                step="any"
                                required 
                                value={estimatedScore} 
                                onChange={e => setEstimatedScore(e.target.value)} 
                                onWheel={(e) => e.target.blur()}
                                placeholder="z.B. 92" 
                                className="flow-input w-full font-mono font-black text-2xl text-[#d97757] text-center border-[#d97757]/60" 
                            />
                        </div>
                        <div className="md:col-span-2 bg-[#1a262c] p-4 rounded-xl border border-[#4f5f68]/40 shadow-sm flex flex-wrap justify-around items-center gap-4 text-center">
                            <div>
                                <span className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc]">Angesagt</span>
                                <span className="text-2xl font-mono font-bold text-[#d97757]">{estimatedScore !== '' ? Number(estimatedScore) : '-'}</span>
                            </div>
                            <div className="text-2xl font-bold text-[#4f5f68]">-</div>
                            <div>
                                <span className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc]">Geschossen</span>
                                <span className="text-2xl font-mono font-bold text-[#3a7d8a]">
                                    {individualScores.reduce((sum, s) => sum + (parseFloat(s.score) || 0), 0).toFixed(1).replace(/\.0$/, '')}
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-[#4f5f68]">=</div>
                            <div className="bg-[#121c21] px-5 py-2.5 rounded-xl border border-[#d97757]/50 shadow-inner">
                                <span className="block text-[11px] font-mono uppercase tracking-wider text-[#d97757]">Differenz (Punkte)</span>
                                <span className="text-3xl font-mono font-black text-[#f3efe6]">
                                    {estimatedScore !== '' && !isNaN(parseFloat(estimatedScore))
                                        ? Math.abs(individualScores.reduce((sum, s) => sum + (parseFloat(s.score) || 0), 0) - parseFloat(estimatedScore)).toFixed(1).replace(/\.0$/, '')
                                        : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {((captureMode === 'template' || captureMode === 'event') && currentTemplate?.type !== 'anmeldung') ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{currentTemplate?.type === 'differenzler' ? 'Differenz (Berechnetes Total)' : t('totalScore', 'Total')}</label>
                        <input type="number" step="0.1" value={totalScore} onChange={(e) => setTotalScore(e.target.value)} onWheel={(e) => e.target.blur()} placeholder={currentTemplate?.type === 'differenzler' ? 'Differenz' : 'Total'} className="flow-input w-full font-mono font-extrabold text-2xl text-[#d97757]" readOnly={['standard', 'zick_zack', 'kombination', 'best_of', 'differenzler'].includes(currentTemplate?.type)} />
                    </div>
                    {currentTemplate?.type === 'best_of' && (
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Geschossene Schüsse</label>
                            <input type="number" value={shotCount} onChange={(e) => { const val = e.target.value; setShotCount(val); const num = parseInt(val, 10); if (!isNaN(num) && num >= 0) { setIndividualScores(prev => { const arr = [...prev]; while(arr.length < num) arr.push({ score: '', direction: '' }); return arr.slice(0, num); }); } }} onWheel={(e) => e.target.blur()} className="flow-input w-full font-mono" />
                        </div>
                    )}
                </div>
            ) : captureMode === 'individual' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Total</label><input type="number" step="0.1" value={totalScore} onChange={(e) => setTotalScore(e.target.value)} onWheel={(e) => e.target.blur()} required className="flow-input w-full font-mono text-xl text-[#d97757] font-bold"/></div>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Schüsse</label><input type="number" value={shotCount} onChange={(e) => setShotCount(e.target.value)} onWheel={(e) => e.target.blur()} required className="flow-input w-full font-mono"/></div>
                    <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Wertung</label><input type="number" value={targetType} onChange={(e) => setTargetType(e.target.value)} onWheel={(e) => e.target.blur()} required className="flow-input w-full font-mono"/></div>
                </div>
            ) : null}
            
            {(((captureMode === 'template' || captureMode === 'event') && currentTemplate && currentTemplate.type !== 'kombination' && currentTemplate.type !== 'anmeldung') || captureMode === 'individual') ? (
            <div>
                <div className="flex flex-wrap justify-between items-center mb-4 border-b border-[#4f5f68]/30 pb-2 gap-2">
                    <div className="flex items-center gap-2">
                        <label className="block text-xs font-mono uppercase tracking-wider text-[#3a7d8a]">Einzelschüsse & Trefferlage</label>
                        {bestOfLiveInfo && (
                            <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded font-mono font-bold">
                                Best-Of: Top {bestOfLiveInfo.count} = {bestOfLiveInfo.total.toFixed(1).replace(/\.0$/, '')} Pkt
                                {bestOfLiveInfo.tieBreakerScore !== null ? ` (11. Schuss: ${bestOfLiveInfo.tieBreakerScore})` : ''}
                            </span>
                        )}
                    </div>
                    {currentTemplate?.type === 'best_of' && <button type="button" onClick={() => setIndividualScores([...individualScores, { score: '', direction: '' }])} className="flow-btn-tide text-xs py-1.5 px-3 rounded-lg font-bold flex items-center gap-1 cursor-pointer"><Icons.Add/> Schuss hinz.</button>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-10 gap-3.5">
                    {individualScores.map((shot, index) => {
                        const isTop = bestOfLiveInfo?.topIndices.has(index);
                        const isTieBreaker = bestOfLiveInfo?.tieBreakerIdx === index;
                        let cardClass = "border-[#4f5f68]/35 bg-[#121c21]/90";
                        let badge = null;
                        if (bestOfLiveInfo) {
                            if (isTop) {
                                cardClass = "border-emerald-500/80 bg-emerald-500/10 shadow-md shadow-emerald-500/10";
                                badge = <span className="text-[9px] bg-emerald-500/30 text-emerald-300 px-1 py-0.5 rounded font-mono font-bold">★ Top {bestOfLiveInfo.count}</span>;
                            } else if (isTieBreaker) {
                                cardClass = "border-amber-400 bg-amber-500/20 shadow-md shadow-amber-400/20 ring-1 ring-amber-400/50";
                                badge = <span className="text-[9px] bg-amber-400 text-black px-1.5 py-0.5 rounded font-mono font-extrabold">⚡ 11. Schuss</span>;
                            } else if (shot.score !== '') {
                                cardClass = "border-[#4f5f68]/20 bg-[#121c21]/50 opacity-60";
                                badge = <span className="text-[9px] text-[#a4b3bc] font-mono">Streichung</span>;
                            }
                        }
                        return (
                            <div key={index} className={`flex flex-col space-y-2 p-3 border rounded-xl shadow-md transition-all ${cardClass}`}>
                                <div className="flex justify-between items-center text-xs font-mono uppercase tracking-wider text-[#a4b3bc] border-b border-current/20 pb-1">
                                    <span>Schuss {index + 1}</span>
                                    {badge}
                                </div>
                                <input 
                                    id={`shot-input-${index}`}
                                    type="number" 
                                    step="0.1" 
                                    className={`block w-full px-2 py-1.5 bg-[#1a262c] border rounded-lg text-center font-mono font-bold text-lg outline-none transition-all ${
                                        isTop ? 'border-emerald-500/60 text-emerald-300' : isTieBreaker ? 'border-amber-400 text-amber-300 font-extrabold' : 'border-[#4f5f68]/40 text-[#f3efe6] focus:border-[#d97757]'
                                    }`} 
                                    value={shot.score} 
                                    onChange={(e) => handleIndividualScoreChange(index, e.target.value)} 
                                    onWheel={(e) => e.target.blur()}
                                    onKeyDown={(e) => handleShotKeyDown(e, index)}
                                    placeholder="0"
                                />
                                <div className="grid grid-cols-3 gap-1 mt-1">
                                    {directionOrder.map((key) => ( <button key={key} type="button" tabIndex={-1} className={`w-full py-1 rounded-md text-base font-bold transition-all ${shot.direction === key ? 'bg-[#3a7d8a] text-white shadow-md' : 'bg-[#1a262c] hover:bg-[#25353e] text-[#a4b3bc]'}`} onClick={() => handleIndividualDirectionChange(index, key)}>{directionMap[key]}</button> ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            ) : null}

            <button type="submit" disabled={loading} className={`w-full font-bold py-3.5 px-6 rounded-xl transition-all duration-200 text-lg cursor-pointer ${editingReceipt ? 'flow-btn-coral' : 'flow-btn-coral'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                {loading ? 'Wird gespeichert...' : (editingReceipt ? 'Aktualisierung speichern' : 'Resultat speichern')}
            </button>
        </form>
    </div> 
</div> 
);
};

const TieBreakerModal = ({ isOpen, onClose, tiedGroup, user, showMessage, onDataChange, t }) => {
    const [items, setItems] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (tiedGroup && Array.isArray(tiedGroup.receipts)) {
            setItems([...tiedGroup.receipts]);
        } else {
            setItems([]);
        }
    }, [tiedGroup]);

    if (!isOpen || !tiedGroup || items.length === 0) return null;

    const moveItem = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= items.length) return;
        const copy = [...items];
        const temp = copy[index];
        copy[index] = copy[newIndex];
        copy[newIndex] = temp;
        setItems(copy);
    };

    const handleResetAuto = async () => {
        setSaving(true);
        try {
            const updates = items.map(it => ({ id: it.id, manualRankModifier: 0 }));
            const res = await fetch(`${API_BASE_URL}/receipts/tie_breakers/batch`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: JSON.stringify({ updates })
            });
            if (res.ok) {
                showMessage(t('tieResetSuccess', 'Reihenfolge auf automatisch zurückgesetzt.'), 'success');
                onDataChange('shots');
                onClose();
            } else {
                showMessage(t('tieResetError', 'Fehler beim Zurücksetzen.'), 'error');
            }
        } catch (e) {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveOrder = async () => {
        setSaving(true);
        try {
            const updates = items.map((it, idx) => ({
                id: it.id,
                manualRankModifier: (items.length - idx) * 10
            }));
            const res = await fetch(`${API_BASE_URL}/receipts/tie_breakers/batch`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: JSON.stringify({ updates })
            });
            if (res.ok) {
                showMessage(t('tieSavedSuccess', 'Gleichstand-Reihenfolge erfolgreich gespeichert!'), 'success');
                onDataChange('manual');
                onClose();
            } else {
                showMessage(t('tieSavedError', 'Fehler beim Speichern der Reihenfolge.'), 'error');
            }
        } catch (e) {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-[999999] animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 max-w-xl w-full shadow-2xl animate-scale-in">
                <div className="flex justify-between items-start mb-5 border-b border-[#4f5f68]/30 pb-4">
                    <div>
                        <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">⚖️ {t('manualTieBreaker', 'Gleichstand manuell festlegen')}</h3>
                        <p className="text-xs font-mono text-[#a4b3bc] mt-1">
                            {items[0]?.template_type === 'differenzler' ? (
                                <>Differenz: <span className="font-bold text-[#d97757]">{Number(tiedGroup.score).toFixed(1).replace(/\.0$/, '')} Punkte</span></>
                            ) : (
                                <>Punktzahl: <span className="font-bold text-[#8ec8d4]">{Number(tiedGroup.score).toFixed(2).replace(/\.00$/, '')} Punkte</span></>
                            )} ({items.length} Teilnehmer)
                        </p>
                    </div>
                    <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition"><Icons.Close /></button>
                </div>

                <p className="text-xs text-[#a4b3bc] mb-5 bg-[#131d22] p-3.5 rounded-xl border border-[#4f5f68]/30 font-mono">
                    💡 <strong>Manuelle Rangierung:</strong> Bringe die Schützen mit <span className="text-[#8ec8d4] font-bold">▲ Hoch</span> und <span className="text-[#8ec8d4] font-bold">▼ Runter</span> in deine gewünschte Reihenfolge und speichere ab.
                </p>

                <div className="space-y-2.5 max-h-[50vh] overflow-y-auto mb-6 pr-1 custom-scrollbar">
                    {items.map((it, idx) => (
                        <div key={it.id} className="flex items-center justify-between p-3.5 bg-[#131d22] border border-[#4f5f68]/30 rounded-xl hover:bg-[#1f303a]/50 transition">
                            <div className="flex items-center gap-3">
                                <span className="w-7 h-7 rounded-full bg-[#3a7d8a]/20 border border-[#3a7d8a]/40 text-[#8ec8d4] font-mono font-bold flex items-center justify-center text-xs shadow-xs">
                                    {idx + 1}
                                </span>
                                <div>
                                    <div className="font-bold text-[#f3efe6] text-base">{it.participant_name}</div>
                                    <div className="text-xs text-[#a4b3bc] font-mono flex flex-wrap gap-2 mt-0.5">
                                        {it.year_of_birth && <span className="bg-[#4f5f68]/20 text-[#f3efe6] px-1.5 py-0.5 rounded border border-[#4f5f68]/30">Jg: {it.year_of_birth}</span>}
                                        {it.template_type === 'differenzler' && <span className="bg-[#3a7d8a]/20 text-[#8ec8d4] px-1.5 py-0.5 rounded border border-[#3a7d8a]/40 font-semibold">Ansage: {it.estimated_score ?? '-'} | Ist: {it.actual_score ?? '-'}</span>}
                                        {it._tiefInfo && <span className="text-[#8ec8d4] font-semibold">{it._tiefInfo}</span>}
                                        {(it.folder_name || it.stich_name) && <span className="text-[#a4b3bc]">({it.folder_name || it.stich_name})</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    disabled={idx === 0}
                                    onClick={() => moveItem(idx, 'up')}
                                    className={`px-3 py-1.5 rounded-lg font-bold transition text-xs font-mono cursor-pointer ${idx === 0 ? 'text-[#4f5f68] bg-[#162127] cursor-not-allowed border border-[#4f5f68]/20' : 'bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40'}`}
                                    title="Nach oben"
                                >
                                    ▲
                                </button>
                                <button
                                    type="button"
                                    disabled={idx === items.length - 1}
                                    onClick={() => moveItem(idx, 'down')}
                                    className={`px-3 py-1.5 rounded-lg font-bold transition text-xs font-mono cursor-pointer ${idx === items.length - 1 ? 'text-[#4f5f68] bg-[#162127] cursor-not-allowed border border-[#4f5f68]/20' : 'bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40'}`}
                                    title="Nach unten"
                                >
                                    ▼
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-[#4f5f68]/30">
                    <button
                        type="button"
                        onClick={handleResetAuto}
                        disabled={saving}
                        className="text-xs text-[#a4b3bc] hover:text-[#d97757] underline font-mono order-2 sm:order-1 transition cursor-pointer"
                    >
                        {items[0]?.template_type === 'differenzler' ? '🔄 Nach Differenzler-Regel zurücksetzen' : '🔄 Nach Tiefschuss zurücksetzen'}
                    </button>
                    <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flow-btn-ghost flex-1 sm:flex-none text-xs md:text-sm py-2 px-4 cursor-pointer"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveOrder}
                            disabled={saving}
                            className="flow-btn-coral flex-1 sm:flex-none text-xs md:text-sm py-2 px-5 shadow-lg shadow-[#d97757]/20 cursor-pointer"
                        >
                            {saving ? 'Speichert...' : '💾 Reihenfolge speichern'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ScoreboardPrintModal = ({ isOpen, onClose, printSettings, setPrintSettings, onPrint, hasGruppenStich }) => {
    const { t } = useTranslation();
    if(!isOpen) return null;
    return createPortal(
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-[9999999] animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-scale-in">
                <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-3">
                    <h2 className="text-xl md:text-2xl font-bold text-[#f3efe6] tracking-tight">🖨️ {t('printPreviewTitle', 'Drucken / PDF Export')}</h2>
                    <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition"><Icons.Close /></button>
                </div>
                
                <div className="space-y-5">
                    <div>
                        <label className="font-mono text-xs uppercase tracking-wider text-[#a4b3bc] block mb-2">{t('printOrientation', 'Ausrichtung (Papierformat)')}</label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium"><input type="radio" name="orientation" checked={printSettings.orientation === 'portrait'} onChange={() => setPrintSettings({...printSettings, orientation: 'portrait'})} className="w-4 h-4 text-[#d97757] focus:ring-[#d97757] accent-[#d97757]"/> {t('portrait', 'Hochformat')}</label>
                            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium"><input type="radio" name="orientation" checked={printSettings.orientation === 'landscape'} onChange={() => setPrintSettings({...printSettings, orientation: 'landscape'})} className="w-4 h-4 text-[#d97757] focus:ring-[#d97757] accent-[#d97757]"/> {t('landscape', 'Querformat')}</label>
                        </div>
                    </div>
                    
                    <div>
                        <label className="font-mono text-xs uppercase tracking-wider text-[#a4b3bc] block mb-2">{t('tableFontSize', 'Schriftgrösse (Tabelle)')}</label>
                        <select value={printSettings.fontSize} onChange={e => setPrintSettings({...printSettings, fontSize: e.target.value})} className="flow-input w-full text-sm">
                            <option value="text-xs">{t('fontSmall', 'Klein (Platzsparend)')}</option>
                            <option value="text-sm">{t('fontNormal', 'Normal')}</option>
                            <option value="text-base">{t('fontLarge', 'Gross (Gut lesbar)')}</option>
                            <option value="text-lg">{t('fontExtraLarge', 'Sehr Gross')}</option>
                        </select>
                    </div>

                    <div>
                        <label className="font-mono text-xs uppercase tracking-wider text-[#a4b3bc] block mb-2">{t('whichColumnsToShow', 'Welche Spalten anzeigen?')}</label>
                        <div className="grid grid-cols-2 gap-2.5">
                            {Object.keys(printSettings.showColumns).map(col => (
                                <label key={col} className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" checked={printSettings.showColumns[col]} onChange={e => setPrintSettings({...printSettings, showColumns: {...printSettings.showColumns, [col]: e.target.checked}})} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757] accent-[#d97757]"/> {col === 'rank' ? t('rank', 'Rang') : col === 'name' ? t('participant', 'Name') : col === 'score' ? t('totalScore', 'Resultat') : col === 'stich' ? t('stich', 'Stich/Abzeichen') : col === 'group' ? t('groupNameColumn', 'Gruppe') : t('captureDate', 'Datum')}</label>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer p-3.5 bg-[#131d22] border border-[#4f5f68]/30 rounded-xl hover:border-[#4f5f68]/60 transition">
                            <input type="checkbox" checked={printSettings.showDetails} onChange={e => setPrintSettings({...printSettings, showDetails: e.target.checked})} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757] accent-[#d97757]"/>
                            <span className="font-medium text-sm text-[#f3efe6]">{t('printSingleShots', 'Details (Einzelschüsse) andrucken')}</span>
                        </label>

                        {hasGruppenStich && (
                            <div className="p-4 bg-[#131d22] border border-[#3a7d8a]/40 rounded-xl space-y-3 shadow-sm">
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-[#3a7d8a] block font-bold mb-2">
                                        👥 {t('groupShootScope', 'Gruppenschiessen: Welche Wertung drucken?')}
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setPrintSettings({ ...printSettings, groupScope: 'both' })}
                                            className={`py-2 px-3 rounded-lg text-xs font-mono font-semibold border transition cursor-pointer text-center ${(printSettings.groupScope || 'both') === 'both' ? 'bg-[#3a7d8a] text-[#162127] border-[#3a7d8a] font-bold shadow-xs' : 'bg-[#162127] text-[#a4b3bc] border-[#4f5f68]/40 hover:text-[#f3efe6]'}`}
                                        >
                                            {t('groupScopeBoth', 'Beides (Gruppe & Einzel)')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPrintSettings({ ...printSettings, groupScope: 'teams' })}
                                            className={`py-2 px-3 rounded-lg text-xs font-mono font-semibold border transition cursor-pointer text-center ${printSettings.groupScope === 'teams' ? 'bg-[#d97757] text-[#162127] border-[#d97757] font-bold shadow-xs' : 'bg-[#162127] text-[#a4b3bc] border-[#4f5f68]/40 hover:text-[#f3efe6]'}`}
                                        >
                                            {t('groupScopeTeams', 'Nur Gruppenresultate')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPrintSettings({ ...printSettings, groupScope: 'individuals' })}
                                            className={`py-2 px-3 rounded-lg text-xs font-mono font-semibold border transition cursor-pointer text-center ${printSettings.groupScope === 'individuals' ? 'bg-[#d97757] text-[#162127] border-[#d97757] font-bold shadow-xs' : 'bg-[#162127] text-[#a4b3bc] border-[#4f5f68]/40 hover:text-[#f3efe6]'}`}
                                        >
                                            {t('groupScopeIndividuals', 'Nur Einzelresultate')}
                                        </button>
                                    </div>
                                </div>

                                {printSettings.groupScope !== 'individuals' && (
                                    <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-[#4f5f68]/30">
                                        <input 
                                            type="checkbox" 
                                            checked={printSettings.showTeamMembers ?? true} 
                                            onChange={e => setPrintSettings({ ...printSettings, showTeamMembers: e.target.checked })} 
                                            className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757] accent-[#d97757]"
                                        />
                                        <span className="font-medium text-xs text-[#f3efe6]">
                                            👥 {t('printTeamMembers', 'Alle Schützen der Gruppe unter dem Gruppennamen auflisten')}
                                        </span>
                                    </label>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex gap-3 mt-8 pt-4 border-t border-[#4f5f68]/30">
                    <button onClick={onClose} className="flow-btn-ghost flex-1 py-2.5 text-sm cursor-pointer">{t('cancel', 'Abbrechen')}</button>
                    <button onClick={onPrint} className="flow-btn-coral flex-1 py-2.5 text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer"><Icons.Print/> {t('previewAndPrint', 'Vorschau & Drucken')}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const QRCodePrintFlyer = ({ targetUrl, qrDataUrl, groupName, eventName, onClose, t }) => {
    return createPortal(
        <div className="fixed inset-0 bg-[#10171a] print:bg-white text-black font-sans min-h-screen z-[99999999] overflow-y-auto">
            <style>{`
                @media print { 
                    nav, footer, .no-print, #root { display: none !important; } 
                    @page { size: A4 portrait; margin: 12mm; } 
                    body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
                    .print-flyer-container { position: static !important; background: white !important; overflow: visible !important; width: 100% !important; height: auto !important; }
                    * { overflow: visible !important; } 
                }
            `}</style>
            
            <div className="no-print bg-[#162127] border-b border-[#4f5f68]/40 p-4 text-[#f3efe6] flex justify-between items-center shadow-2xl sticky top-0 z-[100000000]">
                <div className="flex items-center gap-3">
                    <span className="text-[#d97757] font-mono text-sm uppercase tracking-wider font-bold">📄 {t('printFlyerTitle', 'Aushang Druckvorschau')}</span>
                    <span className="text-xs text-[#a4b3bc] font-mono">DIN A4 Hochformat</span>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => window.print()} 
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 px-5 rounded-xl text-sm shadow-lg shadow-emerald-600/30 flex items-center gap-2 cursor-pointer transition"
                    >
                        <Icons.Print /> {t('nowPrintOrSave', 'Jetzt Drucken')}
                    </button>
                    <button 
                        onClick={onClose} 
                        className="bg-[#21323a] hover:bg-[#2b414c] text-[#f3efe6] font-semibold py-2 px-4 rounded-xl text-sm border border-[#4f5f68]/40 cursor-pointer transition flex items-center gap-1.5"
                    >
                        <Icons.Close /> {t('close', 'Schliessen')}
                    </button>
                </div>
            </div>

            <div className="p-8 md:p-12 max-w-2xl mx-auto my-6 bg-white shadow-2xl print:shadow-none print:m-0 print:p-0 print:max-w-none text-center flex flex-col justify-between items-center min-h-[90vh] print:min-h-0 border-8 border-[#1a262c] print:border-4 print:border-black rounded-3xl print:rounded-none">
                <div className="w-full flex flex-col items-center pt-2">
                    <img src={logoWithNameURL} alt="Logo" className="h-16 md:h-20 mb-4 object-contain" />
                    
                    <div className="inline-block bg-[#1a262c] text-white print:bg-black print:text-white font-mono uppercase tracking-widest text-xs md:text-sm px-4 py-1.5 rounded-full font-bold mb-3">
                        {t('flyerLiveScoreboard', 'LIVE RANGLISTE')}
                    </div>

                    <h1 className="text-3xl md:text-5xl font-black text-black tracking-tight mb-2 uppercase">
                        {groupName || 'KKS Schiessen'}
                    </h1>

                    {eventName && (
                        <h2 className="text-xl md:text-3xl font-extrabold text-[#d97757] print:text-black mb-3 border-b-2 border-black pb-2 px-6">
                            {eventName}
                        </h2>
                    )}
                </div>

                <div className="my-5 p-4 bg-white border-4 border-black rounded-2xl shadow-xl print:shadow-none inline-block">
                    {qrDataUrl ? (
                        <img src={qrDataUrl} alt="QR Code" className="w-64 h-64 md:w-80 md:h-80 object-contain mx-auto" />
                    ) : (
                        <div className="w-64 h-64 flex items-center justify-center font-mono text-sm text-gray-500">Generiere QR-Code...</div>
                    )}
                </div>

                <div className="w-full max-w-lg px-4 mb-3">
                    <div className="text-base md:text-xl font-bold text-black mb-2 leading-snug">
                        📱 Scanne den QR-Code mit der Handykamera
                    </div>
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed mb-4">
                        {t('flyerInstruction', 'Scanne diesen QR-Code mit deiner Smartphone-Kamera, um die aktuellen Resultate & Zwischenstände live mitzuverfolgen!')}
                    </p>
                    
                    <div className="bg-gray-100 border border-gray-300 rounded-xl p-2.5 font-mono text-xs md:text-sm text-black break-all select-all font-semibold">
                        {targetUrl}
                    </div>
                </div>

                <div className="w-full border-t border-gray-300 pt-3 pb-1 text-[11px] font-mono text-gray-500 flex justify-between items-center px-4">
                    <span>KKS Schützenportal</span>
                    <span>{t('flyerAutoRefresh', 'Aktualisiert sich automatisch alle 15 Sekunden • Viel Erfolg & Gut Schuss!')}</span>
                </div>
            </div>
        </div>,
        document.body
    );
};

const QRCodeModal = ({ isOpen, onClose, targetData, t }) => {
    const [scope, setScope] = useState('event');
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [flyerPrintActive, setFlyerPrintActive] = useState(false);

    const hasSpecificScope = Boolean(targetData?.folderId || targetData?.eventId);

    useEffect(() => {
        if (!hasSpecificScope) setScope('group');
        else setScope('event');
    }, [hasSpecificScope, targetData]);

    const targetUrl = useMemo(() => {
        if (!targetData?.groupId) return '';
        const origin = window.location.origin;
        if (scope === 'event' && (targetData.folderId || targetData.eventId)) {
            const fId = targetData.folderId;
            return fId ? `${origin}/public-scoreboard/${targetData.groupId}?folderId=${fId}` : `${origin}/public-scoreboard/${targetData.groupId}`;
        }
        return `${origin}/public-scoreboard/${targetData.groupId}`;
    }, [targetData, scope]);

    useEffect(() => {
        if (!targetUrl) return;
        QRCode.toDataURL(targetUrl, {
            width: 400,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
        })
        .then(url => setQrDataUrl(url))
        .catch(err => console.error('QR Code error:', err));
    }, [targetUrl]);

    if (!isOpen || !targetData) return null;

    if (flyerPrintActive) {
        return (
            <QRCodePrintFlyer 
                targetUrl={targetUrl}
                qrDataUrl={qrDataUrl}
                groupName={targetData.groupName}
                eventName={scope === 'event' ? (targetData.eventName || targetData.folderName) : null}
                onClose={() => setFlyerPrintActive(false)}
                t={t}
            />
        );
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(targetUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-[9999999] animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-scale-in">
                <div className="flex justify-between items-center mb-6 border-b border-[#4f5f68]/30 pb-3">
                    <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">
                        📱 {t('qrCodeModalTitle', 'QR-Code für Live-Rangliste')}
                    </h3>
                    <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition cursor-pointer">
                        <Icons.Close/>
                    </button>
                </div>

                {hasSpecificScope && (
                    <div className="mb-5 bg-[#131d22] p-1 rounded-xl border border-[#4f5f68]/30 flex font-mono text-xs">
                        <button
                            type="button"
                            onClick={() => setScope('event')}
                            className={`flex-1 py-2 px-3 rounded-lg font-semibold transition cursor-pointer ${scope === 'event' ? 'bg-[#d97757] text-[#f3efe6] shadow-sm' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                        >
                            📌 {targetData.eventName || targetData.folderName || t('qrScopeThisEvent', 'Nur dieses Event')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setScope('group')}
                            className={`flex-1 py-2 px-3 rounded-lg font-semibold transition cursor-pointer ${scope === 'group' ? 'bg-[#3a7d8a] text-[#162127] font-bold shadow-sm' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                        >
                            👥 {targetData.groupName || t('qrScopeAllGroup', 'Ganze Gruppe')}
                        </button>
                    </div>
                )}

                <div className="bg-white p-4 rounded-2xl shadow-inner mx-auto w-64 h-64 flex items-center justify-center border-4 border-[#3a7d8a]/40 mb-5">
                    {qrDataUrl ? (
                        <img src={qrDataUrl} alt="QR Code" className="w-full h-full object-contain" />
                    ) : (
                        <div className="text-gray-500 font-mono text-xs">Lade QR-Code...</div>
                    )}
                </div>

                <p className="text-center text-xs text-[#a4b3bc] mb-4 font-mono leading-relaxed">
                    {t('qrCodeScanHint', 'Scanne den QR-Code mit der Smartphone-Kamera, um die Rangliste live auf dem Handy mitzuverfolgen.')}
                </p>

                <div className="flex items-center gap-2 mb-6 bg-[#131d22] border border-[#4f5f68]/30 rounded-xl p-1.5 pl-3">
                    <input 
                        type="text" 
                        readOnly 
                        value={targetUrl} 
                        className="bg-transparent text-xs font-mono text-[#8ec8d4] w-full focus:outline-hidden truncate"
                    />
                    <button 
                        onClick={handleCopy} 
                        className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition flex items-center gap-1 cursor-pointer whitespace-nowrap ${copied ? 'bg-emerald-600 text-white' : 'bg-[#4f5f68]/30 text-[#f3efe6] hover:bg-[#4f5f68]/50'}`}
                    >
                        {copied ? '✓ Kopiert' : '📋 Kopieren'}
                    </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 border-t border-[#4f5f68]/30 pt-4">
                    <a 
                        href={targetUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flow-btn-ghost flex-1 py-2.5 text-xs md:text-sm text-center flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                        🔗 {t('openInNewTab', 'Im neuen Tab')}
                    </a>
                    <button 
                        type="button" 
                        onClick={() => setFlyerPrintActive(true)} 
                        className="flow-btn-coral flex-1 py-2.5 text-xs md:text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer font-bold"
                    >
                        🖨️ {t('printFlyer', 'Aushang / Flyer drucken')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const getDescendantFolders = (parentId, allFolders) => {
    let descendants = [parseInt(parentId)];
    const children = allFolders.filter(f => f.parent_id === parseInt(parentId));
    children.forEach(child => {
        descendants = descendants.concat(getDescendantFolders(child.id, allFolders));
    });
    return descendants;
};

const Scoreboard = ({ user, userGroups, folders, showMessage, requestConfirm, publicGroupId, publicFolderId: propPublicFolderId, onEditReceipt }) => {
    const { t } = useTranslation();
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const publicMode = !user;
    const [filterGroupId, setFilterGroupId] = useState('');
    const [filterFolderId, setFilterFolderId] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [qrModalData, setQrModalData] = useState(null);
    const [publicGroupInfo, setPublicGroupInfo] = useState(null);
    const [publicFolderInfo, setPublicFolderInfo] = useState(null);

    const activePublicFolderId = propPublicFolderId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('folderId') : null) || null;

    useEffect(() => {
        if (publicMode && publicGroupId) {
            fetch(`${API_BASE_URL}/public/groups/${publicGroupId}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => { if (data) setPublicGroupInfo(data); })
                .catch(() => {});
        }
        if (publicMode && activePublicFolderId) {
            fetch(`${API_BASE_URL}/public/folders/${activePublicFolderId}`)
                .then(res => res.ok ? res.json() : null)
                .then(data => { if (data) setPublicFolderInfo(data); })
                .catch(() => {});
        }
    }, [publicMode, publicGroupId, activePublicFolderId]);
    
    const [tieBreakerRule, setTieBreakerRule] = useState(() => localStorage.getItem('kks_tie_breaker_rule') || 'shots');
    const [tieModalState, setTieModalState] = useState({ isOpen: false, score: null, receipts: [] });

    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [printViewActive, setPrintViewActive] = useState(false);
    const [printSettings, setPrintSettings] = useState({
        orientation: 'portrait',
        fontSize: 'text-sm',
        showDetails: false,
        showTeamMembers: true,
        groupScope: 'both',
        showColumns: { rank: true, name: true, score: true, stich: true, group: true, date: true }
    });

    useEffect(() => {
        if (filterFolderId && folders) {
            const currentFolder = folders.find(f => f.id == filterFolderId);
            if (currentFolder?.tie_breaker_rule) {
                setTieBreakerRule(currentFolder.tie_breaker_rule);
            }
        }
    }, [filterFolderId, folders]);

    const handleTieBreakerChange = async (newRule, specificFolderId = null) => {
        setTieBreakerRule(newRule);
        localStorage.setItem('kks_tie_breaker_rule', newRule);
        const folderIdToUpdate = specificFolderId || filterFolderId;
        if (user?.token) {
            try {
                let targetFolders = [];
                if (folderIdToUpdate) {
                    targetFolders = [{ id: folderIdToUpdate }];
                } else if (filterGroupId && folders) {
                    targetFolders = folders.filter(f => f.group_id === parseInt(filterGroupId));
                } else if (folders && folders.length > 0) {
                    targetFolders = folders;
                }
                if (targetFolders.length > 0) {
                    await Promise.all(targetFolders.map(f =>
                        fetch(`${API_BASE_URL}/folders/${f.id}/tie_breaker`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                            body: JSON.stringify({ tie_breaker_rule: newRule })
                        })
                    ));
                    setReceipts(prev => prev.map(r => {
                        if (targetFolders.some(tf => tf.id === r.folder_id)) {
                            return { ...r, tie_breaker_rule: newRule };
                        }
                        return r;
                    }));
                    if (folders && Array.isArray(folders)) {
                        folders.forEach(f => {
                            if (targetFolders.some(tf => tf.id === f.id)) {
                                f.tie_breaker_rule = newRule;
                            }
                        });
                    }
                }
            } catch(e) { console.error(e); }
        }
    };

    const handleMoveReceipt = async (receipt, direction, list, currentIndex) => {
        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= list.length) return;
        const targetReceipt = list[targetIndex];

        if (receipt.isNoGroup || targetReceipt.isNoGroup) {
            showMessage(t('noGroupCannotMove', 'Die Gruppe "Keine Gruppe" kann nicht verschoben werden und bleibt am letzten Platz.'), 'info');
            return;
        }

        if (parseFloat(receipt.total_score) !== parseFloat(targetReceipt.total_score)) {
            showMessage(t('onlyTiesCanMove', 'Manuelles Verschieben ist nur bei Gleichstand möglich.'), 'info');
            return;
        }

        const currMod = parseInt(receipt.manual_rank_modifier) || 0;
        const targetMod = parseInt(targetReceipt.manual_rank_modifier) || 0;

        let newReceiptMod, newTargetMod;
        if (direction === 'up') {
            newReceiptMod = Math.max(currMod, targetMod) + 1;
            newTargetMod = Math.min(currMod, targetMod);
            if (newReceiptMod <= newTargetMod) newReceiptMod = newTargetMod + 1;
        } else {
            newTargetMod = Math.max(currMod, targetMod) + 1;
            newReceiptMod = Math.min(currMod, targetMod);
            if (newTargetMod <= newReceiptMod) newTargetMod = newReceiptMod + 1;
        }

        if (tieBreakerRule !== 'manual') {
            handleTieBreakerChange('manual', receipt.folder_id);
        }

        setReceipts(prev => prev.map(item => {
            if (item.id === receipt.id) return { ...item, manual_rank_modifier: newReceiptMod };
            if (item.id === targetReceipt.id) return { ...item, manual_rank_modifier: newTargetMod };
            return item;
        }));

        try {
            const res = await fetch(`${API_BASE_URL}/receipts/tie_breakers/batch`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: JSON.stringify({
                    updates: [
                        { id: receipt.id, manualRankModifier: newReceiptMod },
                        { id: targetReceipt.id, manualRankModifier: newTargetMod }
                    ]
                })
            });
            if (res.ok) {
                showMessage(t('rankAdjusted', `Gleichstand angepasst: Reihenfolge aktualisiert.`), 'success');
                fetchReceipts(true);
            } else {
                showMessage(t('rankAdjustError', 'Fehler beim Aktualisieren der Rangierung.'), 'error');
                fetchReceipts(true);
            }
        } catch (err) {
            showMessage(t('networkError', 'Netzwerkfehler.'), 'error');
            fetchReceipts(true);
        }
    };

    const openTieModalForScore = (score, list) => {
        const tiedList = list.filter(r => parseFloat(r.total_score) === parseFloat(score));
        if (tiedList.length >= 2) {
            setTieModalState({ isOpen: true, score, receipts: tiedList });
        }
    };

    const openTieModalForGroup = (group) => {
        const valid = (group.receipts || []).filter(r => !r.isNoGroup && !r._isNoGroup);
        for (let i = 0; i < valid.length - 1; i++) {
            if (parseFloat(valid[i].total_score) === parseFloat(valid[i + 1].total_score)) {
                openTieModalForScore(parseFloat(valid[i].total_score), valid);
                return;
            }
        }
    };

    const hasAnyTie = (list) => {
        if (!list || list.length < 2) return false;
        const valid = list.filter(r => !r.isNoGroup && !r._isNoGroup);
        for (let i = 0; i < valid.length - 1; i++) {
            if (parseFloat(valid[i].total_score) === parseFloat(valid[i + 1].total_score)) return true;
        }
        return false;
    };

    const countTies = (list) => {
        if (!list || list.length < 2) return 0;
        const valid = list.filter(r => !r.isNoGroup && !r._isNoGroup);
        const scoreCounts = {};
        valid.forEach(r => {
            const s = parseFloat(r.total_score);
            scoreCounts[s] = (scoreCounts[s] || 0) + 1;
        });
        return Object.values(scoreCounts).filter(c => c > 1).reduce((sum, c) => sum + c, 0);
    };
    
    useEffect(() => { const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500); return () => clearTimeout(handler); }, [searchTerm]);

    const fetchReceipts = useCallback(async (isLiveUpdate = false) => {
        if (!isLiveUpdate) setLoading(true);
        const params = new URLSearchParams(); let headers = {};
        if (publicMode) { 
            params.append('public', 'true'); 
            if (publicGroupId) { 
                params.append('groupId', publicGroupId); 
            } else if (debouncedSearchTerm) { 
                params.append('groupSearch', debouncedSearchTerm); 
            }
            const effFolderId = propPublicFolderId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('folderId') : null);
            if (effFolderId) {
                params.append('folderId', effFolderId);
            }
        } else {
            if (user?.token) headers['Authorization'] = `Bearer ${user.token}`;
            if (filterGroupId) params.append('groupId', filterGroupId); 
            if (filterStartDate) params.append('startDate', filterStartDate); if (filterEndDate) params.append('endDate', filterEndDate);
        }
        try {
            const response = await fetch(`${API_BASE_URL}/receipts?${params.toString()}`, { headers });
            const data = await response.json();
            if (response.ok) setReceipts(data); else showMessage(data.message || 'Fehler beim Laden', 'error');
        } catch (error) { showMessage('Netzwerkfehler', 'error'); } finally { if (!isLiveUpdate) setLoading(false); }
    }, [user, publicMode, publicGroupId, propPublicFolderId, debouncedSearchTerm, filterGroupId, filterStartDate, filterEndDate, showMessage]);

    useEffect(() => { fetchReceipts(); }, [fetchReceipts]);
    useEffect(() => { if (publicMode) { const interval = setInterval(() => fetchReceipts(true), 15000); return () => clearInterval(interval); } }, [publicMode, fetchReceipts]);
    
    const handleDelete = async (receiptId) => {
        requestConfirm('Löschen?', async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/receipts/${receiptId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (response.ok) { showMessage('Gelöscht', 'success'); fetchReceipts(); }
            } catch (error) { showMessage(error.message, 'error'); }
        });
    };

    const sortZickZack = (arr) => {
        let pool = [...arr].map(r => {
            const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
            const validShots = shots.map(s => parseFloat(s.score) || 0);
            return { ...r, _bestShot: validShots.length > 0 ? Math.max(...validShots) : 0, _total: parseFloat(r.total_score) || 0 };
        });
        let sorted = []; let useTotal = true;
        while(pool.length > 0) { 
            pool.sort((a, b) => useTotal ? (b._total - a._total || b._bestShot - a._bestShot || a.id - b.id) : (b._bestShot - a._bestShot || b._total - a._total || a.id - b.id)); 
            const winner = pool.shift();
            winner._rankedBy = useTotal ? 'total' : 'best';
            sorted.push(winner); 
            useTotal = !useTotal; 
        }
        return sorted;
    };

    const sortGruppenStich = (arr, bestOfCount) => {
        const teams = {};
        arr.forEach(r => {
            const rawName = r.team_name?.trim() || '';
            const isNoGroup = !rawName || rawName.toLowerCase() === 'keine gruppe' || rawName.toLowerCase() === 'ohne gruppe';
            const tName = isNoGroup ? 'Keine Gruppe' : rawName;
            if (!teams[tName]) {
                teams[tName] = { 
                    isTeam: true, 
                    id: 'team_' + tName, 
                    team_name: tName, 
                    isNoGroup: isNoGroup,
                    members: [], 
                    total_score: 0, 
                    timestamp: r.timestamp, 
                    group_name: r.group_name, 
                    folder_name: r.folder_name, 
                    stich_name: r.stich_name, 
                    bestOfCount 
                };
            }
            teams[tName].members.push(r);
        });

        const regularTeams = [];
        let noGroupTeam = null;

        Object.values(teams).forEach(team => {
            team.members.sort((a, b) => (parseFloat(b.total_score) || 0) - (parseFloat(a.total_score) || 0));
            const count = bestOfCount > 0 ? bestOfCount : team.members.length;
            team.total_score = team.members.slice(0, count).reduce((sum, m) => sum + (parseFloat(m.total_score) || 0), 0);
            if (team.isNoGroup) {
                noGroupTeam = team;
            } else {
                regularTeams.push(team);
            }
        });

        // Normale Teams nach Punkten absteigend sortieren
        regularTeams.sort((a, b) => b.total_score - a.total_score);

        // Ränge für reguläre Teams vergeben
        regularTeams.forEach((t, idx) => {
            t._computedRank = idx + 1;
        });

        // "Keine Gruppe" MUSS ganz am Schluss sein und darf NIEMALS aufsteigen
        if (noGroupTeam) {
            noGroupTeam._computedRank = '-';
            noGroupTeam._isNoGroup = true;
            regularTeams.push(noGroupTeam);
        }

        return regularTeams;
    };

    const sortBestOf = (arr, bestOfCount = 10, rule = 'shots') => {
        const count = bestOfCount > 0 ? bestOfCount : 10;
        const list = arr.map(item => ({ ...item }));

        list.forEach(r => {
            let rawShots = [];
            try {
                const raw = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                if (Array.isArray(raw)) {
                    rawShots = raw.map((s, idx) => {
                        const score = typeof s === 'object' && s !== null ? (parseFloat(s.score) || 0) : (parseFloat(s) || 0);
                        const direction = typeof s === 'object' && s !== null ? s.direction : '';
                        return { origIdx: idx, score, direction };
                    });
                }
            } catch(e) {
                rawShots = [];
            }

            // Alle Schüsse des Schützen absteigend sortiert
            const sortedShots = [...rawShots].sort((a, b) => b.score - a.score);

            // Die besten X Schüsse
            const topShots = sortedShots.slice(0, count);
            const topIndices = new Set(topShots.map(s => s.origIdx));
            const topSum = topShots.reduce((sum, s) => sum + s.score, 0);

            // Verbleibende Schüsse (11., 12., etc.)
            const remainingShots = sortedShots.slice(count);
            const tieBreakerShot = remainingShots.length > 0 ? remainingShots[0] : null;

            r._rawShotsObj = rawShots;
            r._rawShots = rawShots.map(s => s.score);
            r._sortedShots = sortedShots.map(s => s.score);
            r._bestOfCount = count;
            r._topIndices = topIndices;
            r._tieBreakerIdx = tieBreakerShot ? tieBreakerShot.origIdx : null;
            r._tieBreakerScore = tieBreakerShot ? tieBreakerShot.score : null;
            r._remainingShots = remainingShots.map(s => s.score);
            r._calculatedBestOfTotal = topSum;

            if (topShots.length > 0 && (!r.total_score || parseFloat(r.total_score) === 0)) {
                r.total_score = topSum;
            }
        });

        list.sort((a, b) => {
            // 1. Primär: Best-Of Gesamtpunktzahl absteigend
            const totalA = parseFloat(a.total_score) || a._calculatedBestOfTotal || 0;
            const totalB = parseFloat(b.total_score) || b._calculatedBestOfTotal || 0;
            const diffTotal = totalB - totalA;
            if (diffTotal !== 0 && !isNaN(diffTotal)) return diffTotal;

            // Manueller Modifier
            const modA = parseInt(a.manual_rank_modifier) || 0;
            const modB = parseInt(b.manual_rank_modifier) || 0;
            if (modA !== modB) return modB - modA;

            // 2. Bei Gleichstand: 11. Resultat (nächstbester Schuss), dann 12., usw.
            const maxRemaining = Math.max(a._remainingShots.length, b._remainingShots.length);
            for (let i = 0; i < maxRemaining; i++) {
                const valA = a._remainingShots[i] !== undefined ? a._remainingShots[i] : -1;
                const valB = b._remainingShots[i] !== undefined ? b._remainingShots[i] : -1;
                if (valA !== valB) {
                    a._tieResolvedByShot = true;
                    b._tieResolvedByShot = true;
                    a._decidingShotNum = count + 1 + i;
                    b._decidingShotNum = count + 1 + i;
                    return valB - valA;
                }
            }

            // 3. Fallback-Regeln (Ältere / Jüngere / Schüsse)
            if (rule === 'older') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobA - yobB;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            } else if (rule === 'younger') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobB - yobA;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            }

            return a.id - b.id;
        });

        // Ränge berechnen und Gleichstände kennzeichnen
        list.forEach((r, idx) => {
            const prev = idx > 0 ? list[idx - 1] : null;
            const next = idx < list.length - 1 ? list[idx + 1] : null;

            const prevSameTotal = prev && (parseFloat(prev.total_score) || 0) === (parseFloat(r.total_score) || 0);
            const nextSameTotal = next && (parseFloat(next.total_score) || 0) === (parseFloat(r.total_score) || 0);

            if (prevSameTotal || nextSameTotal) {
                r._hadBestOfScoreTie = true;
            }

            if (idx === 0) {
                r._computedRank = 1;
            } else {
                const isFullTie = prevSameTotal && (
                    r._remainingShots.join(',') === prev._remainingShots.join(',')
                );
                if (rule === 'shared' && isFullTie) {
                    r._computedRank = prev._computedRank;
                } else {
                    r._computedRank = idx + 1;
                }
            }

            const isTiePrev = prev && (prev._computedRank === r._computedRank || (prevSameTotal && rule === 'manual'));
            const isTieNext = next && (next._computedRank === r._computedRank || (nextSameTotal && rule === 'manual'));
            r._isTie = !!(isTiePrev || isTieNext);
        });

        return list;
    };

    const sortStandard = (arr, rule = 'shots') => {
        const list = arr.map(item => ({ ...item }));
        list.forEach(r => {
            if (r._sortedShots === undefined) {
                try {
                    const raw = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                    if (Array.isArray(raw)) {
                        r._rawShots = raw.map(s => (typeof s === 'object' && s !== null ? parseFloat(s.score) : parseFloat(s))).filter(n => !isNaN(n));
                    } else {
                        r._rawShots = [];
                    }
                } catch(e) {
                    r._rawShots = [];
                }
                r._sortedShots = [...r._rawShots].sort((a, b) => b - a);
                if (r._sortedShots.length > 0) {
                    const maxHit = r._sortedShots[0];
                    const count = r._sortedShots.filter(s => s === maxHit).length;
                    r._tiefInfo = `🎯 ${count}x ${maxHit}er`;
                } else {
                    r._tiefInfo = '';
                }
            }
        });

        list.sort((a, b) => {
            const diffTotal = (parseFloat(b.total_score) || 0) - (parseFloat(a.total_score) || 0);
            if (diffTotal !== 0 && !isNaN(diffTotal)) return diffTotal;

            const modA = parseInt(a.manual_rank_modifier) || 0;
            const modB = parseInt(b.manual_rank_modifier) || 0;

            if (modA !== modB) return modB - modA;

            if (rule === 'older') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobA - yobB;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            } else if (rule === 'younger') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobB - yobA;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            } else if (rule === 'last_shot') {
                const len = Math.min(a._rawShots.length, b._rawShots.length);
                for (let i = 0; i < len; i++) {
                    const valA = a._rawShots[a._rawShots.length - 1 - i] || 0;
                    const valB = b._rawShots[b._rawShots.length - 1 - i] || 0;
                    if (valA !== valB) return valB - valA;
                }
            }

            if (modA !== modB) return modB - modA;

            const maxLen = Math.max(a._sortedShots.length, b._sortedShots.length);
            for (let i = 0; i < maxLen; i++) {
                const valA = a._sortedShots[i] || 0;
                const valB = b._sortedShots[i] || 0;
                if (valA !== valB) return valB - valA;
            }

            const yobA = parseInt(a.year_of_birth) || 0;
            const yobB = parseInt(b.year_of_birth) || 0;
            if (yobA > 0 && yobB > 0 && yobA !== yobB) {
                return rule === 'younger' ? yobB - yobA : yobA - yobB;
            }

            return a.id - b.id;
        });

        list.forEach((r, idx) => {
            if (idx === 0) {
                r._computedRank = 1;
            } else {
                const prev = list[idx - 1];
                const isExactScoreTie = parseFloat(r.total_score) === parseFloat(prev.total_score);
                if (rule === 'shared' && isExactScoreTie) {
                    r._computedRank = prev._computedRank;
                } else {
                    r._computedRank = idx + 1;
                }
            }
            const prev = idx > 0 ? list[idx - 1] : null;
            const next = idx < list.length - 1 ? list[idx + 1] : null;
            r._isTie = !!((prev && parseFloat(prev.total_score) === parseFloat(r.total_score)) ||
                          (next && parseFloat(next.total_score) === parseFloat(r.total_score)));
        });

        return list;
    };

    const sortDifferenzler = (arr, rule = 'shots') => {
        const list = arr.map(item => ({ ...item }));
        list.forEach(r => {
            if (r._sortedShots === undefined) {
                try {
                    const raw = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                    if (Array.isArray(raw)) {
                        r._rawShots = raw.map(s => (typeof s === 'object' && s !== null ? parseFloat(s.score) : parseFloat(s))).filter(n => !isNaN(n));
                    } else {
                        r._rawShots = [];
                    }
                } catch(e) {
                    r._rawShots = [];
                }
                r._sortedShots = [...r._rawShots].sort((a, b) => b - a);
                if (r._sortedShots.length > 0) {
                    const maxHit = r._sortedShots[0];
                    const count = r._sortedShots.filter(s => s === maxHit).length;
                    r._tiefInfo = `🎯 ${count}x ${maxHit}er`;
                } else {
                    r._tiefInfo = '';
                }
                if (r.actual_score === undefined || r.actual_score === null) {
                    r.actual_score = r._rawShots.reduce((a, b) => a + b, 0);
                }
            }
        });

        list.sort((a, b) => {
            // 1. Geringste Differenz zuerst (AUFSTEIGEND: 0 ist Rang 1)
            const diffA = parseFloat(a.total_score) || 0;
            const diffB = parseFloat(b.total_score) || 0;
            if (diffA !== diffB) return diffA - diffB;

            // 2. Bei gleicher Differenz: Höhere geschossene Punktzahl gewinnt
            const actA = parseFloat(a.actual_score) || 0;
            const actB = parseFloat(b.actual_score) || 0;
            if (actA !== actB) return actB - actA;

            const modA = parseInt(a.manual_rank_modifier) || 0;
            const modB = parseInt(b.manual_rank_modifier) || 0;

            if (modA !== modB) return modB - modA;

            if (rule === 'older') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobA - yobB;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            } else if (rule === 'younger') {
                const yobA = parseInt(a.year_of_birth) || 0;
                const yobB = parseInt(b.year_of_birth) || 0;
                if (yobA > 0 && yobB > 0 && yobA !== yobB) return yobB - yobA;
                if (yobA > 0 && !yobB) return -1;
                if (!yobA && yobB > 0) return 1;
            } else if (rule === 'last_shot') {
                const lastA = a._rawShots.length > 0 ? a._rawShots[a._rawShots.length - 1] : 0;
                const lastB = b._rawShots.length > 0 ? b._rawShots[b._rawShots.length - 1] : 0;
                if (lastA !== lastB) return lastB - lastA;
            }

            if (modA !== modB) return modB - modA;

            const maxLen = Math.max(a._sortedShots.length, b._sortedShots.length);
            for (let i = 0; i < maxLen; i++) {
                const valA = a._sortedShots[i] || 0;
                const valB = b._sortedShots[i] || 0;
                if (valA !== valB) return valB - valA;
            }

            const yobA = parseInt(a.year_of_birth) || 0;
            const yobB = parseInt(b.year_of_birth) || 0;
            if (yobA > 0 && yobB > 0 && yobA !== yobB) {
                return rule === 'younger' ? yobB - yobA : yobA - yobB;
            }

            return a.id - b.id;
        });

        list.forEach((r, idx) => {
            if (idx === 0) {
                r._computedRank = 1;
            } else {
                const prev = list[idx - 1];
                const isExactScoreTie = parseFloat(r.total_score) === parseFloat(prev.total_score);
                if (rule === 'shared' && isExactScoreTie) {
                    r._computedRank = prev._computedRank;
                } else {
                    r._computedRank = idx + 1;
                }
            }
            const prev = idx > 0 ? list[idx - 1] : null;
            const next = idx < list.length - 1 ? list[idx + 1] : null;
            r._isTie = !!((prev && parseFloat(prev.total_score) === parseFloat(r.total_score)) ||
                          (next && parseFloat(next.total_score) === parseFloat(r.total_score)));
        });

        return list;
    };

    const processedData = useMemo(() => {
        const groups = {};
        let filteredReceipts = receipts;

        if (!publicMode && filterFolderId) {
            const allowedFolderIds = getDescendantFolders(filterFolderId, folders);
            filteredReceipts = receipts.filter(r => allowedFolderIds.includes(parseInt(r.folder_id)));
        }

        filteredReceipts.forEach(r => {
            const groupKey = `${r.group_id || 0}_${r.folder_id || 0}_${r.template_id || 0}`;
            if (!groups[groupKey]) {
                const folder = folders?.find(f => f.id == r.folder_id);
                const parent = (r.parent_folder_name || '').trim();
                const folderName = (r.folder_name || '').trim();

                let categoryTitle = folderName || r.stich_name || 'Unsortiert';
                if (parent && folderName && parent.toLowerCase() !== folderName.toLowerCase()) {
                    categoryTitle = `${parent} › ${folderName}`;
                }

                groups[groupKey] = {
                    name: `${r.group_name || 'Allgemein'} – ${categoryTitle}`,
                    receipts: [],
                    type: 'standard',
                    bestOfCount: 0,
                    teamSize: 0,
                    tieBreakerRule: folder?.tie_breaker_rule || r.tie_breaker_rule || 'shots'
                };
            }
            groups[groupKey].receipts.push(r);
            if (r.template_type && groups[groupKey].type === 'standard') {
                groups[groupKey].type = r.template_type;
                groups[groupKey].bestOfCount = r.best_of_count;
                groups[groupKey].teamSize = r.team_size;
            }
        });
        const finalGroups = {};
        Object.keys(groups).forEach(key => {
            const g = groups[key];
            const effectiveRule = filterFolderId ? tieBreakerRule : (g.tieBreakerRule || tieBreakerRule || 'shots');
            if (g.type === 'zick_zack') {
                g.receipts = sortZickZack(g.receipts);
                finalGroups[key] = g;
            } else if (g.type === 'differenzler') {
                g.receipts = sortDifferenzler(g.receipts, effectiveRule);
                finalGroups[key] = g;
            } else if (g.type === 'gruppen_stich') {
                const teamsGroup = { ...g, name: g.name + ' - Gruppenwertung', type: 'gruppen_stich', isTeamGroup: true, isGruppenStich: true };
                teamsGroup.receipts = sortGruppenStich([...g.receipts], g.bestOfCount);
                finalGroups[key + '_gruppe'] = teamsGroup;
                
                const individualsGroup = { ...g, name: g.name + ' - Einzelwertung', type: 'standard', isIndividualGroup: true, isGruppenStich: true };
                individualsGroup.receipts = sortStandard([...g.receipts], effectiveRule);
                finalGroups[key + '_einzel'] = individualsGroup;
            } else if (g.type === 'best_of') {
                g.receipts = sortBestOf(g.receipts, g.bestOfCount, effectiveRule);
                finalGroups[key] = g;
            } else {
                g.receipts = sortStandard(g.receipts, effectiveRule);
                finalGroups[key] = g;
            }
        });
        return finalGroups;
    }, [receipts, filterFolderId, folders, publicMode, tieBreakerRule]);
    
    const scoreboardFolderTree = useMemo(() => {
        if (!folders || !filterGroupId) return [];
        const tree = []; const map = {};
        const filteredFolders = folders.filter(f => f.group_id === parseInt(filterGroupId));
        filteredFolders.forEach(folder => { map[folder.id] = { ...folder, children: [] }; });
        filteredFolders.forEach(folder => { if (folder.parent_id && map[folder.parent_id]) { map[folder.parent_id].children.push(map[folder.id]); } else { tree.push(map[folder.id]); } });
        return tree;
    }, [folders, filterGroupId]);

    const processedPublicData = useMemo(() => {
        const foldersMap = {};
        receipts.forEach(r => {
            const folderKey = `${r.folder_id || 0}_${r.template_id || 0}`;
            if (!foldersMap[folderKey]) {
                const folder = folders?.find(f => f.id == r.folder_id);
                const parent = (r.parent_folder_name || '').trim();
                const folderName = (r.folder_name || '').trim();

                let categoryTitle = folderName || r.stich_name || 'Unsortiert';
                if (parent && folderName && parent.toLowerCase() !== folderName.toLowerCase()) {
                    categoryTitle = `${parent} › ${folderName}`;
                }

                foldersMap[folderKey] = {
                    name: categoryTitle,
                    folder_name: r.folder_name,
                    parent_folder_name: r.parent_folder_name,
                    stich_name: r.stich_name,
                    receipts: [],
                    type: 'standard',
                    bestOfCount: 0,
                    tieBreakerRule: folder?.tie_breaker_rule || r.tie_breaker_rule || 'shots'
                };
            }
            foldersMap[folderKey].receipts.push(r);
            if (r.template_type && foldersMap[folderKey].type === 'standard') {
                foldersMap[folderKey].type = r.template_type;
                foldersMap[folderKey].bestOfCount = r.best_of_count;
            }
        });
        const finalGroups = {};
        Object.keys(foldersMap).forEach(key => {
            const g = foldersMap[key];
            const effectiveRule = g.tieBreakerRule || 'shots';
            if (g.type === 'zick_zack') {
                g.receipts = sortZickZack(g.receipts);
                finalGroups[key] = g;
            } else if (g.type === 'differenzler') {
                g.receipts = sortDifferenzler(g.receipts, effectiveRule);
                finalGroups[key] = g;
            } else if (g.type === 'gruppen_stich') {
                const teamsGroup = { ...g, name: g.name + ' - Gruppenwertung', type: 'gruppen_stich', isTeamGroup: true, isGruppenStich: true };
                teamsGroup.receipts = sortGruppenStich([...g.receipts], g.bestOfCount);
                finalGroups[key + '_gruppe'] = teamsGroup;
                
                const individualsGroup = { ...g, name: g.name + ' - Einzelwertung', type: 'standard', isIndividualGroup: true, isGruppenStich: true };
                individualsGroup.receipts = sortStandard([...g.receipts], effectiveRule);
                finalGroups[key + '_einzel'] = individualsGroup;
            } else if (g.type === 'best_of') {
                g.receipts = sortBestOf(g.receipts, g.bestOfCount, effectiveRule);
                finalGroups[key] = g;
            } else {
                g.receipts = sortStandard(g.receipts, effectiveRule);
                finalGroups[key] = g;
            }
        });
        return finalGroups;
    }, [receipts, folders]);

    const handleExcelExport = () => {
        const dataToExport = publicMode ? processedPublicData : processedData;
        let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
        
        let maxShots = 0;
        Object.keys(dataToExport).forEach(key => {
            dataToExport[key].receipts.forEach(r => {
                 if (r.template_type !== 'kombination' && r.template_type !== 'anmeldung' && !r.isTeam) {
                     try {
                        const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                        if (shots.length > maxShots) maxShots = shots.length;
                     } catch(e) {}
                 }
            });
        });

        let headers = ["Kategorie/Ordner", "Rang", "Teilnehmer", "Resultat", "Stich/Detail", "Gruppe", "Datum"];
        for(let i=1; i<=maxShots; i++) headers.push(`Schuss ${i}`);
        headers.push("Bemerkungen");

        csvContent += headers.join(";") + "\n";
        
        Object.keys(dataToExport).forEach(key => {
            const group = dataToExport[key];
            group.receipts.forEach((r, i) => {
                const rang = r._isNoGroup ? '-' : (r._computedRank || (i + 1));
                const teilnehmer = r.isTeam ? (r._isNoGroup ? 'Team: Keine Gruppe' : `Team: ${r.team_name}`) : (r.team_name && group.name.includes('Einzelwertung') ? (r.team_name.toLowerCase() === 'keine gruppe' ? `${r.participant_name} (Keine Gruppe)` : `${r.participant_name} (${r.team_name})`) : r.participant_name);
                const resultat = r.template_type === 'differenzler' ? `Diff: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}` : r.template_type === 'anmeldung' ? `${Number(r.total_score).toFixed(0)} Pers.` : Number(r.total_score).toFixed(2).replace(/\.00$/, '');
                const stich = group.type === 'zick_zack' ? (r._rankedBy === 'total' ? 'Total' : `Tief: ${r._bestShot}`) : group.type === 'differenzler' ? `Ansage: ${r.estimated_score ?? '-'} | Ist: ${r.actual_score ?? '-'}` : group.type === 'best_of' ? `Best Of ${r._bestOfCount || group.bestOfCount || 10}` : (r.folder_name || r.stich_name || '-');
                const gruppe = (r.team_name && group.name.includes('Einzelwertung')) ? (r.team_name.toLowerCase() === 'keine gruppe' ? 'Keine Gruppe' : r.team_name) : (r.group_name || '-');
                const datum = new Date(r.timestamp).toLocaleDateString();
                
                let row = [group.name, rang, teilnehmer, resultat, stich, gruppe, datum].map(v => `"${v}"`);
                
                let shotsData = [];
                let bemerkungen = "";

                if (r.template_type === 'anmeldung') {
                    try {
                        const data = (typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : r.individual_scores)[0] || {};
                        bemerkungen = (data.companion ? "Mit Begleitung (+1)" : "Alleine") + (data.contact ? ` | Kontakt: ${data.contact}` : "");
                    } catch(e) {}
                } else if (r.template_type === 'kombination') {
                     try {
                         const data = (typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : r.individual_scores) || [];
                         bemerkungen = data.map(d => `${d.name}: ${Number(d.weighted).toFixed(1)}`).join(" + ");
                     } catch(e) {}
                } else if (r.template_type === 'differenzler') {
                     bemerkungen = `Ansage: ${r.estimated_score ?? '-'} | Geschossen: ${r.actual_score ?? '-'} | Differenz: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}`;
                     try {
                         const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                         shotsData = shots.map(s => `${s.score}${s.direction || ''}`);
                     } catch(e) {}
                } else if (r.isTeam) {
                     bemerkungen = r._isNoGroup 
                        ? "Keine Gruppe (Außer Konkurrenz, letzter Platz)" 
                        : `Teammitglieder: ${r.members.map((m, mIdx) => `${mIdx + 1}. ${m.participant_name} (${Number(m.total_score).toFixed(1).replace('.0', '')} Pkt${r.bestOfCount > 0 && mIdx >= r.bestOfCount ? ', Streichresultat' : ''})`).join(", ")}`;
                } else if (group.type === 'best_of' || r.template_type === 'best_of') {
                     bemerkungen = `Top ${r._bestOfCount || 10} Schüsse gewertet` + (r._tieBreakerScore !== null ? ` | 11. Schuss: ${r._tieBreakerScore} Pkt${r._tieResolvedByShot ? ' (Entscheidung bei Gleichstand)' : ''}` : '');
                     try {
                         const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                         shotsData = shots.map(s => `${s.score}${s.direction || ''}`);
                     } catch(e) {}
                } else {
                     try {
                         const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                         shotsData = shots.map(s => `${s.score}${s.direction || ''}`);
                     } catch(e) {}
                }

                for(let j=0; j<maxShots; j++) {
                     row.push(`"${shotsData[j] || ''}"`);
                }
                row.push(`"${bemerkungen}"`);

                csvContent += row.join(";") + "\n";
            });
        });
        
        try {
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `KKS_Rangliste_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch(err) {
            console.error("Export Error", err);
        }
    };

    const renderFolderOptions = (nodes, level = 0) => nodes.flatMap(node => [ <option key={node.id} value={node.id}> {'\u00A0\u00A0'.repeat(level)} {node.name} </option>, ...renderFolderOptions(node.children || [], level + 1) ]);
    
    const renderDetailsRow = (r, colSpan) => {
        return (
            <tr className="bg-[#121c21]/90 border-b border-[#4f5f68]/30 print:bg-white print:border-b print:border-gray-300">
                <td colSpan={colSpan} className="px-6 py-4 border-b border-[#4f5f68]/20 print:border-none print:px-2 print:py-2">
                    {r.isTeam ? (
                        <div>
                            <div className="font-bold mb-3 font-mono text-xs uppercase tracking-wider text-[#a4b3bc] print:text-black print:mb-1">Teammitglieder (Top {r.bestOfCount || 'Alle'} in Wertung):</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2">
                                {r.members.map((m, idx) => {
                                    const isOwner = user?.id === m.leader_id; const isGroupLeader = userGroups?.find(g => g.id === m.group_id)?.created_by === user?.id;
                                    const inWertung = idx < (r.bestOfCount > 0 ? r.bestOfCount : r.members.length);
                                    return (
                                    <div key={m.id} className={`bg-[#1a262c] border rounded-xl p-3 shadow-sm print:bg-white print:shadow-none flex justify-between items-center ${!inWertung ? 'opacity-50 border-[#4f5f68]/30' : 'border-[#3a7d8a]/40 print:border-gray-400'}`}>
                                        <div><span className="font-mono font-bold text-[#a4b3bc] mr-2 print:text-black">{idx + 1}.</span><span className="font-semibold text-[#f3efe6]">{m.participant_name}</span>{!inWertung && <span className="ml-2 text-xs bg-[#4f5f68]/30 px-2 py-0.5 rounded text-[#a4b3bc] font-mono print:bg-white print:border">Streichresultat</span>}</div>
                                        <div className="flex items-center gap-4">
                                            <span className="font-mono font-bold text-[#f3efe6] print:text-black">{Number(m.total_score).toFixed(1).replace('.0', '')}</span>
                                            {!publicMode && (isOwner || isGroupLeader) && ( <div className="flex gap-2 no-print"><button onClick={(e) => { e.stopPropagation(); onEditReceipt(m); }} className="text-[#3a7d8a] hover:bg-[#3a7d8a]/20 p-1 rounded transition"><Icons.Edit/></button><button onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} className="text-red-400 hover:bg-red-500/20 p-1 rounded transition"><Icons.Delete/></button></div> )}
                                        </div>
                                    </div>
                                )})}
                            </div>
                        </div>
                    ) : r.template_type === 'kombination' ? (
                        <div className="w-full max-w-md bg-[#1a262c] border border-[#4f5f68]/40 rounded-xl p-4 shadow-sm print:bg-white print:border-gray-400 print:p-2">
                            <div className="font-bold text-[#d97757] font-mono text-xs uppercase tracking-wider mb-2 border-b border-[#4f5f68]/30 pb-1 print:text-black">Berechnungs-Details:</div>
                            {(() => {
                                const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                                if (shots.length === 0) return <div className="italic text-[#a4b3bc] text-sm">Keine Details gespeichert.</div>;
                                return shots.map((det, idx) => ( <div key={idx} className="flex justify-between text-sm py-1.5 border-b border-[#4f5f68]/20 last:border-0 print:border-gray-300"><span className="text-[#f3efe6]">{det.name} <span className="text-[#a4b3bc] text-xs font-mono">(Best: {det.bestScore})</span></span><span className="font-mono text-[#f3efe6]">x {det.percentage}% = <b className="text-[#d97757] print:text-black">{Number(det.weighted).toFixed(1).replace(/\.0$/, '')}</b></span></div> ));
                            })()}
                        </div>
                    ) : r.template_type === 'anmeldung' ? (
                        <div className="w-full max-w-md bg-[#1a262c] border border-[#4f5f68]/40 rounded-xl p-4 shadow-sm print:bg-white print:border-gray-400 print:p-2">
                            <div className="font-bold text-[#3a7d8a] font-mono text-xs uppercase tracking-wider mb-2 border-b border-[#4f5f68]/30 pb-1 print:text-black">Anmelde-Details:</div>
                            {(() => {
                                const data = (typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : r.individual_scores)[0] || {};
                                return (
                                    <>
                                        <div className="flex justify-between text-sm py-1"><span className="text-[#a4b3bc]">Begleitung:</span><b className={data.companion ? "text-emerald-400 print:text-black" : "text-[#a4b3bc] print:text-black"}>{data.companion ? 'Ja (+1)' : 'Nein (Alleine)'}</b></div>
                                        <div className="flex justify-between text-sm py-1"><span className="text-[#a4b3bc]">Kontakt:</span><b className="text-[#f3efe6]">{data.contact || '-'}</b></div>
                                    </>
                                );
                            })()}
                        </div>
                    ) : r.template_type === 'differenzler' ? (
                        <div className="space-y-3">
                            <div className="w-full max-w-md bg-[#1a262c] border border-[#4f5f68]/40 rounded-xl p-4 shadow-sm print:bg-white print:border-gray-400 print:p-2">
                                <div className="font-bold text-[#f3efe6] mb-3 border-b border-[#4f5f68]/30 pb-2 flex justify-between items-center print:text-black">
                                    <span className="font-mono text-xs uppercase tracking-wider text-[#a4b3bc]">🎯 Differenzler-Wertung</span>
                                    <span className="bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40 px-2 py-0.5 rounded font-mono text-xs font-bold">
                                        Differenz: {Number(r.total_score).toFixed(1).replace(/\.0$/, '')} Pkt
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-sm py-1">
                                    <div className="bg-[#131d22] p-2.5 rounded-lg border border-[#4f5f68]/30">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc]">Ansage</div>
                                        <div className="text-lg font-mono font-bold text-[#f3efe6]">{r.estimated_score !== null && r.estimated_score !== undefined ? r.estimated_score : '-'}</div>
                                    </div>
                                    <div className="bg-[#131d22] p-2.5 rounded-lg border border-[#4f5f68]/30">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc]">Geschossen</div>
                                        <div className="text-lg font-mono font-bold text-[#f3efe6]">{r.actual_score !== null && r.actual_score !== undefined ? r.actual_score : '-'}</div>
                                    </div>
                                    <div className="bg-[#d97757]/15 p-2.5 rounded-lg border border-[#d97757]/30">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-[#d97757]">Differenz</div>
                                        <div className="text-lg font-mono font-black text-[#d97757]">{Number(r.total_score).toFixed(1).replace(/\.0$/, '')}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="font-mono text-xs uppercase tracking-wider text-[#a4b3bc]">Einzelschüsse:</div>
                            <div className="flex flex-wrap gap-2">
                                {(() => {
                                    try {
                                        const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                                        if (shots.length === 0) return <span className="text-[#a4b3bc] italic text-sm">Keine Einzelschüsse erfasst.</span>;
                                        return shots.map((s, idx) => (
                                            <div key={idx} className="border border-[#4f5f68]/40 bg-[#131d22] rounded-lg p-2 text-center shadow-xs w-16 print:border-gray-400 print:shadow-none print:p-1 print:w-12">
                                                <div className="text-[10px] font-mono text-[#a4b3bc] border-b border-[#4f5f68]/30 mb-1 pb-1">#{idx + 1}</div>
                                                <div className="font-mono font-bold text-[#f3efe6] print:text-black">{s.score}</div>
                                                {s.direction && <div className="text-xl leading-none mt-1 text-[#3a7d8a] print:text-sm">{s.direction}</div>}
                                            </div>
                                        ));
                                    } catch (e) { return <span className="text-red-400 text-sm">Fehler beim Laden.</span>; }
                                })()}
                            </div>
                        </div>
                    ) : (r.template_type === 'best_of' || r._bestOfCount) ? (
                        <div className="space-y-3">
                            {(() => {
                                try {
                                    const raw = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                                    if (!Array.isArray(raw) || raw.length === 0) return <span className="text-[#a4b3bc] italic text-sm">Keine Einzelschüsse erfasst.</span>;
                                    
                                    const bestCount = r._bestOfCount || 10;
                                    const parsed = raw.map((s, idx) => ({
                                        origIdx: idx,
                                        score: typeof s === 'object' && s !== null ? (parseFloat(s.score) || 0) : (parseFloat(s) || 0),
                                        direction: typeof s === 'object' && s !== null ? s.direction : '',
                                        rawScore: typeof s === 'object' && s !== null ? s.score : s
                                    }));
                                    
                                    const sorted = [...parsed].sort((a, b) => b.score - a.score);
                                    const topIndices = new Set(sorted.slice(0, bestCount).map(item => item.origIdx));
                                    const tieBreakerShot = sorted.length > bestCount ? sorted[bestCount] : null;
                                    const tieBreakerIdx = tieBreakerShot ? tieBreakerShot.origIdx : null;

                                    return (
                                        <>
                                            <div className="w-full bg-[#1a262c] border border-[#4f5f68]/40 rounded-xl p-3.5 shadow-sm print:bg-white print:border-gray-400 print:p-2 flex flex-wrap justify-between items-center gap-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs uppercase tracking-wider text-[#3a7d8a] font-bold">
                                                        🎯 Best-Of Wertung (Top {bestCount} von {raw.length} Schüssen gewertet)
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-3 text-xs font-mono">
                                                    <span className="flex items-center gap-1.5 text-emerald-300 font-semibold print:text-green-800">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block"></span>
                                                        ★ In Wertung ({bestCount} beste)
                                                    </span>
                                                    {tieBreakerShot && (
                                                        <span className="flex items-center gap-1.5 text-amber-300 font-bold print:text-amber-800">
                                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>
                                                            ⚡ {bestCount + 1}. Schuss (Gleichstandswertung)
                                                        </span>
                                                    )}
                                                    {raw.length > bestCount + 1 && (
                                                        <span className="flex items-center gap-1.5 text-[#a4b3bc]/70 print:text-gray-500">
                                                            <span className="w-2.5 h-2.5 rounded-full bg-[#4f5f68] inline-block"></span>
                                                            Streichresultat
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-2.5">
                                                {parsed.map((s) => {
                                                    const isTop = topIndices.has(s.origIdx);
                                                    const isTieBreaker = tieBreakerIdx === s.origIdx;
                                                    
                                                    let boxClass = "border border-[#4f5f68]/30 bg-[#121c21]/60 opacity-40 hover:opacity-100 transition-opacity w-16 print:w-12 print:border-gray-300";
                                                    let badgeText = "Streichung";
                                                    let badgeClass = "text-[#a4b3bc]/70";
                                                    let scoreClass = "text-[#a4b3bc] print:text-gray-500";

                                                    if (isTop) {
                                                        boxClass = "border-2 border-emerald-500/80 bg-emerald-500/15 shadow-md shadow-emerald-500/10 w-16 print:w-12 print:border-green-600 print:bg-green-50";
                                                        badgeText = `Top ${bestCount}`;
                                                        badgeClass = "bg-emerald-500/30 text-emerald-300 font-bold border border-emerald-500/40 print:text-green-900";
                                                        scoreClass = "text-emerald-300 font-black print:text-green-900";
                                                    } else if (isTieBreaker) {
                                                        boxClass = "border-2 border-amber-400 bg-amber-500/20 shadow-md shadow-amber-400/20 ring-2 ring-amber-400/40 w-20 print:w-14 print:border-amber-600 print:bg-yellow-50";
                                                        badgeText = `⚡ ${bestCount + 1}. Schuss`;
                                                        badgeClass = "bg-amber-400 text-black font-extrabold shadow-xs";
                                                        scoreClass = "text-amber-300 font-black print:text-amber-900";
                                                    }

                                                    return (
                                                        <div key={s.origIdx} className={`rounded-xl p-2 text-center shadow-xs transition-all ${boxClass}`}>
                                                            <div className="flex justify-between items-center text-[10px] font-mono border-b border-current/20 mb-1 pb-0.5">
                                                                <span className="text-[#a4b3bc] print:text-black font-bold">#{s.origIdx + 1}</span>
                                                                <span className={`text-[8px] px-1 py-0.2 rounded font-mono ${badgeClass}`}>{badgeText}</span>
                                                            </div>
                                                            <div className={`font-mono text-base ${scoreClass}`}>{s.rawScore}</div>
                                                            {s.direction && <div className={`text-sm leading-none mt-1 ${isTieBreaker ? 'text-amber-400 font-bold' : isTop ? 'text-emerald-400' : 'text-[#a4b3bc]/50'}`}>{s.direction}</div>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    );
                                } catch(e) { return <span className="text-red-400 text-sm">Fehler beim Laden.</span>; }
                            })()}
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {(() => {
                                try {
                                    const shots = typeof r.individual_scores === 'string' ? JSON.parse(r.individual_scores) : (r.individual_scores || []);
                                    if (shots.length === 0) return <span className="text-[#a4b3bc] italic text-sm">Keine Einzelschüsse erfasst.</span>;
                                    return shots.map((s, idx) => ( <div key={idx} className="border border-[#4f5f68]/40 bg-[#131d22] rounded-lg p-2 text-center shadow-xs w-16 print:border-gray-400 print:shadow-none print:p-1 print:w-12"><div className="text-[10px] font-mono text-[#a4b3bc] border-b border-[#4f5f68]/30 mb-1 pb-1">#{idx + 1}</div><div className="font-mono font-bold text-[#f3efe6] print:text-black">{s.score}</div>{s.direction && <div className="text-xl leading-none mt-1 text-[#3a7d8a] print:text-sm">{s.direction}</div>}</div> ));
                                } catch (e) { return <span className="text-red-400 text-sm">Fehler beim Laden.</span>; }
                            })()}
                        </div>
                    )}
                </td>
            </tr>
        );
    };
 
    const displayData = publicMode ? processedPublicData : processedData;
    const hasGruppenStich = useMemo(() => {
        return Object.values(displayData).some(g => g.isGruppenStich || g.isTeamGroup || g.type === 'gruppen_stich' || g.receipts?.some(r => r.isTeam));
    }, [displayData]);

    if (printViewActive) {
        const s = printSettings;
        const scope = s.groupScope || 'both';

        const filteredGroupKeys = Object.keys(displayData).filter(key => {
            const group = displayData[key];
            const isTeam = group.isTeamGroup || key.endsWith('_gruppe') || group.receipts?.some(r => r.isTeam);
            const isIndividual = group.isIndividualGroup || key.endsWith('_einzel');

            if (isTeam && scope === 'individuals') return false;
            if (isIndividual && scope === 'teams') return false;
            return true;
        });

        return createPortal(
            <div className={`fixed inset-0 bg-[#10171a] print:bg-white text-black font-sans min-h-screen z-[9999999] overflow-y-auto print-scoreboard-container ${s.fontSize}`}>
                <style>{`
                    @media print { 
                        nav, footer, .no-print, #root { display: none !important; } 
                        @page { size: A4 ${s.orientation}; margin: 10mm; } 
                        body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } 
                        .print-scoreboard-container { position: static !important; background: white !important; overflow: visible !important; width: 100% !important; height: auto !important; }
                        table { page-break-inside: auto; }
                        tr { page-break-inside: avoid; page-break-after: auto; }
                        * { overflow: visible !important; } 
                    }
                `}</style>
                <div className="no-print bg-[#162127] border-b border-[#4f5f68]/40 p-4 text-[#f3efe6] flex flex-wrap justify-between items-center shadow-2xl sticky top-0 z-[10000000] gap-4">
                    <span className="font-bold flex items-center gap-3 text-sm font-mono">
                        <span className="text-[#3a7d8a] font-bold tracking-wider">DRUCKVORSCHAU / PDF EXPORT</span>
                    </span>
                    <div className="flex flex-wrap items-center gap-3">
                        {hasGruppenStich && (
                            <div className="flex bg-[#131d22] border border-[#4f5f68]/40 rounded-xl p-1 text-xs font-mono">
                                <button 
                                    type="button" 
                                    onClick={() => setPrintSettings(prev => ({ ...prev, groupScope: 'both' }))} 
                                    className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${(s.groupScope || 'both') === 'both' ? 'bg-[#3a7d8a] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                                    title="Gruppen- und Einzelrangliste drucken"
                                >
                                    Beides
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setPrintSettings(prev => ({ ...prev, groupScope: 'teams' }))} 
                                    className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${s.groupScope === 'teams' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                                    title="Nur Gruppenrangliste drucken"
                                >
                                    Nur Gruppen
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setPrintSettings(prev => ({ ...prev, groupScope: 'individuals' }))} 
                                    className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${s.groupScope === 'individuals' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                                    title="Nur Einzelrangliste drucken"
                                >
                                    Nur Einzel
                                </button>
                            </div>
                        )}
                        {hasGruppenStich && s.groupScope !== 'individuals' && (
                            <button 
                                type="button" 
                                onClick={() => setPrintSettings(prev => ({ ...prev, showTeamMembers: !(prev.showTeamMembers ?? true) }))} 
                                className={`px-3 py-1.5 text-xs font-mono rounded-xl border transition cursor-pointer flex items-center gap-1.5 ${(s.showTeamMembers ?? true) ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/40 font-bold' : 'bg-[#131d22] text-[#a4b3bc] border-[#4f5f68]/40'}`}
                                title="Namen aller Schützen in den Gruppen anzeigen oder ausblenden"
                            >
                                👥 Schützen auflisten: {(s.showTeamMembers ?? true) ? 'AN' : 'AUS'}
                            </button>
                        )}
                        <div className="flex bg-[#131d22] border border-[#4f5f68]/40 rounded-xl p-1 text-xs font-mono">
                            <button 
                                type="button" 
                                onClick={() => setPrintSettings(prev => ({ ...prev, orientation: 'landscape' }))} 
                                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${s.orientation === 'landscape' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                            >
                                Querformat
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setPrintSettings(prev => ({ ...prev, orientation: 'portrait' }))} 
                                className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${s.orientation === 'portrait' ? 'bg-[#d97757] text-[#162127] font-bold shadow-xs' : 'text-[#a4b3bc] hover:text-[#f3efe6]'}`}
                            >
                                Hochformat
                            </button>
                        </div>
                        <button onClick={() => window.print()} className="flow-btn-coral px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer shadow-lg shadow-[#d97757]/30 hover:brightness-110 active:scale-95 transition-all"><Icons.Print/> Jetzt Drucken / Als PDF speichern</button>
                        <button onClick={() => setPrintViewActive(false)} className="flow-btn-ghost px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 cursor-pointer hover:bg-[#4f5f68]/20 transition-all"><Icons.Close/> Ansicht schliessen</button>
                    </div>
                </div>
                
                <div className="p-8 max-w-5xl mx-auto bg-white my-6 shadow-2xl print:shadow-none print:m-0 print:p-0 print:max-w-none">
                    <img src={logoWithNameURL} alt="Logo" className="h-16 mb-6 block object-contain" />
                    <h1 className="text-3xl font-bold mb-6 underline">Rangliste</h1>
                    {filteredGroupKeys.map(key => {
                        const group = displayData[key];
                        return (
                            <div key={key} className="mb-10">
                                <h2 className="text-2xl font-bold mb-4 border-b-2 border-black pb-1">
                                    {group.name}
                                </h2>
                                <table className="w-full text-left border-collapse border border-black">
                                    <thead>
                                        <tr className="bg-gray-200 border-b border-black">
                                            {s.showColumns.rank && <th className="p-2 border border-black w-12 text-center">Rang</th>}
                                            {s.showColumns.name && <th className="p-2 border border-black">Teilnehmer</th>}
                                            {s.showColumns.score && <th className="p-2 border border-black text-right">Resultat</th>}
                                            {s.showColumns.stich && <th className="p-2 border border-black">Stich/Detail</th>}
                                            {!publicMode && s.showColumns.group && <th className="p-2 border border-black">Gruppe</th>}
                                            {s.showColumns.date && <th className="p-2 border border-black">Datum</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.receipts.map((r, i) => (
                                            <React.Fragment key={r.id}>
                                                <tr className="border-b border-gray-400 align-top">
                                                    {s.showColumns.rank && <td className="p-2 border border-black text-center font-bold align-top">{r._isNoGroup || r._computedRank === '-' ? '-' : (r._computedRank ? `${r._computedRank}.` : `${i + 1}.`)}</td>}
                                                    {s.showColumns.name && (
                                                        <td className="p-2 border border-black font-semibold align-top">
                                                            {r.isTeam ? (
                                                                <div>
                                                                    <div className="font-bold text-black">{`Team: ${r.team_name}`}{r.isNoGroup ? ' (Ohne Wertung)' : ''}</div>
                                                                    {(s.showTeamMembers ?? true) && r.members && r.members.length > 0 && (
                                                                        <div className="mt-1.5 pt-1 border-t border-gray-300 font-normal">
                                                                            <div className="text-[10px] font-mono uppercase tracking-wider text-gray-600 font-bold mb-0.5">
                                                                                Schützen {r.bestOfCount > 0 ? `(Top ${r.bestOfCount} in Wertung)` : ''}:
                                                                            </div>
                                                                            <div className="space-y-0.5">
                                                                                {r.members.map((m, mIdx) => {
                                                                                    const inWertung = mIdx < (r.bestOfCount > 0 ? r.bestOfCount : r.members.length);
                                                                                    return (
                                                                                        <div key={m.id || mIdx} className="text-xs flex justify-between items-center text-gray-800 py-0.5 border-b border-gray-100 last:border-0">
                                                                                            <span className="flex items-center gap-1.5">
                                                                                                <span className="font-mono text-[10px] text-gray-500 w-4 inline-block">{mIdx + 1}.</span>
                                                                                                <span className="font-medium text-black">{m.participant_name}</span>
                                                                                                {!inWertung && <span className="text-[9px] bg-gray-200 border border-gray-300 px-1 rounded font-mono text-gray-600 italic">Streichresultat</span>}
                                                                                            </span>
                                                                                            <span className="font-mono font-bold text-black ml-3">{Number(m.total_score).toFixed(1).replace('.0', '')} Pkt</span>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (r.team_name && group.name.includes('Einzelwertung') ? `${r.participant_name} (${r.team_name})` : r.participant_name)}
                                                        </td>
                                                    )}
                                                    {s.showColumns.score && <td className="p-2 border border-black font-bold text-right align-top">{r.template_type === 'differenzler' ? `Diff: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}` : r.template_type === 'anmeldung' ? `${Number(r.total_score).toFixed(0)} Pers.` : Number(r.total_score).toFixed(2).replace(/\.00$/, '')}{r._tieResolvedByShot ? ` (⚡ ${r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss: ${r._tieBreakerScore})` : ''}</td>}
                                                    {s.showColumns.stich && <td className="p-2 border border-black">
                                                        {(() => {
                                                            if (group.type === 'differenzler') return `Ansage: ${r.estimated_score ?? '-'} | Ist: ${r.actual_score ?? '-'}`;
                                                            if (group.type === 'zick_zack') return r._rankedBy === 'total' ? '📊 Total' : `🎯 Tief: ${r._bestShot}`;
                                                            if (group.type === 'best_of' || r.template_type === 'best_of' || r._bestOfCount) {
                                                                let txt = `Top ${r._bestOfCount || group.bestOfCount || 10} gewertet`;
                                                                if (r._tieResolvedByShot) {
                                                                    txt += ` | ⚡ ${r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss: ${r._tieBreakerScore} Pkt`;
                                                                }
                                                                return txt;
                                                            }
                                                            return (r.folder_name || r.stich_name || '-') + (r._tiefInfo ? ` | ${r._tiefInfo}` : '');
                                                        })()}
                                                    </td>}
                                                    {!publicMode && s.showColumns.group && <td className="p-2 border border-black">{r.group_name || '-'}</td>}
                                                    {s.showColumns.date && <td className="p-2 border border-black">{new Date(r.timestamp).toLocaleDateString()}</td>}
                                                </tr>
                                                {s.showDetails && renderDetailsRow(r, Object.values(s.showColumns).filter(Boolean).length + (!publicMode && s.showColumns.group ? 1 : 0))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            </div>,
            document.body
        );
    }

    return ( <div className="p-2 sm:p-4 lg:p-6 w-full max-w-[1920px] mx-auto">
        <ScoreboardPrintModal isOpen={printModalOpen} onClose={() => setPrintModalOpen(false)} printSettings={printSettings} setPrintSettings={setPrintSettings} hasGruppenStich={hasGruppenStich} onPrint={() => { setPrintModalOpen(false); setPrintViewActive(true); }} />
        {!publicMode && <TieBreakerModal isOpen={tieModalState.isOpen} onClose={() => setTieModalState({ isOpen: false, score: null, receipts: [] })} tiedGroup={tieModalState} user={user} showMessage={showMessage} onDataChange={(newRule) => { if (newRule) handleTieBreakerChange(newRule, tieModalState.receipts[0]?.folder_id); fetchReceipts(true); }} t={t} />}
        <QRCodeModal isOpen={!!qrModalData} onClose={() => setQrModalData(null)} targetData={qrModalData} t={t} />
        
        <div className="flow-card p-4 sm:p-6 md:p-8 w-full mb-8 shadow-2xl">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 border-b border-[#4f5f68]/30 pb-5 gap-4">
                <div>
                    <span className="font-mono text-xs uppercase tracking-widest text-[#d97757] block mb-1">
                        {publicMode ? 'Live Rangliste' : 'Live Standings'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[#f3efe6] tracking-tight flex items-center flex-wrap gap-2">
                        {publicMode ? (publicGroupInfo?.name || receipts[0]?.group_name || t('scoreboard')) : t('scoreboard')}
                        {publicFolderInfo?.name && (
                            <span className="text-xl md:text-2xl text-[#8ec8d4] font-normal font-sans">
                                › {publicFolderInfo.name}
                            </span>
                        )}
                    </h2>
                    {publicMode && publicGroupId && (
                        <span className="text-xs font-mono text-[#a4b3bc] mt-1 block">
                            Gruppe: <b>{publicGroupInfo?.name || receipts[0]?.group_name || publicGroupId}</b>
                            {publicFolderInfo?.name ? ` • Stich/Ordner: ${publicFolderInfo.name}` : ''}
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-3">
                    {publicMode && publicGroupId && (
                        <button 
                            onClick={() => setQrModalData({
                                groupId: publicGroupId,
                                groupName: publicGroupInfo?.name || receipts[0]?.group_name,
                                folderId: activePublicFolderId,
                                folderName: publicFolderInfo?.name
                            })} 
                            className="bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 font-semibold py-2 px-4 rounded-xl text-sm shadow-sm flex items-center gap-2 transition cursor-pointer"
                            title={t('qrCodeFlyer', 'QR-Code & Aushang generieren')}
                        >
                            📱 {t('qrCode', 'QR-Code')}
                        </button>
                    )}
                    <button onClick={handleExcelExport} className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 font-semibold py-2 px-4 rounded-xl text-sm shadow-sm flex items-center gap-2 transition cursor-pointer">📊 {t('excelExport', 'Excel Export')}</button>
                    <button onClick={() => setPrintModalOpen(true)} className="bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 font-semibold py-2 px-4 rounded-xl text-sm shadow-sm flex items-center gap-2 transition cursor-pointer">🖨️ {t('printExport', 'Export / Drucken')}</button>
                </div>
            </div>
            
            {publicMode && (
                <div className="mb-6">
                    <input type="text" placeholder={t('searchPlaceholder', 'Suche...')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="flow-input w-full"/>
                </div>
            )}
            {!publicMode && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 bg-[#131d22]/70 p-5 rounded-2xl border border-[#4f5f68]/30">
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('filterGroup', 'Gruppe filtern')}</label>
                        <select value={filterGroupId} onChange={e => {setFilterGroupId(e.target.value); setFilterFolderId('');}} className="flow-input w-full text-sm">
                            <option value="">{t('allGroups', 'Alle Gruppen')}</option>
                            {userGroups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('filterByFolder', 'Ordner filtern')}</label>
                        <select value={filterFolderId} onChange={e => setFilterFolderId(e.target.value)} className="flow-input w-full text-sm">
                            <option value="">{t('allFolders', 'Alle Ordner')}</option>
                            {renderFolderOptions(scoreboardFolderTree)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('tieBreakerProcedure', 'Gleichstand (Vorgehen)')}</label>
                        <select value={tieBreakerRule} onChange={e => handleTieBreakerChange(e.target.value)} className="flow-input w-full text-sm font-medium">
                            <option value="shots">{t('tieRuleShots', '🎯 Tiefschuss (bester Schuss)')}</option>
                            <option value="older">{t('tieRuleOlder', '👴 Ältere bevorzugt')}</option>
                            <option value="younger">{t('tieRuleYounger', '👶 Jüngere bevorzugt')}</option>
                            <option value="last_shot">{t('tieRuleLastShot', '🎯 Letzter Schuss zuerst')}</option>
                            <option value="shared">{t('tieRuleShared', '🤝 Gleicher Rang (ex aequo)')}</option>
                            <option value="manual">{t('tieRuleManual', '✍️ Manuell auswählen')}</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('dateFrom', 'Datum von')}</label>
                        <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="flow-input w-full text-sm"/>
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('dateUntil', 'Datum bis')}</label>
                        <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="flow-input w-full text-sm"/>
                    </div>
                </div>
            )}
            <div className="overflow-x-auto rounded-xl border border-[#4f5f68]/30 bg-[#162127]/60">
                <table className="min-w-full divide-y divide-[#4f5f68]/30">
                    <thead className="bg-[#131d22] border-b border-[#4f5f68]/30">
                        <tr>
                            <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc] w-20">{t('rank', 'Rang')}</th>
                            <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('participant', 'Teilnehmer')}</th>
                            <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('pointsOrPersons', 'Punkte/Personen')}</th>
                            <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('stichOrDetail', 'Stich / Detail')}</th>
                            {!publicMode && <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('groupNameColumn', 'Gruppe')}</th>}
                            <th className="px-6 py-3.5 text-left font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('captureDate', 'Datum')}</th>
                            {!publicMode && <th className="px-6 py-3.5 text-center font-mono text-xs uppercase tracking-widest text-[#a4b3bc]">{t('actions', 'Aktionen')}</th>}
                        </tr>
                    </thead>
                     {publicMode ? (
                        <tbody className="divide-y divide-[#4f5f68]/20">
                            {Object.keys(processedPublicData).length === 0 ? (<tr><td colSpan="5" className="text-center py-12 text-[#a4b3bc] font-mono text-sm">{t('noDataAvailable', 'Keine Daten verfügbar')}</td></tr>)
                            : Object.keys(processedPublicData).map(key => {
                                const group = processedPublicData[key];
                                return (
                                 <React.Fragment key={key}>
                                     <tr className="bg-[#1a262c] border-y border-[#4f5f68]/30">
                                         <th colSpan="5" className="px-6 py-3.5 text-left font-semibold text-[#f3efe6]">
                                             <div className="flex justify-between items-center font-mono text-sm tracking-wide">
                                                 <div className="flex items-center flex-wrap gap-2">
                                                     <span className="text-[#d97757] text-base">📁</span>
                                                     <span className="font-bold text-[#f3efe6] text-sm md:text-base tracking-normal">{group.name}</span>
                                                 </div>
                                                 {group.receipts?.length > 0 && (
                                                     <span className="text-xs text-[#a4b3bc] font-mono hidden sm:inline">
                                                         {group.receipts.length} {group.receipts.length === 1 ? 'Resultat' : 'Resultate'}
                                                     </span>
                                                 )}
                                             </div>
                                         </th>
                                     </tr>
                                     {group.receipts.map((r, i) => {
                                         const isExpanded = expandedRow === r.id;
                                         const rankVal = r._isNoGroup || r._computedRank === '-' ? '-' : (r._computedRank !== undefined ? r._computedRank : (i + 1));
                                         return (
                                         <React.Fragment key={r.id}>
                                             <tr className="hover:bg-[#1f303a]/50 cursor-pointer transition-colors border-b border-[#4f5f68]/20" onClick={() => setExpandedRow(isExpanded ? null : r.id)}>
                                                 <td className="px-6 py-4 whitespace-nowrap">
                                                     {rankVal === '-' || r._isNoGroup ? (
                                                         <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/40 font-mono font-bold text-xs" title="Außer Konkurrenz / Letzter Platz">-</span>
                                                     ) : rankVal === 1 ? (
                                                         <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 font-mono font-black text-xs shadow-xs">1</span>
                                                     ) : rankVal === 2 ? (
                                                         <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-300/20 text-slate-200 border border-slate-300/40 font-mono font-black text-xs shadow-xs">2</span>
                                                     ) : rankVal === 3 ? (
                                                         <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#d97757]/20 text-[#d97757] border border-[#d97757]/40 font-mono font-black text-xs shadow-xs">3</span>
                                                     ) : (
                                                         <span className="font-mono font-bold text-[#a4b3bc] text-sm px-1.5">{rankVal}.</span>
                                                     )}
                                                 </td>
                                                 <td className="px-6 py-4 font-semibold text-[#f3efe6] whitespace-nowrap">
                                                     {r.isTeam ? (
                                                         <div className="flex items-center gap-2">
                                                             <span>Team: {r.team_name}</span>
                                                             {r.isNoGroup && <span className="text-[10px] font-mono uppercase bg-[#4f5f68]/30 text-[#a4b3bc] border border-[#4f5f68]/50 px-2 py-0.5 rounded font-normal">Ohne Wertung</span>}
                                                         </div>
                                                     ) : r.participant_name}
                                                 </td>
                                                 <td className="px-6 py-4 whitespace-nowrap">
                                                     <div className="flex flex-col">
                                                         <span className="font-mono font-bold text-[#f3efe6] text-base">
                                                             {r.template_type === 'differenzler' ? `Diff: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}` : r.template_type === 'anmeldung' ? `${Number(r.total_score).toFixed(0)} Pers.` : Number(r.total_score).toFixed(2).replace(/\.00$/, '')}
                                                         </span>
                                                         {r._tieResolvedByShot && (
                                                             <span className="text-[11px] font-mono text-amber-300 flex items-center gap-1 font-bold">
                                                                 ⚡ {r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss: {r._tieBreakerScore} Pkt
                                                             </span>
                                                         )}
                                                     </div>
                                                 </td>
                                                <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                    {r.template_type === 'differenzler' ? (
                                                        <div className="flex flex-col gap-0.5 items-start">
                                                            <span className="bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40 px-2 py-0.5 rounded text-xs font-mono font-semibold">{r.folder_name || r.stich_name || 'Differenzler'}</span>
                                                            <span className="text-xs text-[#a4b3bc] font-mono">Ansage: <b className="text-[#f3efe6]">{r.estimated_score !== null && r.estimated_score !== undefined ? r.estimated_score : '-'}</b> | Ist: <b className="text-[#f3efe6]">{r.actual_score !== null && r.actual_score !== undefined ? r.actual_score : '-'}</b></span>
                                                        </div>
                                                    ) : group.type === 'zick_zack' ? (
                                                        <div className="flex flex-col gap-1 items-start">
                                                            {(r.folder_name || r.stich_name) && <span className="bg-[#4f5f68]/20 text-[#f3efe6] px-2 py-0.5 rounded text-xs border border-[#4f5f68]/30 font-mono">{r.folder_name || r.stich_name}</span>}
                                                            <span className={`text-xs px-2 py-0.5 rounded font-mono font-bold uppercase ${r._rankedBy === 'total' ? 'bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40' : 'bg-[#d97757]/20 text-[#d97757] border border-[#d97757]/40'}`}>{r._rankedBy === 'total' ? '📊 Gewertet: Total' : `🎯 Tiefschuss: ${r._bestShot}`}</span>
                                                        </div>
                                                    ) : (group.type === 'best_of' || r.template_type === 'best_of' || r._bestOfCount) ? (
                                                        <div className="flex flex-col gap-1 items-start">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                {(r.folder_name || r.stich_name) && <span className="bg-[#4f5f68]/20 text-[#f3efe6] border border-[#4f5f68]/40 px-2 py-0.5 rounded text-xs font-mono">{r.folder_name || r.stich_name}</span>}
                                                                <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded text-xs font-mono font-bold">Top {r._bestOfCount || group.bestOfCount || 10}</span>
                                                            </div>
                                                            {r._tieResolvedByShot ? (
                                                                <span className="text-xs text-amber-300 font-mono font-bold flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded">
                                                                    ⚡ {r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss ({r._tieBreakerScore} Pkt) hat Gleichstand gelöst
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-[#a4b3bc] font-mono">Beste {r._bestOfCount || group.bestOfCount || 10} Schüsse</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-1 items-start">
                                                            {(r.folder_name || r.stich_name) ? <span className="bg-[#4f5f68]/20 text-[#f3efe6] border border-[#4f5f68]/40 px-2 py-0.5 rounded text-xs font-mono">{r.folder_name || r.stich_name}</span> : <span className="text-[#a4b3bc]">-</span>}
                                                            {r._tiefInfo && <span className="text-xs text-[#a4b3bc] font-mono">{r._tiefInfo}</span>}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-mono text-[#a4b3bc] whitespace-nowrap"><div className="flex items-center gap-2">{new Date(r.timestamp).toLocaleDateString()} <span className="text-[#a4b3bc]/60 text-xs">{isExpanded ? '▲' : '▼'}</span></div></td>
                                            </tr>
                                            {isExpanded && renderDetailsRow(r, 5)}
                                        </React.Fragment>);
                                    })}
                                </React.Fragment>
                            )})}
                        </tbody>
                    ) : (
                    <tbody className="divide-y divide-[#4f5f68]/20">
                        {Object.keys(processedData).length === 0 ? (<tr><td colSpan="7" className="text-center py-12 text-[#a4b3bc] font-mono text-sm">{t('noDataAvailable', 'Keine Daten verfügbar')}</td></tr>)
                        : Object.keys(processedData).map(key => {
                            const group = processedData[key];
                            return (
                            <React.Fragment key={key}>
                                <tr className="bg-[#1a262c] border-y border-[#4f5f68]/30">
                                    <th colSpan="7" className="px-6 py-3 text-left font-semibold text-[#f3efe6]">
                                        <div className="flex justify-between items-center font-mono text-sm tracking-wide">
                                            <div className="flex items-center flex-wrap gap-2">
                                                <span>{group.name}</span>
                                            </div>
                                            {hasAnyTie(group.receipts) && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); openTieModalForGroup(group); }}
                                                    className="text-xs bg-[#3a7d8a]/15 hover:bg-[#3a7d8a]/25 text-[#8ec8d4] border border-[#3a7d8a]/40 font-mono px-3 py-1 rounded-full shadow-xs flex items-center gap-1.5 transition cursor-pointer"
                                                    title="Gleichstand in dieser Wertung manuell anpassen"
                                                >
                                                    ✍️ Gleichstand anpassen ({countTies(group.receipts)})
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                </tr>
                                {group.receipts.map((r, i) => {
                                    const isOwner = user?.id === r.leader_id; const isGroupLeader = userGroups?.find(g => g.id === r.group_id)?.created_by === user?.id; const isExpanded = expandedRow === r.id;
                                    const rankVal = r._isNoGroup || r._computedRank === '-' ? '-' : (r._computedRank !== undefined ? r._computedRank : (i + 1));
                                    return (
                                    <React.Fragment key={r.id}>
                                        <tr className="hover:bg-[#1f303a]/50 cursor-pointer transition-colors border-b border-[#4f5f68]/20" onClick={() => setExpandedRow(isExpanded ? null : r.id)}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {rankVal === '-' || r._isNoGroup ? (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#4f5f68]/20 text-[#a4b3bc] border border-[#4f5f68]/40 font-mono font-bold text-xs" title="Außer Konkurrenz / Letzter Platz">-</span>
                                                    ) : rankVal === 1 ? (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 font-mono font-black text-xs shadow-xs">1</span>
                                                    ) : rankVal === 2 ? (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-300/20 text-slate-200 border border-slate-300/40 font-mono font-black text-xs shadow-xs">2</span>
                                                    ) : rankVal === 3 ? (
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#d97757]/20 text-[#d97757] border border-[#d97757]/40 font-mono font-black text-xs shadow-xs">3</span>
                                                    ) : (
                                                        <span className="font-mono font-bold text-[#a4b3bc] text-sm min-w-[20px] inline-block text-center">{rankVal}.</span>
                                                    )}
                                                    {r._isTie && !publicMode && !r._isNoGroup && (
                                                        <div className="flex flex-col no-print" onClick={e => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                disabled={i === 0 || parseFloat(group.receipts[i - 1]?.total_score) !== parseFloat(r.total_score) || group.receipts[i - 1]?._isNoGroup}
                                                                onClick={(e) => { e.stopPropagation(); handleMoveReceipt(r, 'up', group.receipts, i); }}
                                                                className={`leading-none text-[10px] px-1 py-0.5 rounded font-bold transition ${
                                                                    i > 0 && parseFloat(group.receipts[i - 1]?.total_score) === parseFloat(r.total_score) && !group.receipts[i - 1]?._isNoGroup
                                                                        ? 'bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 cursor-pointer'
                                                                        : 'text-[#4f5f68] cursor-not-allowed'
                                                                }`}
                                                                title="Bei Gleichstand 1 Rang nach oben verschieben"
                                                            >
                                                                ▲
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={i === group.receipts.length - 1 || parseFloat(group.receipts[i + 1]?.total_score) !== parseFloat(r.total_score) || group.receipts[i + 1]?._isNoGroup}
                                                                onClick={(e) => { e.stopPropagation(); handleMoveReceipt(r, 'down', group.receipts, i); }}
                                                                className={`leading-none text-[10px] px-1 py-0.5 rounded font-bold transition ${
                                                                    i < group.receipts.length - 1 && parseFloat(group.receipts[i + 1]?.total_score) === parseFloat(r.total_score) && !group.receipts[i + 1]?._isNoGroup
                                                                        ? 'bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 cursor-pointer'
                                                                        : 'text-[#4f5f68] cursor-not-allowed'
                                                                }`}
                                                                title="Bei Gleichstand 1 Rang nach unten verschieben"
                                                            >
                                                                ▼
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 font-semibold text-[#f3efe6] whitespace-nowrap">
                                                {r.isTeam ? (
                                                    <div className="flex items-center gap-2">
                                                        <span>Team: {r.team_name}</span>
                                                        {r.isNoGroup && <span className="text-[10px] font-mono uppercase bg-[#4f5f68]/30 text-[#a4b3bc] border border-[#4f5f68]/50 px-2 py-0.5 rounded font-normal">Ohne Wertung</span>}
                                                    </div>
                                                ) : (r.team_name && group.name.includes('Einzelwertung') ? `${r.participant_name} (${r.team_name})` : r.participant_name)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    {r._isTie ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); openTieModalForScore(r.total_score, group.receipts); }}
                                                            className="font-mono font-bold text-[#f3efe6] hover:text-[#d97757] transition-colors cursor-pointer text-base text-left"
                                                            title="Klicken, um Rangierung manuell festzulegen"
                                                        >
                                                            {r.template_type === 'differenzler' ? `Diff: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}` : r.template_type === 'anmeldung' ? `${Number(r.total_score).toFixed(0)} Pers.` : Number(r.total_score).toFixed(2).replace(/\.00$/, '')}
                                                        </button>
                                                    ) : (
                                                        <span className="font-mono font-bold text-[#f3efe6] text-base">
                                                            {r.template_type === 'differenzler' ? `Diff: ${Number(r.total_score).toFixed(1).replace(/\.0$/, '')}` : r.template_type === 'anmeldung' ? `${Number(r.total_score).toFixed(0)} Pers.` : Number(r.total_score).toFixed(2).replace(/\.00$/, '')}
                                                        </span>
                                                    )}
                                                    {r._tieResolvedByShot && (
                                                        <span className="text-[11px] font-mono text-amber-300 flex items-center gap-1 font-bold">
                                                            ⚡ {r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss: {r._tieBreakerScore} Pkt
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                {r.template_type === 'differenzler' ? (
                                                    <div className="flex flex-col gap-0.5 items-start">
                                                        <span className="bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40 px-2 py-0.5 rounded text-xs font-mono font-semibold">{r.folder_name || r.stich_name || 'Differenzler'}</span>
                                                        <span className="text-xs text-[#a4b3bc] font-mono">Ansage: <b className="text-[#f3efe6]">{r.estimated_score !== null && r.estimated_score !== undefined ? r.estimated_score : '-'}</b> | Ist: <b className="text-[#f3efe6]">{r.actual_score !== null && r.actual_score !== undefined ? r.actual_score : '-'}</b></span>
                                                    </div>
                                                ) : group.type === 'zick_zack' ? (
                                                    <div className="flex flex-col gap-1 items-start">
                                                        {(r.folder_name || r.stich_name) && <span className="bg-[#4f5f68]/20 text-[#f3efe6] px-2 py-0.5 rounded text-xs border border-[#4f5f68]/30 font-mono">{r.folder_name || r.stich_name}</span>}
                                                        <span className={`text-xs px-2 py-0.5 rounded font-mono font-bold uppercase ${r._rankedBy === 'total' ? 'bg-[#3a7d8a]/20 text-[#8ec8d4] border border-[#3a7d8a]/40' : 'bg-[#d97757]/20 text-[#d97757] border border-[#d97757]/40'}`}>{r._rankedBy === 'total' ? '📊 Gewertet: Total' : `🎯 Tiefschuss: ${r._bestShot}`}</span>
                                                    </div>
                                                ) : (group.type === 'best_of' || r.template_type === 'best_of' || r._bestOfCount) ? (
                                                    <div className="flex flex-col gap-1 items-start">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            {(r.folder_name || r.stich_name) && <span className="bg-[#4f5f68]/20 text-[#f3efe6] border border-[#4f5f68]/40 px-2 py-0.5 rounded text-xs font-mono">{r.folder_name || r.stich_name}</span>}
                                                            <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded text-xs font-mono font-bold">Top {r._bestOfCount || group.bestOfCount || 10}</span>
                                                        </div>
                                                        {r._tieResolvedByShot ? (
                                                            <span className="text-xs text-amber-300 font-mono font-bold flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded">
                                                                ⚡ {r._decidingShotNum || ((r._bestOfCount || 10) + 1)}. Schuss ({r._tieBreakerScore} Pkt) hat Gleichstand gelöst
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-[#a4b3bc] font-mono">Beste {r._bestOfCount || group.bestOfCount || 10} Schüsse</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-1 items-start">
                                                        {(r.folder_name || r.stich_name) ? <span className="bg-[#4f5f68]/20 text-[#f3efe6] border border-[#4f5f68]/40 px-2 py-0.5 rounded text-xs font-mono">{r.folder_name || r.stich_name}</span> : <span className="text-[#a4b3bc]">-</span>}
                                                        {r._tiefInfo && <span className="text-xs text-[#a4b3bc] font-mono">{r._tiefInfo}</span>}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-mono text-[#a4b3bc] whitespace-nowrap">{r.group_name || '-'}</td>
                                            <td className="px-6 py-4 text-sm font-mono text-[#a4b3bc] whitespace-nowrap"><div className="flex items-center gap-2">{new Date(r.timestamp).toLocaleDateString()} <span className="text-[#a4b3bc]/60 text-xs">{isExpanded ? '▲' : '▼'}</span></div></td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {!r.isTeam && (
                                                    <div className="flex items-center justify-center gap-3">
                                                        {(isOwner || isGroupLeader) && <button onClick={(e) => { e.stopPropagation(); onEditReceipt(r); }} className="text-[#3a7d8a] hover:text-[#8ec8d4] transition p-1 cursor-pointer"><Icons.Edit/></button>}
                                                        {(isOwner || isGroupLeader) && <button onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }} className="text-red-400 hover:text-red-300 transition p-1 cursor-pointer"><Icons.Delete/></button>}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        {isExpanded && renderDetailsRow(r, 7)}
                                    </React.Fragment>
                                )})}
                            </React.Fragment>
                        )})}
                    </tbody>
                    )}
                </table>
            </div>
        </div>
    </div> );
};

const MemberManagerModal = ({ groupId, user, showMessage, requestConfirm, onClose, t }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMembers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members`, { headers: { 'Authorization': `Bearer ${user.token}` } });
            if (response.ok) { setMembers(await response.json()); } 
        } catch (error) { console.error(error); } finally { setLoading(false); }
    }, [groupId, user.token]);

    useEffect(() => { fetchMembers(); }, [fetchMembers]);

    const handleRemove = async (memberId) => {
        requestConfirm('Mitglied entfernen?', async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${memberId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
                if (response.ok) { showMessage('Entfernt', 'success'); fetchMembers(); } 
            } catch (error) { console.error(error); }
        });
    };

    const handlePermissionChange = async (memberId, field, value) => {
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${groupId}/members/${memberId}/permissions`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: JSON.stringify({ field, value })
            });
            if (res.ok) fetchMembers();
        } catch(error) { console.error(error); }
    };

    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
                <div className="flex justify-between items-center border-b border-[#4f5f68]/30 pb-4 mb-6">
                    <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">👥 Mitglieder & Rechte verwalten</h3>
                    <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition"><Icons.Close /></button>
                </div>
                {loading ? ( <p className="font-mono text-sm text-[#a4b3bc]">Lädt...</p> ) : (
                    <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                        {members.length > 0 ? members.map(member => (
                            <li key={member.id} className="p-4 bg-[#131d22]/80 border border-[#4f5f68]/30 rounded-xl shadow-xs">
                                <div className="flex justify-between items-center border-b border-[#4f5f68]/20 pb-2.5 mb-3">
                                    <span className="font-bold text-[#f3efe6] text-base">{member.email}</span>
                                    {member.id !== user.id && ( <button onClick={() => handleRemove(member.id)} className="text-red-400 bg-red-500/15 hover:bg-red-500/25 p-1.5 rounded-lg transition cursor-pointer"><Icons.Delete/></button> )}
                                </div>
                                {member.id !== user.id ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#f3efe6] hover:bg-[#4f5f68]/20 p-1.5 rounded-lg transition"><input type="checkbox" checked={!!member.can_capture} onChange={(e) => handlePermissionChange(member.id, 'can_capture', e.target.checked)} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757]"/>🔫 Erfassen</label>
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#f3efe6] hover:bg-[#4f5f68]/20 p-1.5 rounded-lg transition"><input type="checkbox" checked={!!member.can_manage_events} onChange={(e) => handlePermissionChange(member.id, 'can_manage_events', e.target.checked)} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757]"/>📅 Events</label>
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#f3efe6] hover:bg-[#4f5f68]/20 p-1.5 rounded-lg transition"><input type="checkbox" checked={!!member.can_manage_folders} onChange={(e) => handlePermissionChange(member.id, 'can_manage_folders', e.target.checked)} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757]"/>📁 Ordner</label>
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#f3efe6] hover:bg-[#4f5f68]/20 p-1.5 rounded-lg transition"><input type="checkbox" checked={!!member.can_manage_members} onChange={(e) => handlePermissionChange(member.id, 'can_manage_members', e.target.checked)} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757]"/>👥 Mitglieder</label>
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono text-[#f3efe6] hover:bg-[#4f5f68]/20 p-1.5 rounded-lg transition"><input type="checkbox" checked={!!member.can_change_password} onChange={(e) => handlePermissionChange(member.id, 'can_change_password', e.target.checked)} className="w-4 h-4 rounded text-[#d97757] focus:ring-[#d97757]"/>🔑 Passwort</label>
                                    </div>
                                ) : <div className="text-xs font-mono text-[#3a7d8a] italic">Inhaber mit vollen Rechten.</div>}
                            </li>
                        )) : <p className="font-mono text-sm text-[#a4b3bc]">Keine Mitglieder gefunden.</p>}
                    </ul>
                )}
                <button onClick={onClose} className="flow-btn-ghost mt-6 w-full py-2.5 text-sm cursor-pointer"><Icons.Close/> Schliessen</button>
            </div>
        </div>
    );
};

const FolderTreeItem = ({ folder, onShare, onDelete, onTieBreaker, onQRCode, t, level = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = folder.children && folder.children.length > 0;
    return ( <li className="my-1.5">
            <div style={{ marginLeft: `${level * 20}px` }} className="flex justify-between items-center p-2.5 bg-[#131d22]/80 border border-[#4f5f68]/30 rounded-xl text-[#f3efe6]">
                <div className="flex items-center">{hasChildren && ( <button onClick={() => setIsOpen(!isOpen)} className="mr-2 px-2 font-mono text-[#3a7d8a] hover:text-[#8ec8d4]"> {isOpen ? '[-]' : '[+]'} </button> )}<span className={!hasChildren ? 'ml-8' : ''}>{folder.name}</span></div>
                <div className="flex items-center space-x-2.5">
                    <button type="button" onClick={() => onQRCode && onQRCode(folder)} title="QR-Code für diesen Ordner generieren" className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded-lg transition cursor-pointer text-xs">
                        📱
                    </button>
                    <select value={folder.tie_breaker_rule || 'shots'} onChange={(e) => onTieBreaker(folder, e.target.value)} className="flow-input text-xs py-1 px-2 font-mono" title="Gleichstand-Regel">
                        <option value="shots">🎯 Tiefschuss</option>
                        <option value="older">👴 Ältere bevorzugt</option>
                        <option value="younger">👶 Jüngere bevorzugt</option>
                        <option value="last_shot">🎯 Letzter Schuss</option>
                        <option value="shared">🤝 Gleicher Rang</option>
                        <option value="manual">✍️ Manuell</option>
                    </select>
                    <input type="checkbox" checked={!!folder.is_public} onChange={() => onShare(folder)} title="Freigeben" className="h-4 w-4 rounded text-[#d97757] focus:ring-[#d97757] cursor-pointer"/>
                    <button onClick={() => onDelete(folder)} className="text-red-400 hover:text-red-300 p-1 cursor-pointer"><Icons.Delete/></button>
                </div>
            </div>
            {hasChildren && isOpen && ( <ul className="pl-4 border-l-2 border-[#4f5f68]/30 mt-1"> {folder.children.map(child => ( <FolderTreeItem key={child.id} folder={child} onShare={onShare} onDelete={onDelete} onTieBreaker={onTieBreaker} onQRCode={onQRCode} t={t} level={level + 1} /> ))} </ul> )}
        </li> );
};

const FolderManagerModal = ({ groupId, user, showMessage, requestConfirm, onDataChange, onClose, t }) => {
    const [folders, setFolders] = useState([]);
    const [newFolderName, setNewFolderName] = useState('');
    const [parentFolderId, setParentFolderId] = useState('');
    const [qrModalData, setQrModalData] = useState(null);

    const fetchFolders = useCallback(async () => {
        const res = await fetch(`${API_BASE_URL}/folders`, { headers: { 'Authorization': `Bearer ${user.token}` } });
        if (res.ok) setFolders((await res.json()).filter(f => f.group_id === groupId));
    }, [groupId, user.token]);
    useEffect(() => { fetchFolders(); }, [fetchFolders]);
    
    const folderTree = useMemo(() => {
        const tree = []; const map = {};
        folders.forEach(folder => { map[folder.id] = { ...folder, children: [] }; });
        folders.forEach(folder => { if (folder.parent_id && map[folder.parent_id]) { map[folder.parent_id].children.push(map[folder.id]); } else { tree.push(map[folder.id]); } });
        return tree;
    }, [folders]);

    const handleCreate = async (e) => {
        e.preventDefault();
        const res = await fetch(`${API_BASE_URL}/folders`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ name: newFolderName, group_id: groupId, parent_id: parentFolderId || null }) });
        if(res.ok) { onDataChange(); fetchFolders(); setNewFolderName(''); setParentFolderId(''); }
    };
    const handleShare = async (folder) => {
        await fetch(`${API_BASE_URL}/folders/${folder.id}/share`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ is_public: !folder.is_public }) });
        onDataChange(); fetchFolders();
    };
    const handleTieBreaker = async (folder, rule) => {
        await fetch(`${API_BASE_URL}/folders/${folder.id}/tie_breaker`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ tie_breaker_rule: rule }) });
        onDataChange(); fetchFolders();
    };
    const handleDelete = async (folder) => {
        requestConfirm(`Ordner "${folder.name}" wirklich löschen?`, async () => {
            const res = await fetch(`${API_BASE_URL}/folders/${folder.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${user.token}` } });
            if (res.ok) { showMessage('Ordner gelöscht', 'success'); onDataChange(); fetchFolders(); }
        });
    };
    return ( <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
        <QRCodeModal isOpen={!!qrModalData} onClose={() => setQrModalData(null)} targetData={qrModalData} t={t} />
        <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-lg shadow-2xl animate-scale-in">
            <div className="flex justify-between items-center border-b border-[#4f5f68]/30 pb-3 mb-5">
                <h3 className="text-xl md:text-2xl font-bold text-[#f3efe6] flex items-center gap-2">📁 Ordner verwalten</h3>
                <button onClick={onClose} className="text-[#a4b3bc] hover:text-[#f3efe6] p-1.5 rounded-lg hover:bg-[#4f5f68]/20 transition"><Icons.Close /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3 mb-5">
                <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Ordnername</label><input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} required className="flow-input w-full" /></div>
                <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">Übergeordneter Ordner</label><select value={parentFolderId} onChange={e => setParentFolderId(e.target.value)} className="flow-input w-full text-sm"><option value="">Keiner</option>{folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                <button type="submit" className="flow-btn-coral w-full py-2.5 text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer"><Icons.Add/> Erstellen</button>
            </form>
            <div className="mb-3 p-3 bg-[#3a7d8a]/15 text-[#8ec8d4] text-xs font-mono rounded-xl border border-[#3a7d8a]/30">Ordner für öffentliche Rangliste freigeben (Checkbox) • QR-Code (📱)</div>
            <ul className="space-y-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                {folderTree.map(folder => (
                    <FolderTreeItem 
                        key={folder.id} 
                        folder={folder} 
                        onShare={handleShare} 
                        onDelete={handleDelete} 
                        onTieBreaker={handleTieBreaker} 
                        onQRCode={(f) => setQrModalData({ groupId, groupName: 'Gruppe', folderId: f.id, folderName: f.name })}
                        t={t} 
                    />
                ))}
            </ul>
            <button onClick={onClose} className="flow-btn-ghost mt-6 w-full py-2.5 text-sm cursor-pointer"><Icons.Close/> Schliessen</button>
        </div>
    </div> );
};

const GroupPasswordModal = ({ groupId, user, showMessage, onClose, t }) => {
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.trim() === '') {
            return showMessage('Das Passwort darf nicht leer sein.', 'error');
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${groupId}/password`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ newPassword }) });
            if (res.ok) { showMessage('Gruppen-Passwort erfolgreich geändert!', 'success'); onClose(); } 
            else { const data = await res.json().catch(()=>({})); showMessage(data.message || 'Fehler aufgetreten', 'error'); }
        } catch (err) { showMessage('Netzwerkfehler', 'error'); } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
            <div className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 rounded-2xl p-6 md:p-8 w-full max-w-sm shadow-2xl animate-scale-in">
                <h3 className="text-xl font-bold mb-2 text-[#f3efe6] flex items-center gap-2">🔑 Gruppen-Passwort ändern</h3>
                <p className="text-xs font-mono text-[#a4b3bc] mb-4">Bitte gib ein neues, sicheres Passwort für diese Gruppe ein.</p>
                <form onSubmit={handleSave}>
                    <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Neues Passwort" className="flow-input w-full mb-4" />
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="flow-btn-ghost flex-1 py-2 text-xs md:text-sm cursor-pointer">Abbrechen</button>
                        <button type="submit" disabled={loading} className="flow-btn-coral flex-1 py-2 text-xs md:text-sm shadow-lg shadow-[#d97757]/20 cursor-pointer">{loading ? '...' : 'Speichern'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const GroupManagement = ({ user, showMessage, requestConfirm, onDataChange }) => {
    const { t } = useTranslation();
    const [modalGroupId, setModalGroupId] = useState(null);
    const [managingMembersGroupId, setManagingMembersGroupId] = useState(null);
    const [managingEventsGroupId, setManagingEventsGroupId] = useState(null);
    const [changingPasswordGroupId, setChangingPasswordGroupId] = useState(null);
    const [qrModalData, setQrModalData] = useState(null);
    const [view, setView] = useState('myGroups');
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupPassword, setNewGroupPassword] = useState('');
    const [joinGroupId, setJoinGroupId] = useState('');
    const [joinGroupPassword, setJoinGroupPassword] = useState('');

    const handleCreateGroup = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE_URL}/groups`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ name: newGroupName, password: newGroupPassword }) });
            if (res.ok) { showMessage('Erstellt', 'success'); onDataChange(); setView('myGroups'); setNewGroupName(''); setNewGroupPassword(''); } 
        } catch (error) { console.error(error); }
    };
    
    const handleJoinGroup = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE_URL}/groups/${joinGroupId}/join`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}`}, body: JSON.stringify({ password: joinGroupPassword }) });
            if (res.ok) { showMessage('Beigetreten', 'success'); onDataChange(); setView('myGroups'); setJoinGroupId(''); setJoinGroupPassword(''); } 
            else { const errorData = await res.json().catch(() => ({})); showMessage(errorData.message || 'Fehler beim Beitreten der Gruppe', 'error'); }
        } catch (error) { showMessage('Netzwerkfehler', 'error'); }
    };

    return ( <div className="p-4 max-w-4xl mx-auto">
            <QRCodeModal isOpen={!!qrModalData} onClose={() => setQrModalData(null)} targetData={qrModalData} t={t} />
            <div className="flow-card p-6 md:p-8 w-full">
                <div className="flex justify-center gap-3 mb-6 border-b border-[#4f5f68]/30 pb-4 flex-wrap">
                    <button onClick={() => setView('myGroups')} className={`px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition cursor-pointer ${view === 'myGroups' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'bg-[#131d22] text-[#a4b3bc] hover:text-[#f3efe6] border border-[#4f5f68]/30'}`}>{t('myGroups', 'Meine Gruppen')}</button>
                    <button onClick={() => setView('create')} className={`px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition cursor-pointer ${view === 'create' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'bg-[#131d22] text-[#a4b3bc] hover:text-[#f3efe6] border border-[#4f5f68]/30'}`}>{t('createGroup', 'Gruppe erstellen')}</button>
                    <button onClick={() => setView('join')} className={`px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition cursor-pointer ${view === 'join' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'bg-[#131d22] text-[#a4b3bc] hover:text-[#f3efe6] border border-[#4f5f68]/30'}`}>{t('joinGroup', 'Gruppe beitreten')}</button>
                </div>
                {view === 'myGroups' && <MyGroupsView user={user} onManageFolders={setModalGroupId} onManageMembers={setManagingMembersGroupId} onManageEvents={setManagingEventsGroupId} onChangePassword={setChangingPasswordGroupId} onQRCode={(group) => setQrModalData({ groupId: group.id, groupName: group.name })} onDataChange={onDataChange} t={t} />}
                {view === 'create' && <CreateGroupView name={newGroupName} setName={setNewGroupName} password={newGroupPassword} setPassword={setNewGroupPassword} onSubmit={handleCreateGroup} t={t} />}
                {view === 'join' && <JoinGroupView id={joinGroupId} setId={setJoinGroupId} password={joinGroupPassword} setPassword={setJoinGroupPassword} onSubmit={handleJoinGroup} t={t} />}
            </div>
            {modalGroupId && <FolderManagerModal groupId={modalGroupId} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onDataChange={onDataChange} onClose={() => setModalGroupId(null)} t={t} />}
            {managingMembersGroupId && <MemberManagerModal groupId={managingMembersGroupId} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onClose={() => setManagingMembersGroupId(null)} t={t} />}
            {managingEventsGroupId && <EventManagerModal groupId={managingEventsGroupId} user={user} showMessage={showMessage} requestConfirm={requestConfirm} onClose={() => setManagingEventsGroupId(null)} t={t} />}
            {changingPasswordGroupId && <GroupPasswordModal groupId={changingPasswordGroupId} user={user} showMessage={showMessage} onClose={() => setChangingPasswordGroupId(null)} t={t} />}
        </div> );
};

const MyGroupsView = ({ user, onManageFolders, onManageMembers, onManageEvents, onChangePassword, onQRCode, onDataChange, t }) => {
    const [userGroups, setUserGroups] = useState([]);
    useEffect(() => { const fetchGroups = async () => { const res = await fetch(`${API_BASE_URL}/groups`, { headers: { 'Authorization': `Bearer ${user.token}`}}); if (res.ok) setUserGroups(await res.json()); }; fetchGroups(); }, [user.token, onDataChange]);
    return ( <div> <h3 className="font-mono text-xs uppercase tracking-widest text-[#a4b3bc] mb-4">{t('myGroups', 'Meine Gruppen')}</h3> <ul className="space-y-3">
        {userGroups.map(group => (<li key={group.id} className="bg-[#131d22]/80 border border-[#4f5f68]/30 p-4 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-3">
            <span className="font-bold text-base text-[#f3efe6]">{group.name} <span className="text-xs font-mono text-[#a4b3bc]">(ID: {group.id})</span></span>
            <div className="flex gap-2 flex-wrap justify-end">
                <button onClick={() => onQRCode && onQRCode(group)} className="px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded-lg text-xs font-mono font-bold transition cursor-pointer flex items-center gap-1" title="QR-Code für die öffentliche Rangliste">📱 {t('qrCode', 'QR-Code')}</button>
                {(user.id === group.created_by || !!group.can_change_password) && <button onClick={() => onChangePassword(group.id)} className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-mono font-bold transition cursor-pointer">🔑 {t('password', 'Passwort')}</button>}
                {(user.id === group.created_by || !!group.can_manage_members) && <button onClick={() => onManageMembers(group.id)} className="px-3 py-1.5 bg-[#3a7d8a]/20 hover:bg-[#3a7d8a]/30 text-[#8ec8d4] border border-[#3a7d8a]/40 rounded-lg text-xs font-mono font-bold transition cursor-pointer">👥 {t('manageMembers', 'Mitglieder')}</button>}
                {(user.id === group.created_by || !!group.can_manage_folders) && <button onClick={() => onManageFolders(group.id)} className="px-3 py-1.5 bg-[#4f5f68]/20 hover:bg-[#4f5f68]/30 text-[#f3efe6] border border-[#4f5f68]/40 rounded-lg text-xs font-mono font-bold transition cursor-pointer">📁 {t('manageFolders', 'Ordner')}</button>}
                {(user.id === group.created_by || !!group.can_manage_events) && <button onClick={() => onManageEvents(group.id)} className="px-3 py-1.5 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 border border-indigo-500/30 rounded-lg text-xs font-mono font-bold transition cursor-pointer">📅 {t('manageEvents', 'Events')}</button>}
            </div>
        </li>))}
    </ul></div> );
};

const CreateGroupView = ({ name, setName, password, setPassword, onSubmit, t }) => (
    <form onSubmit={onSubmit} className="space-y-4 max-w-md mx-auto"><h3 className="text-2xl font-bold text-center text-[#f3efe6]">{t('createGroup', 'Gruppe erstellen')}</h3>
        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('groupName', 'Gruppenname')}</label><input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('groupName', 'Gruppenname')} className="flow-input w-full" required/></div>
        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('groupPassword', 'Gruppenpasswort')}</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('groupPassword', 'Gruppenpasswort')} className="flow-input w-full" required/></div>
        <button type="submit" className="flow-btn-coral w-full py-2.5 text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer"><Icons.Add/> {t('create', 'Erstellen')}</button>
    </form>
);

const JoinGroupView = ({ id, setId, password, setPassword, onSubmit, t }) => (
    <form onSubmit={onSubmit} className="space-y-4 max-w-md mx-auto"><h3 className="text-2xl font-bold text-center text-[#f3efe6]">{t('joinGroup', 'Gruppe beitreten')}</h3>
        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('groupId', 'Gruppen-ID')}</label><input type="text" value={id} onChange={e => setId(e.target.value)} placeholder="ID" className="flow-input w-full"/></div>
        <div><label className="block text-xs font-mono uppercase tracking-wider text-[#a4b3bc] mb-1.5">{t('password', 'Passwort')}</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('password', 'Passwort')} className="flow-input w-full"/></div>
        <button type="submit" className="flow-btn-coral w-full py-2.5 text-sm shadow-lg shadow-[#d97757]/20 flex items-center justify-center gap-2 cursor-pointer">{t('join', 'Beitreten')}</button>
    </form>
);

function App() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleWheel = () => {
      if (document.activeElement && document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    };
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [userGroups, setUserGroups] = useState([]);
  const [folders, setFolders] = useState([]);
  
  const [printData, setPrintData] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);

  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });

  const showMessage = useCallback((msg, type = 'info') => { 
      setMessage(msg); setMessageType(type); setTimeout(() => setMessage(''), 5000); 
  }, []);

  const requestConfirm = useCallback((msg, onConfirmCallback) => { setConfirmState({ isOpen: true, message: msg, onConfirm: onConfirmCallback }); }, []);
  
  const navigate = (page) => { window.history.pushState({}, '', page === 'landing' ? '/' : `/${page}`); setCurrentPage(page); };
  
  const handleLogout = useCallback(() => { localStorage.removeItem('kks-user'); setUser(null); setUserGroups([]); setFolders([]); navigate('landing'); }, []);
  
  const fetchData = useCallback(async (token) => {
      if (!token) return;
      try {
          const [groupsRes, foldersRes] = await Promise.all([
              fetch(`${API_BASE_URL}/groups`, { headers: { 'Authorization': `Bearer ${token}` } }),
              fetch(`${API_BASE_URL}/folders`, { headers: { 'Authorization': `Bearer ${token}` } })
          ]);
          if(groupsRes.status === 401 || foldersRes.status === 401) { handleLogout(); return; }
          if (groupsRes.ok) setUserGroups(await groupsRes.json());
          if (foldersRes.ok) setFolders(await foldersRes.json());
      } catch (error) { console.error(error); }
  }, [handleLogout]);

  const handleLoginSuccess = (userData) => { setUser(userData); navigate('dashboard'); };

  const handleEditReceipt = (receipt) => { setEditingReceipt(receipt); navigate('dashboard'); };

  useEffect(() => {
    const handleUrlChange = () => {
        const path = window.location.pathname;
        const publicScoreboardMatch = path.match(/^\/public-scoreboard(?:\/(\d+))?$/);
        const storedUser = localStorage.getItem('kks-user');
        const legalPages = ['/privacy', '/cookies', '/disclaimer', '/imprint'];
        
        if (publicScoreboardMatch) { setCurrentPage('public-scoreboard'); } 
        else if (legalPages.includes(path)) { setCurrentPage(path.substring(1)); } 
        else if (path === '/verify-email') { setCurrentPage('verify-email'); }
        else if (path === '/reset-password') { setCurrentPage('reset-password'); }
        else if (path === '/' || path === '/landing') { setCurrentPage('landing'); } 
        else if (path === '/login') {
            if (storedUser && !user) setUser(JSON.parse(storedUser));
            setCurrentPage('login');
        } else if (storedUser) {
            if (!user) setUser(JSON.parse(storedUser));
            const page = path.substring(1);
            setCurrentPage(['dashboard', 'templates', 'scoreboard', 'groupManagement', 'events', 'settings'].includes(page) ? page : 'dashboard');
        } else { setCurrentPage('landing'); }
    };
    window.addEventListener('popstate', handleUrlChange);
    handleUrlChange();
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [user]);

  useEffect(() => { if (user?.token) { fetchData(user.token); } }, [user, fetchData]);

  if (printData) return <StandblattPrintView data={printData} onClose={() => setPrintData(null)} />;

  const renderPage = () => {
    const publicScoreboardMatch = window.location.pathname.match(/^\/public-scoreboard(?:\/(\d+))?$/);
    switch (currentPage) {
        case 'landing': return <LandingPage onNavigate={navigate} />;
        case 'login': return user ? <Dashboard user={user} userGroups={userGroups} folders={folders} showMessage={showMessage} requestConfirm={requestConfirm} onDataChange={() => fetchData(user.token)} editingReceipt={editingReceipt} onCancelEdit={() => setEditingReceipt(null)} /> : <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} />;
        case 'verify-email': return <VerifyEmailPage showMessage={showMessage} onNavigate={navigate} />;
        case 'reset-password': return <ResetPasswordPage showMessage={showMessage} onNavigate={navigate} />;
        case 'public-scoreboard': {
            const publicFolderParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('folderId') : null;
            return publicScoreboardMatch ? <Scoreboard user={null} userGroups={userGroups} folders={folders} showMessage={showMessage} requestConfirm={requestConfirm} publicGroupId={publicScoreboardMatch[1] || null} publicFolderId={publicFolderParam} /> : <LandingPage onNavigate={navigate} />;
        }
        case 'dashboard': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <Dashboard user={user} userGroups={userGroups} folders={folders} showMessage={showMessage} requestConfirm={requestConfirm} onDataChange={() => fetchData(user.token)} editingReceipt={editingReceipt} onCancelEdit={() => setEditingReceipt(null)} />;
        case 'templates': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <TemplateManager user={user} showMessage={showMessage} requestConfirm={requestConfirm} />;
        case 'scoreboard': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <Scoreboard user={user} userGroups={userGroups} folders={folders} showMessage={showMessage} requestConfirm={requestConfirm} onEditReceipt={handleEditReceipt} />;
        case 'events': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <EventsPage user={user} userGroups={userGroups} showMessage={showMessage} requestConfirm={requestConfirm} t={t} setPrintData={setPrintData} />;
        case 'groupManagement': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <GroupManagement user={user} showMessage={showMessage} requestConfirm={requestConfirm} onDataChange={() => fetchData(user.token)}/>;
        case 'settings': return !user ? <Login onLoginSuccess={handleLoginSuccess} showMessage={showMessage} onNavigate={navigate} /> : <Settings user={user} showMessage={showMessage} handleLogout={handleLogout} requestConfirm={requestConfirm} />;
        case 'privacy': return <LegalPage topic="privacy" onNavigate={navigate} />;
        case 'cookies': return <LegalPage topic="cookies" onNavigate={navigate} />;
        case 'disclaimer': return <LegalPage topic="disclaimer" onNavigate={navigate} />;
        case 'imprint': return <LegalPage topic="imprint" onNavigate={navigate} />;
        default: return <LandingPage onNavigate={navigate} />;
    }
  };

  return ( 
    <div className="flex flex-col min-h-screen bg-[#162127] text-[#f3efe6] relative overflow-x-hidden selection:bg-[#d97757]/30 selection:text-[#f3efe6]">
      {/* Ambient Floweon backgrounds */}
      <div aria-hidden="true" className="flow-ambient-bg no-print pointer-events-none">
        <div className="flow-ambient-glow-1" />
        <div className="flow-ambient-glow-2" />
        <div className="flow-grid-pattern" />
      </div>

      <nav className="bg-[#162127]/85 backdrop-blur-md border-b border-[#4f5f68]/30 px-4 md:px-8 py-3 sticky top-0 z-50 no-print transition-all">
        <div className="max-w-7xl mx-auto flex justify-between items-center gap-4">
           <div className="flex items-center gap-4">
             <button onClick={() => navigate(user ? 'dashboard' : 'landing')} className="cursor-pointer transition transform hover:opacity-90 flex items-center">
               <img src="/banner-weiss.png" alt="KKS Logo" className="h-9 md:h-11 w-auto object-contain" />
             </button>
             <div className="hidden sm:flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-[#3a7d8a] bg-[#3a7d8a]/10 px-2.5 py-1 rounded-full border border-[#3a7d8a]/30">
               <span className="relative flex h-2 w-2">
                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3a7d8a] opacity-75"></span>
                 <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3a7d8a]"></span>
               </span>
               <span>ONLINE</span>
             </div>
           </div>

           <div className="flex items-center space-x-3">
            {user ? (
            <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-end">
              <button onClick={() => navigate('dashboard')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'dashboard' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('capture', 'Erfassen')}</button>
              <button onClick={() => navigate('templates')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'templates' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('templates', 'Vorlagen')}</button>
              <button onClick={() => navigate('scoreboard')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'scoreboard' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('scoreboard', 'Rangliste')}</button>
              <button onClick={() => navigate('events')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'events' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('eventsTab', 'Events')}</button>
              <button onClick={() => navigate('groupManagement')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'groupManagement' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('groups', 'Gruppen')}</button>
              <button onClick={() => navigate('settings')} className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg transition-all cursor-pointer ${currentPage === 'settings' ? 'bg-[#d97757] text-[#f3efe6] shadow-md shadow-[#d97757]/20' : 'text-[#f3efe6]/80 hover:text-[#f3efe6] hover:bg-[#4f5f68]/20'}`}>{t('settings', 'Einstellungen')}</button>
              <button onClick={handleLogout} className="px-3 py-1.5 text-xs md:text-sm font-medium rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 transition-all cursor-pointer">{t('logout', 'Abmelden')}</button>
            </div> ) : (
              <button onClick={() => navigate('login')} className="flow-btn-coral py-1.5 px-4 text-xs md:text-sm rounded-lg shadow-md shadow-[#d97757]/20 cursor-pointer">{t('login', 'Anmelden')}</button>
            )}
            <div className="relative">
              <select onChange={(e) => i18n.changeLanguage(e.target.value)} value={(i18n.language || 'de').split('-')[0]} className="bg-[#1a262c] text-[#f3efe6] border border-[#4f5f68]/40 text-xs rounded-lg px-2.5 py-1.5 appearance-none focus:outline-none focus:border-[#d97757] cursor-pointer">
                <option value="de">DE</option>
                <option value="fr">FR</option>
                <option value="it">IT</option>
                <option value="en">EN</option>
              </select>
            </div>
           </div>
        </div>
      </nav>

      {/* Floweon ticker ribbon */}
      <div className="flow-ticker-wrapper border-b border-[#4f5f68]/20 py-1.5 font-mono text-[11px] uppercase tracking-widest text-[#a4b3bc] no-print">
        <div className="flow-ticker-track flex gap-8">
          <span>PRECISE SCORING</span>
          <span className="text-[#d97757]">◆</span>
          <span>REALTIME STANDINGS</span>
          <span className="text-[#3a7d8a]">◆</span>
          <span>ZICK-ZACK & DIFFERENZLER</span>
          <span className="text-[#d97757]">◆</span>
          <span>EXPORT & PRINT</span>
          <span className="text-[#3a7d8a]">◆</span>
          <span>VEREINSKONFORM</span>
          <span className="text-[#d97757]">◆</span>
          <span>PRECISE SCORING</span>
          <span className="text-[#d97757]">◆</span>
          <span>REALTIME STANDINGS</span>
          <span className="text-[#3a7d8a]">◆</span>
          <span>ZICK-ZACK & DIFFERENZLER</span>
          <span className="text-[#d97757]">◆</span>
          <span>EXPORT & PRINT</span>
          <span className="text-[#3a7d8a]">◆</span>
          <span>VEREINSKONFORM</span>
          <span className="text-[#d97757]">◆</span>
        </div>
      </div>

      {message && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[10000] animate-fade-in-down w-[90%] max-w-2xl no-print">
            <div className={`flex items-start justify-between gap-3 px-5 py-3.5 rounded-xl shadow-2xl text-[#f3efe6] font-medium text-sm md:text-base border backdrop-blur-md ${messageType === 'error' ? 'bg-red-950/90 border-red-500/40 text-red-200' : 'bg-emerald-950/90 border-emerald-500/40 text-emerald-200'}`}>
                <div className="flex-1 mt-0.5 break-words">{message}</div>
                <button onClick={() => setMessage('')} className="text-[#f3efe6]/70 hover:text-[#f3efe6] focus:outline-none p-1 rounded-md hover:bg-white/10 transition-colors cursor-pointer"><Icons.Close/></button>
            </div>
        </div>
      )}
      
      <main className="flex-grow flex flex-col pt-6 relative"> {renderPage()} </main>

      <footer className="bg-[#162127]/90 backdrop-blur-md border-t border-[#4f5f68]/30 py-8 text-[#f3efe6]/70 mt-auto no-print z-10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono">
            <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-[#a4b3bc]">BUILT TO FLOW · MADE TO LAST</span>
            </div>
            <div className="flex justify-center gap-6 flex-wrap">
                <button onClick={() => navigate('privacy')} className="hover:text-[#d97757] transition-colors cursor-pointer">{t('privacyPolicy', 'Datenschutz')}</button>
                <button onClick={() => navigate('cookies')} className="hover:text-[#d97757] transition-colors cursor-pointer">{t('cookiePolicy', 'Cookies')}</button>
                <button onClick={() => navigate('disclaimer')} className="hover:text-[#d97757] transition-colors cursor-pointer">{t('disclaimer', 'Disclaimer')}</button>
                <button onClick={() => navigate('imprint')} className="hover:text-[#d97757] transition-colors cursor-pointer">{t('imprint', 'Impressum')}</button>
            </div>
            <p className="text-[#a4b3bc]">&copy; {new Date().getFullYear()} kks-scoreboard.ch</p>
        </div>
      </footer>

      <ConfirmModal isOpen={confirmState.isOpen} message={confirmState.message} onConfirm={() => confirmState.onConfirm && confirmState.onConfirm()} onCancel={() => setConfirmState({ isOpen: false, message: '', onConfirm: null })} t={t} />
    </div> 
  );
}

export default App;